import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { ErrorCode, type Room } from 'colyseus.js';
import type { GameState } from '@fortress/shared/schema';
import {
  ACTION_KINDS,
  ACTION_LABELS,
  ACTION_DESCRIPTIONS,
  BOARD_RADIUS,
  FORTRESS_CARDS_CONFIG,
  APP_VERSION,
  MAX_QUEUE_TURNS,
  RESOURCES,
  areAdjacent,
  buildBoard,
  canAffordWith,
  canPerformAction,
  canUseCardAction,
  committedFortressNodes,
  deriveSlots,
  edgeKey,
  effectFor,
  fortressCardsById,
  fortressCardsByName,
  fortressCostFor,
  matchAllFortressPatterns,
  matchFortressPattern,
  ZONE_HIGHEST,
  nodesWithinAny,
  nodesWithinInto,
  resolveBuilding,
  resolveVigorZone,
  substitutionsFor,
  tokenCapOf,
  tokenTargetOf,
  vigorZoneLevel,
  wildcardCostAs,
  type ActionNotice,
  type AimMode,
  type BuildingConfig,
  type BuildingType,
  type ConstructableBuilding,
  type FortressBuilding,
  type FortressCard,
  type FortressKind,
  type FortressMatch,
  type GameAction,
  type GameConfig,
  type GateQuery,
  type PaymentSubstitutions,
  type PlayerActionState,
  type ResourceAmount,
  type SpecialActionKind,
  type VigorZone,
} from '@fortress/shared';
import {
  BuildingIcon,
  BUILDING_BLURBS,
  BUILDING_LABELS,
  CONSTRUCTABLE_BUILDINGS,
  buildingCursor,
} from './buildings';
import {
  createPixiGame,
  type PixiGame,
  type RemoteCursorView,
} from './game/PixiGame';
import {
  FortressCardFace,
  REGULAR_ACCENT,
  WIN_ACCENT,
} from './library/FortressCardFace';
import { ResourceIcon, RESOURCE_KINDS, RESOURCE_LABELS } from './resources';
import { FortressLibrary } from './library/FortressLibrary';
import { PatternDiagram } from './library/fortressPattern';
import { fetchServerAppVersion, getAppState } from './appState';
import { logout } from './auth/session';
import { LobbyHub } from './LobbyHub';
import {
  ClientMessage,
  ServerMessage,
  joinGameById,
  reconnectGame,
  watchState,
  type CursorView,
  type FortressCardView,
  type HistoryEntryView,
  type LobbyInfo,
  type LobbyPlayer,
  type SpectatorView,
  type MarketPile,
  type MarketView,
  type ResourceCounts,
} from './net/colyseus';
import {
  removeActiveGameToken,
  setActiveGameToken,
} from './net/activeGame';
import { audio } from './audio/AudioManager';

audio.register('turn', '/audio/turn.wav');
audio.register('turn2', '/audio/turn-2.wav');

const PLAYER_COLORS = [0xff5d5d, 0x5db9ff, 0x6dff8f, 0xffd65d];
/** Spectators hold no seat, so their live cursor renders in a neutral grey. */
const SPECTATOR_CURSOR_COLOR = 0x9a948c;

// Topology-only board graph (no pixel layout) for build-range computation. It's
// deterministic from BOARD_RADIUS, so its node ids match the board the server
// and PixiGame build — see AGENTS.md.
const BUILD_GRAPH = buildBoard(BOARD_RADIUS);

// Vigor zone per node id, derived the same way the server stamps `nodeZone`
// (every slot's zone, defaulting forest). Used to price a build's Vigor penalty.
const NODE_ZONE: VigorZone[] = (() => {
  const zones = new Array<VigorZone>(BUILD_GRAPH.nodes.length).fill('forest');
  for (const slot of deriveSlots(BUILD_GRAPH)) {
    for (const id of slot.nodeIds) zones[id] = slot.zone;
  }
  return zones;
})();

const VIGOR_IDX = RESOURCES.indexOf('vigor');

/** Total cost to build `building` on a node — mirrors the server's `buildCost`
 * (config/buildCost.ts): base cost + the node's zone Vigor penalty (if the
 * building pays one) + a Mine's permanent-resource fee. Merges the player's
 * sparse overrides with the match's pinned config. */
function buildCostFor(
  config: GameConfig,
  player: Pick<LobbyPlayer, 'buildings' | 'vigorZones'>,
  building: ConstructableBuilding,
  zone: VigorZone,
  resource: string,
  permanence: string,
  skipVigorPenalty = false,
): number[] {
  const def = resolveBuilding(config, player, building);
  const cost = [...def.cost];
  if (cost.length !== 4) return cost; // wildcard cost — no surcharges
  if (def.vigorPenalty && !skipVigorPenalty) {
    const z = resolveVigorZone(config, player, vigorZoneLevel(zone));
    cost[VIGOR_IDX] = (cost[VIGOR_IDX] ?? 0) + z.vigorPenalty;
  }
  if (
    def.permanentResourceCost &&
    permanence === 'permanent' &&
    resource !== ''
  ) {
    const idx = (RESOURCES as readonly string[]).indexOf(resource);
    if (idx >= 0) cost[idx] = (cost[idx] ?? 0) + def.permanentResourceCost;
  }
  return cost;
}

/** Whether an opponent's building blocks build-through, using that owner's
 * sparse overrides when present on the roster. */
function blocksOpponentBuild(
  config: GameConfig,
  players: readonly LobbyPlayer[],
  ownerId: string,
  building: string,
): boolean {
  const owner = players.find((p) => p.id === ownerId);
  const def = owner
    ? resolveBuilding(config, owner, building as BuildingType)
    : config.buildings[building as BuildingType];
  return !!def?.blockOpponentBuild;
}

/** The construct-building flow: choosing a building, then placing it. `null` when
 * the flow is off. In `place`, `allowed` are the legal (green) targets and
 * `tooCostly` are reachable-but-unaffordable (grey) ones. */
type BuildFlow =
  | { stage: 'pick'; penaltyCardId?: string }
  | {
      stage: 'place';
      building: ConstructableBuilding;
      allowed: ReadonlySet<number>;
      tooCostly: ReadonlySet<number>;
      /** The node tentatively picked for the building, pending Confirm. A board
       * click only selects; the construct action is sent by `confirmBuild`. */
      selected?: number;
      /** node id → Vigor it would cost to build there (only nodes that pay one). */
      vigorPenalty: ReadonlyMap<number, number>;
      /** node id → the one-time tile resource a build there would collect,
       * zone bonus included (only nodes bearing one). */
      oneTimeGain: ReadonlyMap<number, { resource: string; amount: number }>;
      /** Card id of an armed Energized Bun-Bun (double this build's one-time
       * pickup), sent as the construct action's rider. */
      doubleOneTime?: string;
      penaltyCardId?: string;
    }
  | null;

/** The aim-barracks flow: tap one of your unaimed Barracks, pick a posture
 * (attack / defend), then tap a highlighted target node. `defense: true` is the
 * off-turn defense-window variant — posture locked to defend, targets restricted
 * to your own attacked buildings, and no action point spent. */
type AimFlow =
  | { stage: 'pickBarracks'; sources: ReadonlySet<number>; defense: boolean }
  | {
      stage: 'mode';
      barracksId: number;
      /** False when this Barracks is absorbed into a Fortress (defend-only). */
      attackable: boolean;
      /** The posture picker never appears for defense reactions (defend-locked). */
      defense: false;
    }
  | {
      stage: 'target';
      barracksId: number;
      mode: AimMode;
      defense: boolean;
      allowed: ReadonlySet<number>;
    }
  | null;

/** The Card-Effects flow: the tray of your implemented cards' usable effects,
 * then a per-kind sub-flow — an inline confirm, a board pick (protect a Scout /
 * ready Barracks), a token adjust list, or a market-card pick. */
type EffectsFlow =
  | { stage: 'list' }
  | {
      stage: 'confirm';
      cardId: string;
      kind: 'double_banked_resources' | 'force_end_round';
    }
  | { stage: 'pickScout'; cardId: string; allowed: ReadonlySet<number> }
  | { stage: 'replicate'; cardId: string; allowed: ReadonlySet<number> }
  | {
      stage: 'untap';
      cardId: string;
      allowed: ReadonlySet<number>;
      selected: ReadonlySet<number>;
      /** How many more Barracks this card may untap this round (cap − tokens). */
      limit: number;
    }
  | { stage: 'adjustToken'; cardId: string }
  | {
      stage: 'market';
      cardId: string;
      kind: 'set_fortress_lock' | 'free_cycle_market';
    }
  | { stage: 'reorderMarket'; cardId: string }
  | {
      stage: 'reorderDeck';
      cardId: string;
      market: FortressKind;
      order: readonly string[];
      submitted?: boolean;
    }
  | {
      stage: 'moveFortress';
      cardId: string;
      allowed: ReadonlySet<number>;
      selected: ReadonlySet<number>;
      /** How many destination nodes the pattern needs (Booba: 2). */
      limit: number;
      doubleOneTime?: string;
    }
  | {
      stage: 'claim';
      cardId: string;
      picked: readonly string[];
      remaining: number;
      submitted?: boolean;
    }
  | null;

/** Button label per granted effect kind, shown in the effects tray. */
const EFFECT_LABELS: Partial<Record<SpecialActionKind, string>> = {
  pay_penalty_with_token: 'Pay Penalty with Token',
  make_scout_indestructible: 'Protect a Scout',
  replicate_fortress: 'Replicate',
  double_banked_resources: 'Double Banked Resources',
  untap_barracks: 'Ready Barracks',
  set_fortress_lock: 'Lock / Unlock a Card',
  free_cycle_market: 'Free Cycle',
  reorder_fortress_deck: 'Reorder Deck',
  buy_extra_action: 'Buy Extra Action',
  force_end_round: 'End the Round',
  adjust_fortress_token: 'Adjust a Token',
  move_fortress: 'Move Fortress',
  claim_resources: 'Claim 2 Resources',
};

/** Short human-readable summary of an action being queued, shown in the queue
 * panel (and echoed back by the server if the move fails at auto-play). */
function queueMoveLabel(
  action: GameAction,
  cardsById: ReadonlyMap<string, FortressCard>,
): string {
  const cardName = (id: string) => cardsById.get(id)?.name ?? id;
  switch (action.type) {
    case 'construct_building':
      return `Build ${BUILDING_LABELS[action.building]} · node ${action.nodeId}`;
    case 'pay_penalty_with_token':
      return `Build ${BUILDING_LABELS[action.building]} · node ${action.nodeId} (token)`;
    case 'construct_bridge':
      return `Bridge · ${action.from}–${action.to}${
        action.payWith ? ` (pay ${action.payWith})` : ''
      }`;
    case 'implement_fortress':
      return `Implement ${cardName(action.cardId)}`;
    case 'aim_barracks':
      return `${action.mode === 'attack' ? 'Attack' : 'Defend'} · node ${action.targetId}`;
    case 'cycle_market':
      return `Cycle ${action.indices.length} card${action.indices.length === 1 ? '' : 's'}`;
    default:
      return EFFECT_LABELS[action.type] ?? action.type;
  }
}

/** Summary of the queued round-start cycle decision (a JSON
 * `RoundStartCycleChoice` on `Player.queuedRoundStartCycle`) for the queue
 * panel. */
function roundStartCycleLabel(
  payload: string,
  cardsById: ReadonlyMap<string, FortressCard>,
): string {
  try {
    const parsed = JSON.parse(payload) as {
      regular?: string | null;
      win?: string | null;
    };
    const names = [parsed.regular, parsed.win]
      .filter((id): id is string => typeof id === 'string')
      .map((id) => cardsById.get(id)?.name ?? id);
    return names.length > 0 ? `Cycle out ${names.join(' + ')}` : 'Cycle nothing';
  } catch {
    return 'Round-start cycle';
  }
}

/** Effect kinds whose server handler requires an available action point
 * (`isActor`) — mirrored so the tray disables them at 0 actions left. The rest
 * only need it to be your turn (`isCurrentPlayer`). */
const EFFECT_NEEDS_ACTION_POINT: ReadonlySet<SpecialActionKind> = new Set([
  'pay_penalty_with_token',
  'double_banked_resources',
  'collect_card_energy',
  'set_fortress_lock',
  'reorder_fortress_deck',
  'force_end_round',
  'adjust_fortress_token',
  'move_fortress',
]);

/** Board/turn context the card-effect availability checks read. */
interface EffectContext {
  actionsRemaining: number;
  /** Your Scouts not already protected (candidates for Godspeed). */
  protectableScouts: number;
  /** Your aimed Barracks (candidates for an untap effect). */
  aimedBarracks: number;
  /** Banked iCarlie claims awaiting cashing. */
  pendingClaims: number;
  /** True while queue-planning: resource gates don't apply (a queued yield
   * may fund the effect by the time it auto-plays). */
  planning?: boolean;
}

/** Can `kind` be used via `cardId` right now? The shared registry gate
 * (`canUseCardAction`) plus the turn/board conditions the server handlers
 * enforce, so an enabled tray button always passes server validation. */
function effectAvailability(
  player: Pick<LobbyPlayer, 'fortressCards' | 'resources'>,
  cardId: string,
  kind: SpecialActionKind,
  ctx: EffectContext,
): { allowed: boolean; reason?: string } {
  const gate = canUseCardAction(player.fortressCards, cardId, kind);
  if (!gate.allowed) return gate;
  if (EFFECT_NEEDS_ACTION_POINT.has(kind) && ctx.actionsRemaining <= 0) {
    return { allowed: false, reason: 'No actions left this turn.' };
  }
  switch (kind) {
    case 'make_scout_indestructible':
      return {
        allowed: false,
        reason: 'Reactive: use it when a Scout is about to be destroyed.',
      };
    case 'untap_barracks':
      return ctx.aimedBarracks > 0
        ? { allowed: true }
        : { allowed: false, reason: 'None of your Barracks are aimed.' };
    case 'buy_extra_action':
      // 1 Vigor, payable through substitutions (Bench: food; Treadmill:
      // obsidian). Never gates while planning — assume infinite resources.
      return ctx.planning ||
        canAffordWith(
          toAmount(player.resources),
          [0, 0, 1, 0],
          substitutionsFor(player.fortressCards),
        )
        ? { allowed: true }
        : { allowed: false, reason: 'Need 1 Energy.' };
    case 'claim_resources':
      return ctx.pendingClaims > 0
        ? { allowed: true }
        : { allowed: false, reason: 'Nothing banked to claim.' };
    default:
      return { allowed: true };
  }
}

/** A revealed Fortress card the player could implement: its pattern is realised
 * by their free buildings somewhere on the board. `nodeIds` are the matched
 * buildings + markers (to highlight); `affordable` reflects the Obsidian cost. */
interface ImplementCandidate {
  cardId: string;
  kind: FortressKind;
  name: string;
  cost: number[];
  affordable: boolean;
  nodeIds: number[];
  /** From your face-down hand (Dungeon Dice Martyr), not the market row. */
  fromHand?: boolean;
}

/** All board nodes a fortress match occupies (buildings + markers + bridge
 * endpoints), deduped — what gets highlighted / hit-tested for it. */
function nodesOfMatch(m: FortressMatch): number[] {
  return [
    ...new Set([
      ...m.buildingNodeIds,
      ...m.markerNodeIds,
      ...m.bridgeNodeIdPairs.flat(),
    ]),
  ];
}

/** The Implement-Fortress flow.
 * `pick`: the card picker is open (hovering a card previews its first match).
 * `place`: a card is chosen — every node of every possible placement pulses;
 * tapping one selects a concrete placement there (`selected.options` are the
 * indices into `matches` covering that node; `pos` is the one shown, cycled by
 * Rotate), and Confirm implements it. Queued planning skips `place` (the board
 * may change before the move plays) and sends straight from the picker. */
type ImplementFlow =
  | { stage: 'pick'; cards: ImplementCandidate[] }
  | {
      stage: 'place';
      /** The picker's card list, restored on Back/Esc. */
      cards: ImplementCandidate[];
      card: ImplementCandidate;
      /** Every distinct placement of the card's pattern, from the shared matcher. */
      matches: FortressMatch[];
      /** Union of all matched nodes — the pulsing "possible locations". */
      candidates: ReadonlySet<number>;
      selected: { nodeId: number; options: number[]; pos: number } | null;
    };

/** Stand-in bank while planning queued moves: affordability never gates a
 * queued action (a prior queued move may fund it), so pickers that disable
 * unaffordable options get this instead of the live resources. */
const PLANNING_BANK: ResourceCounts = {
  minerals: 999,
  food: 999,
  vigor: 999,
  obsidian: 999,
};

/** How long an action-notice popup stays before auto-dismissing (ms). */
const NOTICE_TTL = 5000;

/** How often the client re-reads `/app-state` to compare the server's
 * `appVersion` against its own baked-in `APP_VERSION` (a deploy detector —
 * boot also checks immediately). */
const APP_VERSION_POLL_MS = 5 * 60 * 1000;

/** How long the top-screen "It's Your Turn" toast stays up (ms). */
const TURN_TOAST_TTL = 4000;

/** How long the queued-move-halted toast stays up (ms) — longer than the turn
 * toast, since it carries the reason the auto-play stopped. */
const QUEUE_HALT_TTL = 8000;

/** The browser tab's default title, captured before we ever mutate it (matches
 * index.html's <title>). Restored whenever it's not the player's turn. */
const DEFAULT_TITLE = document.title;

/** An active action notification: the server `ActionNotice` plus a monotonic
 * `key` so React remounts (and re-animates) it on each new action. */
type ActiveNotice = ActionNotice & { key: number };

/** Seat → CSS colour, wrapping the palette like the board renderer does. */
function seatColor(seat: number): string {
  const c = PLAYER_COLORS[seat % PLAYER_COLORS.length] ?? 0xffffff;
  return `#${c.toString(16).padStart(6, '0')}`;
}

/** A player's roster icon: their initial in a seat-coloured rounded square.
 * Reused by the expanded roster row and the collapsed rail; `current` adds the
 * active-turn outline (used on the rail, where there's no row to highlight).
 * `connected` drives the presence dot on the top-right corner — filled green
 * while their client is connected, an empty outlined ring after they drop.
 * `size` (px) is applied inline so one component serves both placements. */
function PlayerIcon({
  name,
  seat,
  connected,
  size = 20,
  current = false,
}: {
  name: string;
  seat: number;
  connected: boolean;
  size?: number;
  current?: boolean;
}) {
  return (
    <span
      className={`player-icon${current ? ' is-current' : ''}`}
      style={{
        background: seatColor(seat),
        width: size,
        height: size,
        fontSize: Math.round(size * 0.56),
      }}
      aria-hidden
    >
      {(name.trim()[0] ?? '?').toUpperCase()}
      <span className={`presence-dot${connected ? ' is-online' : ''}`} />
    </span>
  );
}

/** One classified pointer sample — the wire shape of every presence send
 * (cursor stream, touch tap, emoji reaction). Board samples carry board-plane
 * coords in x/y; panel samples carry panel-normalised x/y plus the hovered
 * element key and the position within it (see `CursorState`). */
interface CursorSample {
  area: string;
  x: number;
  y: number;
  target: string;
  tx: number;
  ty: number;
}
const HIDDEN_CURSOR: CursorSample = {
  area: '',
  x: 0,
  y: 0,
  target: '',
  tx: 0.5,
  ty: 0.5,
};

/** Clamp a wire value to 0..1, centre when absent/bogus (tx/ty fields). */
function unitOrCenter(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? Math.max(0, Math.min(1, n)) : 0.5;
}

/** Instant-open palette: shown while the full Unicode catalogue chunk loads
 * (a handful of ms from cache) so the picker is never empty. */
const EMOJI_SET = [
  '😀', '😂', '😅', '😍', '🤔', '😮', '😭', '😱',
  '🤯', '😡', '🥳', '😴', '👍', '👎', '👏', '🙏',
  '💪', '🤝', '✌️', '🫡', '❤️', '🔥', '💯', '✨',
  '🎉', '💀', '⚔️', '🛡️', '🏰', '💎', '⛏️', '🌉',
];

/** The full emoji catalogue, grouped Unicode-order (Smileys & Emotion →
 * Flags), lazy-loaded from `unicode-emoji-json` on first picker open and
 * cached for the session. Emojis newer than 15.1 are dropped: current
 * runtimes' `RGI_Emoji` tables (the server's validator) don't know them yet,
 * and most devices would draw tofu anyway. */
interface EmojiGroup {
  name: string;
  emojis: { emoji: string; name: string }[];
}
let emojiCataloguePromise: Promise<EmojiGroup[]> | null = null;
function loadEmojiCatalogue(): Promise<EmojiGroup[]> {
  emojiCataloguePromise ??= import('unicode-emoji-json/data-by-group.json')
    .then((mod) =>
      mod.default.map((group) => ({
        name: group.name,
        emojis: group.emojis
          .filter((e) => parseFloat(e.emoji_version) <= 15.1)
          .map((e) => ({ emoji: e.emoji, name: e.name })),
      })),
    )
    .catch((err) => {
      emojiCataloguePromise = null; // allow a retry on the next open
      throw err;
    });
  return emojiCataloguePromise;
}
/** Persisted across games deliberately (localStorage, not room state). */
const RECENT_EMOJI_KEY = 'fortress.recentEmojis';
const RECENT_EMOJI_MAX = 8;

function loadRecentEmojis(): string[] {
  try {
    const raw = JSON.parse(localStorage.getItem(RECENT_EMOJI_KEY) ?? '[]');
    return Array.isArray(raw)
      ? raw.filter((e): e is string => typeof e === 'string').slice(0, RECENT_EMOJI_MAX)
      : [];
  } catch {
    return [];
  }
}

function pushRecentEmoji(emoji: string): void {
  const next = [
    emoji,
    ...loadRecentEmojis().filter((e) => e !== emoji),
  ].slice(0, RECENT_EMOJI_MAX);
  try {
    localStorage.setItem(RECENT_EMOJI_KEY, JSON.stringify(next));
  } catch {
    /* storage full/blocked — recents just don't persist */
  }
}

/** The emoji reaction picker, opened at the right-click / long-press point.
 * Offers the complete Unicode emoji set (lazy-loaded, grouped by category,
 * name-searchable); recently used emojis (persisted in localStorage across
 * games) lead the grid. Picking one calls `onPick` and the parent sends +
 * echoes it. Closes on outside pointer-down or Escape. */
function EmojiPicker({
  at,
  onPick,
  onClose,
}: {
  at: { x: number; y: number };
  onPick: (emoji: string) => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const [catalogue, setCatalogue] = useState<EmojiGroup[] | null>(null);
  const [query, setQuery] = useState('');
  // Recents are read once per open (the picker remounts each time).
  const recents = useMemo(() => loadRecentEmojis(), []);

  useEffect(() => {
    let stale = false;
    loadEmojiCatalogue()
      .then((groups) => {
        if (!stale) setCatalogue(groups);
      })
      .catch(() => {
        /* chunk failed to load — the curated fallback grid stays */
      });
    return () => {
      stale = true;
    };
  }, []);

  /** What the grid shows: search results (flat), or recents + full groups
   * (curated fallback while the catalogue chunk is still loading). */
  const sections = useMemo((): EmojiGroup[] => {
    const q = query.trim().toLowerCase();
    if (q !== '' && catalogue) {
      const hits: EmojiGroup['emojis'] = [];
      for (const group of catalogue) {
        for (const e of group.emojis) {
          if (e.name.includes(q)) {
            hits.push(e);
            if (hits.length >= 120) break; // grid stays scannable
          }
        }
        if (hits.length >= 120) break;
      }
      return [{ name: hits.length > 0 ? 'Search results' : 'No matches', emojis: hits }];
    }
    const head: EmojiGroup[] =
      recents.length > 0
        ? [{ name: 'Recent', emojis: recents.map((e) => ({ emoji: e, name: e })) }]
        : [];
    if (!catalogue) {
      return [
        ...head,
        { name: 'Popular', emojis: EMOJI_SET.map((e) => ({ emoji: e, name: e })) },
      ];
    }
    return [...head, ...catalogue];
  }, [catalogue, query, recents]);

  // Clamp into the viewport once the real size is measurable. Re-clamp when
  // the content changes shape (catalogue arrives, search narrows the grid).
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({
      left: Math.max(8, Math.min(at.x, window.innerWidth - r.width - 8)),
      top: Math.max(8, Math.min(at.y, window.innerHeight - r.height - 8)),
    });
  }, [at, catalogue, query]);

  // The dismiss listeners are attached exactly once per picker instance.
  // `onClose` is read through a ref: its identity changes on every parent
  // render, and depending on it would detach + re-attach the listeners each
  // time — with the deferred attach below, a keypress landing in that gap
  // would be silently missed.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    const down = (e: PointerEvent) => {
      if (
        ref.current &&
        e.target instanceof Node &&
        !ref.current.contains(e.target)
      ) {
        onCloseRef.current();
      }
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    // Deferred a tick so the opening press can't instantly dismiss it.
    const id = window.setTimeout(() => {
      window.addEventListener('pointerdown', down);
      window.addEventListener('keydown', key);
    }, 0);
    return () => {
      window.clearTimeout(id);
      window.removeEventListener('pointerdown', down);
      window.removeEventListener('keydown', key);
    };
  }, []);

  return createPortal(
    <div
      ref={ref}
      className="emoji-picker"
      style={{
        left: pos?.left ?? at.x,
        top: pos?.top ?? at.y,
        visibility: pos ? 'visible' : 'hidden',
      }}
      role="menu"
      aria-label="Send an emoji reaction"
    >
      <input
        type="search"
        className="emoji-picker-search"
        placeholder="Search emojis…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        // Autofocus pops the keyboard over the grid on touch — desktop only.
        autoFocus={!window.matchMedia('(pointer: coarse)').matches}
        aria-label="Search emojis"
      />
      <div className="emoji-picker-scroll">
        {sections.map((section) => (
          <div key={section.name} className="emoji-picker-section">
            <div className="emoji-picker-heading">{section.name}</div>
            <div className="emoji-picker-grid">
              {section.emojis.map((e, i) => (
                <button
                  // Names repeat across sections (recents), not within one.
                  key={`${e.emoji}-${i}`}
                  type="button"
                  className="emoji-picker-btn"
                  title={e.name === e.emoji ? undefined : e.name}
                  onClick={() => onPick(e.emoji)}
                >
                  {e.emoji}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>,
    document.body,
  );
}

/** One in-flight emoji reaction over a DOM panel (board reactions render in
 * the Pixi canvas). Transient like the tap ripples. */
interface EmojiPopView {
  key: string;
  area: string;
  x: number;
  y: number;
  target: string;
  tx: number;
  ty: number;
  emoji: string;
}

/** Remote (and locally echoed) emoji reactions over the DOM panels: pulsate
 * in, hold a beat, fade out quickly — all CSS; the parent removes each entry
 * after the animation ends. Positioned via `resolvePanelPoint`, so a reaction
 * dropped on a card lands on the same spot of that card here. */
function RemoteEmojiPops({ pops }: { pops: EmojiPopView[] }) {
  return (
    <>
      {pops.map((p) => {
        const pos = resolvePanelPoint(p.area, p.x, p.y, p.target, p.tx, p.ty);
        if (!pos) return null;
        return (
          <span
            key={p.key}
            className="remote-emoji-pop"
            style={{ left: pos.left, top: pos.top }}
            aria-hidden
          >
            {p.emoji}
          </span>
        );
      })}
    </>
  );
}

/** Resolve a synced panel cursor/tap (`area` = market / roster / lobby) to a
 * LOCAL viewport point. A `target` key (`card:<id>` / `player:<id>` /
 * `history:<id>` / `lobby:ready`) anchors to that element's local rect, with
 * `tx`/`ty` (0..1 within the element, as measured on the sender) mapped into
 * it — the exact spot on the same card or row here, whatever each client's
 * scroll, collapse state or window size. Falls back to the synced normalised
 * panel coords when the element isn't in this client's DOM. Null when the
 * panel isn't shown. */
function resolvePanelPoint(
  area: string,
  x: number,
  y: number,
  target: string,
  tx: number,
  ty: number,
): { left: number; top: number } | null {
  const panel = document.querySelector(
    area === 'market'
      ? '.sidebar-right'
      : area === 'lobby'
        ? '.lobby-card'
        : '.sidebar-left',
  );
  if (!panel) return null;
  const anchor = target
    ? panel.querySelector(`[data-cursor-target="${CSS.escape(target)}"]`)
    : null;
  if (anchor) {
    const r = anchor.getBoundingClientRect();
    return { left: r.left + tx * r.width, top: r.top + ty * r.height };
  }
  const r = panel.getBoundingClientRect();
  return { left: r.left + x * r.width, top: r.top + y * r.height };
}

/** Other clients' live pointers over the DOM panels — the two sidebars and the
 * pre-game lobby card — as floating name-tagged cursor chips (positions via
 * `resolvePanelPoint`). Chips are pure presence: fixed-positioned,
 * non-interactive, and smoothed by a CSS transform transition between the
 * ~100ms state patches. */
function RemoteSidebarCursors({
  cursors,
  players,
  spectators,
}: {
  cursors: CursorView[];
  players: LobbyPlayer[];
  spectators: SpectatorView[];
}) {
  // Anchor rects live in the DOM, so re-render on scroll/resize to keep a chip
  // glued to its card/row while the local layout moves under a held cursor.
  const [, setTick] = useState(0);
  const anyShown = cursors.length > 0;
  useEffect(() => {
    if (!anyShown) return;
    const bump = () => setTick((t) => t + 1);
    window.addEventListener('resize', bump);
    window.addEventListener('scroll', bump, true);
    return () => {
      window.removeEventListener('resize', bump);
      window.removeEventListener('scroll', bump, true);
    };
  }, [anyShown]);

  return (
    <>
      {cursors.map((c) => {
        const player = players.find((p) => p.id === c.id);
        const spec = player ? undefined : spectators.find((s) => s.id === c.id);
        if (!player && !spec) return null;
        const pos = resolvePanelPoint(c.area, c.x, c.y, c.target, c.tx, c.ty);
        if (!pos) return null;
        const { left, top } = pos;
        const color = player
          ? seatColor(player.seat)
          : `#${SPECTATOR_CURSOR_COLOR.toString(16).padStart(6, '0')}`;
        return (
          <div
            key={c.id}
            className="remote-cursor-chip"
            style={{
              transform: `translate(${Math.round(left)}px, ${Math.round(top)}px)`,
            }}
            aria-hidden
          >
            <svg
              className="remote-cursor-arrow"
              width="13"
              height="19"
              viewBox="-1 -1 13 19"
            >
              <path
                d="M0 0 L0 14 L3.6 10.8 L6.2 16.2 L8.6 15 L6 9.8 L10.6 9.6 Z"
                fill={color}
                stroke="#14110d"
                strokeWidth="1"
              />
            </svg>
            <span className="remote-cursor-name" style={{ background: color }}>
              {player?.name ?? spec?.name}
            </span>
          </div>
        );
      })}
    </>
  );
}

/** One live tap-ripple from a remote touch client (panel areas only; board
 * taps render inside the Pixi canvas). Transient: pushed on the `cursor_tap`
 * message and removed by its own timeout. */
interface TapRippleView {
  key: string;
  area: string;
  x: number;
  y: number;
  target: string;
  /** Position within `target` (0..1), as measured on the tapper's screen. */
  tx: number;
  ty: number;
  /** CSS colour (seat colour / spectator grey). */
  color: string;
}

/** Remote touch taps over the DOM panels, as subtle pulsating ripples — the
 * presence cue for mobile players, who have no hover cursor to stream. Same
 * anchoring rules as the cursor chips (`resolvePanelPoint`); each ripple is a
 * pure CSS animation that the parent removes shortly after it ends. */
function RemoteTapRipples({ taps }: { taps: TapRippleView[] }) {
  return (
    <>
      {taps.map((t) => {
        const pos = resolvePanelPoint(t.area, t.x, t.y, t.target, t.tx, t.ty);
        if (!pos) return null;
        return (
          <span
            key={t.key}
            className="remote-tap-ripple"
            style={{ left: pos.left, top: pos.top, color: t.color }}
            aria-hidden
          >
            <span className="remote-tap-ring" />
            <span className="remote-tap-ring is-second" />
            <span className="remote-tap-dot" />
          </span>
        );
      })}
    </>
  );
}

/** Fallback catalogue when the match config hasn't arrived yet (library /
 * pre-join). Live match UI should prefer `cardsFromConfig(gameConfig)`. */
const DEFAULT_CARDS_BY_ID = fortressCardsById(FORTRESS_CARDS_CONFIG);
const DEFAULT_CARDS_BY_NAME = fortressCardsByName(FORTRESS_CARDS_CONFIG);

function cardsFromConfig(config: GameConfig | null | undefined): {
  byId: Map<string, FortressCard>;
  byName: Map<string, FortressCard>;
} {
  if (!config?.fortressCards) {
    return { byId: DEFAULT_CARDS_BY_ID, byName: DEFAULT_CARDS_BY_NAME };
  }
  return {
    byId: fortressCardsById(config.fortressCards),
    byName: fortressCardsByName(config.fortressCards),
  };
}

const EMPTY_PILE: MarketPile = {
  cards: [],
  locked: [],
  deckCount: 0,
  discardCount: 0,
};

/** Shared empty selection — a stable reference so sections that hold no cycle
 * selection don't get a fresh Set (and a needless re-render) every render. */
const EMPTY_SELECTION: ReadonlySet<number> = new Set<number>();

/** Stable empty map for the market's implement outlines on an ended game. */
const EMPTY_BUILDABLE: ReadonlyMap<string, 'ready' | 'poor'> = new Map();

/** Cycle-Market selection: which market row, and the indices chosen within it.
 * One row at a time — regular and win cards can't be mixed in a single cycle. */
interface CycleSelection {
  kind: FortressKind;
  indices: Set<number>;
}

/** Everything the market sidebar needs to drive the Cycle-Market flow. */
interface CycleControls {
  /** True while the player is choosing cards to cycle. */
  active: boolean;
  /** The row a selection has been started in, or null before the first pick. */
  selectedKind: FortressKind | null;
  /** Selected indices within `selectedKind`'s row. */
  selectedIndices: ReadonlySet<number>;
  /** Running Food cost = selected card count × that row's per-card cost. */
  foodCost: number;
  /** Whether the player can pay `foodCost` from their Food on hand. */
  affordable: boolean;
  onToggleCard: (kind: FortressKind, index: number) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

/** What an open detail popover describes — a market card, a player, or one of a
 * player's implemented fortress cards (which also carries the board nodes it
 * occupies, so the popover can highlight / focus it). */
type SelectionTarget =
  | { kind: 'card'; card: FortressCard }
  | { kind: 'player'; player: LobbyPlayer }
  | {
      kind: 'fortressCard';
      card: FortressCard;
      nodeIds: number[];
      tokens: number;
      disabled: boolean;
      /** Stable per-instance id (a player can hold two of the same card). */
      instanceKey: string;
      /** Opened by hovering the fortress ON the board — the viewport is
       * already there, so the popover hides its "View on board" action. */
      fromBoard?: boolean;
    }
  // A card named in a Turn History entry (e.g. a Cycle's removed / added cards).
  // Distinct from `card` so it isn't pruned when the card leaves the market.
  | { kind: 'historyCard'; card: FortressCard };

/** A target's stable identity, for matching a hover-out to what's showing. */
const targetId = (t: SelectionTarget): string =>
  t.kind === 'card' || t.kind === 'historyCard'
    ? t.card.id
    : t.kind === 'player'
      ? t.player.id
      : t.instanceKey;

/** The open detail popover: a target, the live tile element it anchors to (read
 * fresh each layout so the popover follows the row as the roster reflows), and
 * whether it's `pinned` (opened by a click — stays until dismissed) or a hover
 * preview (closes when the cursor leaves the tile). */
type Selection = SelectionTarget & { anchorEl: HTMLElement; pinned: boolean };

/** Tile interaction callbacks, shared by market card rows and player rows. The
 * tile's own element is passed as the anchor (not a captured rect) so the popover
 * can re-measure it as live state moves it. */
interface SelectionHandlers {
  /** Cursor entered a tile → show its hover preview. */
  onPreview: (target: SelectionTarget, anchorEl: HTMLElement) => void;
  /** Cursor left a tile → drop its hover preview (a pinned target stays). */
  onPreviewEnd: (target: SelectionTarget) => void;
  /** Tile clicked → pin the detail open. */
  onPin: (target: SelectionTarget, anchorEl: HTMLElement) => void;
}

/** Grace period (ms) between the cursor leaving a hover-preview's tile and the
 * preview closing — long enough to cross the gap onto the popover and keep it
 * open. Pinned details ignore this entirely. */
const PREVIEW_CLOSE_DELAY = 140;

interface AppProps {
  authenticated?: boolean;
}

export function App({ authenticated = false }: AppProps) {
  const stageRef = useRef<HTMLDivElement>(null);
  const roomRef = useRef<Room<GameState> | null>(null);
  const gameRef = useRef<PixiGame | null>(null);
  // Mirrors of phase / current player / self id for the board click handler,
  // which is created once and would otherwise close over stale initial values.
  const phaseRef = useRef<string>('lobby');
  const currentPlayerRef = useRef<string>('');
  const selfIdRef = useRef<string>('');
  /** Live per-node ownership/building info, for computing legal build targets.
   * Kept in a ref (not state) — read on demand by the board click handler and
   * the build-range calc, which don't need to re-render on every node change. */
  const nodeInfoRef = useRef<
    Map<
      number,
      {
        ownerId: string;
        building: string;
        fortressOwnerId: string;
        resource: string;
        permanence: string;
        /** Barracks aim: target node id (-1 upright) and posture ("" upright). */
        aimTargetId: number;
        aimMode: string;
        indestructible: boolean;
      }
    >
  >(new Map());
  /** Severed-edge keys (chasms), mirrored for build-range line-of-sight. */
  const severedRef = useRef<Set<string>>(new Set());
  /** Constructed-bridge edge keys, mirrored to exclude already-bridged paths. */
  const bridgesRef = useRef<Set<string>>(new Set());
  /** Bumped on every node/bridge sync. Board state lives in refs (no re-render
   * per node), so memos that read those refs — the market "buildable" outlines —
   * depend on this counter to recompute when the board changes. */
  const [boardVersion, setBoardVersion] = useState(0);
  /** Mirror of `players` for the once-created board hover handler. */
  const playersRef = useRef<LobbyPlayer[]>([]);
  /** Mirror of match-pinned card catalogue for the board hover handler. */
  const cardsByIdRef = useRef(DEFAULT_CARDS_BY_ID);
  /** Mirror of `selectionHandlers` for the same reason. */
  const selectionHandlersRef = useRef<SelectionHandlers | null>(null);
  /** Invisible fixed-position element the board fortress popover anchors to —
   * moved to the hovered node's screen position (popovers anchor to DOM
   * elements; board nodes live on the canvas). */
  const boardAnchorRef = useRef<HTMLDivElement>(null);
  /** The fortress target currently previewed from a board hover, if any. */
  const boardHoverRef = useRef<SelectionTarget | null>(null);
  const [turn, setTurn] = useState<number | null>(null);
  const [round, setRound] = useState<number | null>(null);
  /** Action points left in the current turn — drives the "action n/total" readout
   * and whether the End-Turn button means "pass" (none spent) or "end turn". */
  const [actionsRemaining, setActionsRemaining] = useState<number>(0);
  const [currentPlayerId, setCurrentPlayerId] = useState<string>('');
  const [phase, setPhase] = useState<string>('lobby');
  const [players, setPlayers] = useState<LobbyPlayer[]>([]);
  const [lobbyInfo, setLobbyInfo] = useState<LobbyInfo>({
    hostUserId: '',
    inviteCode: '',
    visibility: 'public',
    maxPlayers: 8,
    autoStartAtMs: 0,
  });
  /** Selected Colyseus room — null shows the lobby hub. `reconnectionToken`
   * is set when re-entering a live game after a refresh (reconnect, not join).
   * `spectate` joins the room watch-only (no seat; commands are ignored). */
  const [activeRoom, setActiveRoom] = useState<{
    roomId: string;
    reconnectionToken?: string;
    spectate?: boolean;
  } | null>(null);
  /** Everyone currently watching this room (synced `GameState.spectators`). */
  const [spectators, setSpectators] = useState<SpectatorView[]>([]);
  const spectatorsRef = useRef<SpectatorView[]>([]);
  /** Other clients' live pointers over the sidebars (synced `GameState.cursors`,
   * areas "market"/"roster") — rendered as DOM chips anchored to the hovered
   * card/row. Board-area cursors deliberately bypass React: they update ~5Hz
   * and go straight to the Pixi layer (see `pushBoardCursors`). */
  const [sidebarCursors, setSidebarCursors] = useState<CursorView[]>([]);
  const liveCursorsRef = useRef<CursorView[]>([]);
  /** In-flight remote tap ripples over the DOM panels (board taps go straight
   * to Pixi). Each entry removes itself ~a second after it lands. */
  const [tapRipples, setTapRipples] = useState<TapRippleView[]>([]);
  const tapSeqRef = useRef(0);
  /** Open emoji picker (right-click / long-press): where to render it, and
   * the classified point the picked reaction will pin to. */
  const [emojiPicker, setEmojiPicker] = useState<{
    screen: { x: number; y: number };
    sample: CursorSample;
  } | null>(null);
  /** In-flight emoji reactions over the DOM panels (board reactions render in
   * Pixi). Each entry removes itself when its animation ends. */
  const [emojiPops, setEmojiPops] = useState<EmojiPopView[]>([]);
  /** The last reconnect attempt failed (grace lapsed / game gone) — surfaced
   * on the lobby hub once, cleared on the next room entry. */
  const [reconnectFailed, setReconnectFailed] = useState(false);
  /** The live room's socket dropped unexpectedly (Cloud Run's request
   * timeout, a scale event, network) and the in-place reconnect ladder is
   * running — shows the "reconnecting" toast over the board. */
  const [connectionLost, setConnectionLost] = useState(false);
  const [markets, setMarkets] = useState<MarketView>({
    regular: EMPTY_PILE,
    win: EMPTY_PILE,
  });
  const [marketCollapsed, setMarketCollapsed] = useState(false);
  const [playersCollapsed, setPlayersCollapsed] = useState(true);
  /** Full move history (oldest first), synced from `GameState.moveHistory`. */
  const [history, setHistory] = useState<HistoryEntryView[]>([]);
  /** Left sidebar shows the Turn History list instead of the player roster. */
  const [historyMode, setHistoryMode] = useState(false);
  /** Queue-planning mode: the upcoming-turn slot (1..MAX_QUEUE_TURNS) new
   * moves are queued into, or null when not planning. While set (off-turn),
   * the normal action bar and pickers render and route their actions into the
   * private queue instead of sending them live. */
  const [planSlot, setPlanSlot] = useState<number | null>(null);
  /** Mirror of `planSlot` for the once-created board click handler. */
  const planSlotRef = useRef<number | null>(null);
  useEffect(() => {
    planSlotRef.current = planSlot;
  }, [planSlot]);
  /** The queued move currently SELECTED in the queue panel (slot + index
   * within that slot), or null. Its preview markers — the board ghost and the
   * market "queued" badge — show only while it is selected. */
  const [queuedSel, setQueuedSel] = useState<{
    slot: number;
    index: number;
  } | null>(null);
  /** Queued-move auto-play halted on our turn (server `queue_halted` event);
   * drives the explanatory toast. Cleared after `QUEUE_HALT_TTL`. */
  const [queueHalt, setQueueHalt] = useState<{
    label: string;
    reason: string;
  } | null>(null);
  const queueHaltTimer = useRef<number | undefined>(undefined);
  /** Construct-building flow (pick a building, then place it), or null when off. */
  const [buildFlow, setBuildFlow] = useState<BuildFlow>(null);
  /** Mirror of `buildFlow` for the once-created board click handler. */
  const buildFlowRef = useRef<BuildFlow>(null);
  const [aimFlow, setAimFlow] = useState<AimFlow>(null);
  /** Mirror of `aimFlow` for the once-created board click handler. */
  const aimFlowRef = useRef<AimFlow>(null);
  useEffect(() => {
    aimFlowRef.current = aimFlow;
  }, [aimFlow]);
  /** Card-Effects flow (the tray of your implemented cards' effects), or null. */
  const [effectsFlow, setEffectsFlow] = useState<EffectsFlow>(null);
  /** Mirror of `effectsFlow` for the once-created board click handler. */
  const effectsFlowRef = useRef<EffectsFlow>(null);
  useEffect(() => {
    effectsFlowRef.current = effectsFlow;
  }, [effectsFlow]);
  /** Mirror of `computeAimTargets` for the same handler (it closes over live
   * state — gameConfig, selfId — the once-created handler can't see). */
  const computeAimTargetsRef = useRef<
    (barracksId: number, mode: AimMode, defense: boolean) => ReadonlySet<number>
  >(() => new Set<number>());
  /** The end-of-turn defense window, as synced: whether one is open and which
   * players (session ids) still have to respond. */
  const [defense, setDefense] = useState<{ active: boolean; pending: string[] }>(
    { active: false, pending: [] },
  );
  /** Post-combat Barracks upkeep choices still unresolved, as synced — each
   * entry `"playerId|nodeId|foodCost"`. Non-empty freezes the ending turn
   * until every owner keeps (pays) or starves their attacking Barracks. */
  const [upkeepEntries, setUpkeepEntries] = useState<string[]>([]);
  useEffect(() => {
    buildFlowRef.current = buildFlow;
  }, [buildFlow]);
  /** Construct-bridge flow: the eligible paths to bridge, or null when off.
   * `allowed` are tappable; `tooCostly` are in range but unaffordable (dim);
   * `allowedKeys` is the canonical edge-key set for the click check. With a
   * WILDCARD bridge cost (Bridgette's "any resource"), tapping a path parks it
   * in `pending` and the bar asks which resource pays before sending. */
  const [bridgeFlow, setBridgeFlow] = useState<{
    allowed: [number, number][];
    tooCostly: [number, number][];
    allowedKeys: ReadonlySet<string>;
    wildcard: boolean;
    pending: { from: number; to: number } | null;
  } | null>(null);
  /** Mirror of `bridgeFlow` for the once-created board edge-click handler. */
  const bridgeFlowRef = useRef<typeof bridgeFlow>(null);
  useEffect(() => {
    bridgeFlowRef.current = bridgeFlow;
  }, [bridgeFlow]);
  /** Implement-Fortress flow (pick a card, then place it), or null when off. */
  const [implementFlow, setImplementFlow] = useState<ImplementFlow | null>(
    null,
  );
  /** Mirror of `implementFlow` for the once-created board click handler. */
  const implementFlowRef = useRef<ImplementFlow | null>(null);
  useEffect(() => {
    implementFlowRef.current = implementFlow;
  }, [implementFlow]);
  /** Cycle-Market flow: on while picking cards, plus the current selection. */
  const [cycleMode, setCycleMode] = useState(false);
  const [cycleSel, setCycleSel] = useState<CycleSelection | null>(null);
  /** Current First-Turn marker holder — the player expected to open the next
   * round (synced `firstPlayerId`). Gates the round-start cycle queue UI. */
  const [firstPlayerId, setFirstPlayerId] = useState('');
  /** Who the open round-start cycle window waits on ('' = closed). While set,
   * the round is paused before anything else happens: the first player picks
   * up to one card per market to cycle out free, or cycles nothing. */
  const [roundCyclerId, setRoundCyclerId] = useState('');
  /** Round-start cycle picking: 'live' answers the open window, 'queue'
   * pre-plans next round's choice; null = off. At most one card per market. */
  const [rsMode, setRsMode] = useState<'live' | 'queue' | null>(null);
  const [rsSel, setRsSel] = useState<{
    regular: string | null;
    win: string | null;
  }>({ regular: null, win: null });
  const [libraryOpen, setLibraryOpen] = useState(false);
  /** The match result, synced from state once `phase === "ended"` (covers live
   * game-over, reconnects and late spectators alike); drives the top result
   * banner. `winnerId` null = draw; `winCardId` names the fortress card whose
   * win condition ended the game. */
  const [gameOver, setGameOver] = useState<{
    winnerId: string | null;
    winCardId: string;
  } | null>(null);
  const [selection, setSelection] = useState<Selection | null>(null);
  const [selfId, setSelfId] = useState<string>('');
  /** Setup phase: the node tentatively picked for the home base, pending Confirm. */
  const [selectedHomeNode, setSelectedHomeNode] = useState<number | null>(null);
  /** The match's pinned config snapshot, sent by the room on join. Drives rule
   * values the client shows (e.g. production points to distribute). */
  const [gameConfig, setGameConfig] = useState<GameConfig | null>(null);
  /** Server-advertised dev mode (non-prod). Gates the lobby's Auto-Initialize
   * button; the server independently rejects the command when this is false. */
  const [devMode, setDevMode] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  /** Live element of each player's roster row, keyed by id, so an action-notice
   * popup can anchor beside the player who just acted. Populated by callback refs
   * in `PlayerSidebar`. */
  const playerRowEls = useRef<Map<string, HTMLElement>>(new Map());
  const registerRow = useCallback((id: string, el: HTMLElement | null) => {
    if (el) playerRowEls.current.set(id, el);
    else playerRowEls.current.delete(id);
  }, []);
  /** Active action notifications — at most one per player (a newer action by the
   * same player replaces the older), each auto-dismissed after `NOTICE_TTL`. */
  const [notices, setNotices] = useState<ActiveNotice[]>([]);
  /** Per-player auto-dismiss timers, so a replaced/closed notice clears its timer. */
  const noticeTimers = useRef<Map<string, number>>(new Map());
  /** Shows the top-screen "It's Your Turn" banner; cleared after `TURN_TOAST_TTL`. */
  const [turnToast, setTurnToast] = useState(false);
  const turnToastTimer = useRef<number | undefined>(undefined);
  /** Monotonic key so React remounts (and re-animates) the popup on each action,
   * even when it replaces the same player's previous one. */
  const noticeSeq = useRef(0);

  const dismissNotice = useCallback((playerId: string) => {
    setNotices((cur) => cur.filter((n) => n.playerId !== playerId));
    const t = noticeTimers.current.get(playerId);
    if (t !== undefined) {
      window.clearTimeout(t);
      noticeTimers.current.delete(playerId);
    }
  }, []);

  const pushNotice = useCallback((notice: ActionNotice) => {
    const key = (noticeSeq.current += 1);
    setNotices((cur) => [
      ...cur.filter((n) => n.playerId !== notice.playerId),
      { ...notice, key },
    ]);
    const prev = noticeTimers.current.get(notice.playerId);
    if (prev !== undefined) window.clearTimeout(prev);
    const t = window.setTimeout(() => {
      setNotices((cur) => cur.filter((n) => n.playerId !== notice.playerId));
      noticeTimers.current.delete(notice.playerId);
    }, NOTICE_TTL);
    noticeTimers.current.set(notice.playerId, t);
  }, []);

  // Clear any in-flight dismiss timers on unmount.
  useEffect(() => {
    const timers = noticeTimers.current;
    return () => {
      for (const t of timers.values()) window.clearTimeout(t);
      timers.clear();
    };
  }, []);

  // Clear the turn-toast timer on unmount.
  useEffect(
    () => () => {
      if (turnToastTimer.current !== undefined)
        window.clearTimeout(turnToastTimer.current);
    },
    [],
  );

  // Clear the queue-halt toast timer on unmount.
  useEffect(
    () => () => {
      if (queueHaltTimer.current !== undefined)
        window.clearTimeout(queueHaltTimer.current);
    },
    [],
  );

  /** Send a game action live — or, while planning, queue it for the chosen
   * upcoming turn instead. Every action-flow send routes through here so the
   * same pickers serve both modes. Reactive / off-turn sends (defense
   * reactions, iCarlie claims, prompt responses) bypass this deliberately. */
  const sendGameAction = useCallback((action: GameAction) => {
    const room = roomRef.current;
    if (!room) return;
    const slot = planSlotRef.current;
    if (slot !== null) {
      room.send(ClientMessage.QueueMove, {
        slot,
        action,
        label: queueMoveLabel(action, cardsByIdRef.current),
      });
      return;
    }
    room.send(ClientMessage.Action, action);
  }, []);
  /** Mirror for the once-created board click handler. */
  const sendGameActionRef = useRef(sendGameAction);
  useEffect(() => {
    sendGameActionRef.current = sendGameAction;
  }, [sendGameAction]);

  // Reflect "it's your turn" in the browser tab title, so a backgrounded tab
  // still signals it; revert to the default the moment it's no longer your turn.
  useEffect(() => {
    const myTurn = !!selfId && currentPlayerId === selfId;
    document.title = myTurn ? `Your Turn! | ${DEFAULT_TITLE}` : DEFAULT_TITLE;
    return () => {
      document.title = DEFAULT_TITLE;
    };
  }, [currentPlayerId, selfId]);

  // Ask for OS-notification permission once, on the first user gesture (browsers
  // reject an unprompted request). Best-effort: if it's denied or unsupported,
  // the turn notification simply never fires — the toast still does.
  useEffect(() => {
    if (
      typeof Notification === 'undefined' ||
      Notification.permission !== 'default'
    )
      return;
    const ask = () => {
      void Notification.requestPermission();
      window.removeEventListener('pointerdown', ask);
    };
    window.addEventListener('pointerdown', ask);
    return () => window.removeEventListener('pointerdown', ask);
  }, []);

  // Drop notifications for players who have since left (their anchor row is gone).
  useEffect(() => {
    const ids = new Set(players.map((p) => p.id));
    setNotices((cur) => cur.filter((n) => ids.has(n.playerId)));
  }, [players]);

  /** Feed board-area remote cursors straight into the Pixi layer, resolving
   * each session id to a seat colour + name (players) or the spectator grey.
   * Reads refs (not state) so the state watcher can call it at patch rate
   * without re-rendering the app; unresolvable ids (roster not yet synced)
   * are skipped and self-heal on the next patch. */
  const pushBoardCursors = useCallback(() => {
    const game = gameRef.current;
    if (!game) return;
    const list: RemoteCursorView[] = [];
    for (const c of liveCursorsRef.current) {
      if (c.area !== 'board' || c.id === selfIdRef.current) continue;
      const player = playersRef.current.find((p) => p.id === c.id);
      const spec = player
        ? undefined
        : spectatorsRef.current.find((s) => s.id === c.id);
      if (!player && !spec) continue;
      list.push({
        id: c.id,
        x: c.x,
        y: c.y,
        color: player
          ? (PLAYER_COLORS[player.seat % PLAYER_COLORS.length] ?? 0xffffff)
          : SPECTATOR_CURSOR_COLOR,
        name: player?.name ?? spec?.name ?? '',
      });
    }
    game.setRemoteCursors(list);
  }, []);

  // Re-resolve cursor colours/names when the roster changes (seats are
  // reassigned at game start; a held-still cursor would otherwise keep its
  // stale colour until its owner next moved).
  useEffect(() => {
    pushBoardCursors();
  }, [players, spectators, pushBoardCursors]);

  /** Play an emoji reaction locally — used for both incoming broadcasts and
   * the local echo of our own pick (the server relays to everyone else).
   * Board reactions render in the Pixi canvas at the shared board point;
   * panel reactions become a transient DOM pop anchored like the cursors. */
  const spawnEmoji = useCallback((r: Omit<EmojiPopView, 'key'>) => {
    if (r.area === 'board') {
      gameRef.current?.showEmoji({ x: r.x, y: r.y, emoji: r.emoji });
      return;
    }
    const pop: EmojiPopView = { ...r, key: `emoji:${++tapSeqRef.current}` };
    setEmojiPops((cur) => [...cur, pop]);
    window.setTimeout(
      () => setEmojiPops((cur) => cur.filter((p) => p !== pop)),
      1600,
    );
  }, []);

  useEffect(() => {
    if (!activeRoom) return;
    const { roomId, reconnectionToken, spectate } = activeRoom;

    let game: PixiGame | null = null;
    let room: Room<GameState> | null = null;
    let disposed = false;
    // Stale from a prior room whose recovery was cut short by leaving it.
    setConnectionLost(false);
    setGameOver(null);
    liveCursorsRef.current = [];
    setSidebarCursors([]);
    setTapRipples([]);
    setEmojiPops([]);
    setEmojiPicker(null);

    async function boot() {
      const parent = stageRef.current;
      if (!parent) return;

      game = await createPixiGame(parent, (nodeId) => {
        const room = roomRef.current;
        if (!room) return;
        // Setup phase: a click on your turn *selects* a legal home node (pending
        // a Confirm); it isn't committed until the Confirm button is pressed.
        if (phaseRef.current === 'setup') {
          if (
            currentPlayerRef.current === selfIdRef.current &&
            gameRef.current?.isLegalHomeNode(nodeId)
          ) {
            setSelectedHomeNode(nodeId);
          }
          return;
        }
        // Reactive Scout protection (defense window): tap an at-risk Scout to
        // spend a Godspeed charge on it — free, and the window stays open for
        // further reactions until Done.
        const dp = defenseProtectRef.current;
        if (dp) {
          if (dp.allowed.has(nodeId)) {
            room.send(ClientMessage.Action, {
              type: 'make_scout_indestructible',
              cardId: dp.cardId,
              nodeId,
            });
            setDefenseProtect(null);
          }
          return;
        }
        // Playing: the aim flow claims board clicks while it's open — first the
        // Barracks pick, then the target pick (both only on highlighted nodes).
        const af = aimFlowRef.current;
        if (af) {
          if (af.stage === 'pickBarracks' && af.sources.has(nodeId)) {
            if (af.defense) {
              // Defense reactions are always DEFEND — skip the posture picker.
              setAimFlow({
                stage: 'target',
                barracksId: nodeId,
                mode: 'defend',
                defense: true,
                allowed: computeAimTargetsRef.current(nodeId, 'defend', true),
              });
            } else {
              setAimFlow({
                stage: 'mode',
                barracksId: nodeId,
                // Planning never gates the posture — the board may differ by
                // the time the queued aim plays.
                attackable:
                  planSlotRef.current !== null ||
                  (nodeInfoRef.current.get(nodeId)?.fortressOwnerId ?? '') ===
                    '',
                defense: false,
              });
            }
          } else if (af.stage === 'target' && af.allowed.has(nodeId)) {
            const aim = {
              type: 'aim_barracks',
              nodeId: af.barracksId,
              targetId: nodeId,
              mode: af.mode,
            } as const;
            // Defense reactions are off-turn responses — never queued.
            if (af.defense) room.send(ClientMessage.Action, aim);
            else sendGameActionRef.current(aim);
            setAimFlow(null);
          }
          return;
        }
        // Card-Effects board picks: protect-a-Scout sends on the tap; the
        // untap flow toggles Barracks in/out of the selection (bounded by the
        // card's remaining per-round allowance) until its Confirm.
        const ef = effectsFlowRef.current;
        if (ef) {
          if (ef.stage === 'pickScout' && ef.allowed.has(nodeId)) {
            room.send(ClientMessage.Action, {
              type: 'make_scout_indestructible',
              cardId: ef.cardId,
              nodeId,
            });
            setEffectsFlow(null);
          } else if (ef.stage === 'replicate' && ef.allowed.has(nodeId)) {
            sendGameActionRef.current({
              type: 'replicate_fortress',
              cardId: ef.cardId,
              nodeIds: [nodeId],
            });
            setEffectsFlow(null);
          } else if (
            (ef.stage === 'untap' || ef.stage === 'moveFortress') &&
            ef.allowed.has(nodeId)
          ) {
            setEffectsFlow((cur) => {
              if (cur?.stage !== 'untap' && cur?.stage !== 'moveFortress')
                return cur;
              const selected = new Set(cur.selected);
              if (selected.has(nodeId)) selected.delete(nodeId);
              else if (selected.size < cur.limit) selected.add(nodeId);
              return { ...cur, selected };
            });
          }
          return;
        }
        // Implement-Fortress placement: tap a highlighted node to choose where
        // the fortress lands. The selected placement is the first match
        // covering that node; tapping the same node again cycles through the
        // other placements over it (same as the Rotate button).
        const impl = implementFlowRef.current;
        if (impl) {
          if (impl.stage === 'place' && impl.candidates.has(nodeId)) {
            setImplementFlow((cur) => {
              if (cur?.stage !== 'place') return cur;
              const options: number[] = [];
              cur.matches.forEach((m, i) => {
                if (nodesOfMatch(m).includes(nodeId)) options.push(i);
              });
              if (options.length === 0) return cur;
              const pos =
                cur.selected?.nodeId === nodeId
                  ? (cur.selected.pos + 1) % options.length
                  : 0;
              return { ...cur, selected: { nodeId, options, pos } };
            });
          }
          return;
        }
        // A click only does something while placing a building — and only on a
        // highlighted (legal) target. Otherwise it's a no-op (other actions are
        // driven from the action bar, not a bare board click). The click just
        // SELECTS the node (highlighted, pending Confirm); the construct action
        // is sent by `confirmBuild` when the Confirm button is pressed.
        const bf = buildFlowRef.current;
        if (bf?.stage === 'place' && bf.allowed.has(nodeId)) {
          setBuildFlow((cur) =>
            cur?.stage === 'place' ? { ...cur, selected: nodeId } : cur,
          );
        }
      }, (a, b) => {
        // Edge tap during the construct-bridge flow: build a bridge on that path
        // if it's one of the highlighted (eligible) paths.
        const room = roomRef.current;
        if (!room) return;
        const bf = bridgeFlowRef.current;
        if (!bf || bf.pending || !bf.allowedKeys.has(edgeKey(a, b))) return;
        if (bf.wildcard) {
          // Wildcard cost: park the path; the bar asks which resource pays.
          setBridgeFlow((cur) =>
            cur ? { ...cur, pending: { from: a, to: b } } : cur,
          );
          return;
        }
        sendGameActionRef.current({ type: 'construct_bridge', from: a, to: b });
        setBridgeFlow(null);
      }, (nodeId) => {
        // Board hover: over a node of an implemented fortress (anyone's), show
        // the same fortress-card detail popover the roster tiles use, anchored
        // at the node. The popup reflects the OWNER's live card state — tokens
        // and exhausted/disabled — resolved fresh at render time.
        const handlers = selectionHandlersRef.current;
        const current = boardHoverRef.current;
        // While a card effect is picking board nodes — or the Implement flow is
        // placing a fortress — the fortress preview is suppressed: its popover
        // shares the gold-ring highlight channel with those selections, so a
        // hover would wipe the player's picks.
        const ef = effectsFlowRef.current;
        if (
          ef?.stage === 'pickScout' ||
          ef?.stage === 'replicate' ||
          ef?.stage === 'untap' ||
          ef?.stage === 'moveFortress' ||
          implementFlowRef.current?.stage === 'place'
        ) {
          if (current && handlers) {
            boardHoverRef.current = null;
            handlers.onPreviewEnd(current);
          }
          return;
        }
        if (nodeId !== null) {
          const fOwner = nodeInfoRef.current.get(nodeId)?.fortressOwnerId ?? '';
          if (fOwner !== '') {
            const owner = playersRef.current.find((p) => p.id === fOwner);
            const idx =
              owner?.fortressCards.findIndex((fc) =>
                fc.nodeIds.includes(nodeId),
              ) ?? -1;
            const fc = idx >= 0 ? owner?.fortressCards[idx] : undefined;
            const card = fc ? cardsByIdRef.current.get(fc.cardId) : undefined;
            const anchor = boardAnchorRef.current;
            const pos = gameRef.current?.nodeScreenPosition(nodeId);
            if (owner && fc && card && anchor && pos && handlers) {
              anchor.style.left = `${pos.x}px`;
              anchor.style.top = `${pos.y}px`;
              const target: SelectionTarget = {
                kind: 'fortressCard',
                card,
                nodeIds: [...fc.nodeIds],
                tokens: fc.tokens,
                disabled: fc.disabled,
                instanceKey: `${owner.id}:${idx}`,
                fromBoard: true,
              };
              boardHoverRef.current = target;
              handlers.onPreview(target, anchor);
              return;
            }
          }
        }
        // Left the node (or entered a non-fortress one): drop the board-driven
        // preview, with the usual grace period to reach the popover itself.
        if (current && handlers) {
          boardHoverRef.current = null;
          handlers.onPreviewEnd(current);
        }
      });
      if (disposed) {
        game.destroy();
        game = null;
        return;
      }
      gameRef.current = game;

      /** Reconnect ladder for an unexpected socket close. First the
       * reconnection token (the seat is held under the server's grace
       * period), with backoff for transient failures — Cloud Run may be
       * cold-starting a fresh instance. A definitive rejection means the
       * token is dead; try one authed joinById (the returning-participant
       * gate re-seats by account) before giving up to the hub, where the
       * saved-match list offers Resume for a game that survived in Mongo. */
      const recover = async (lost: Room<GameState>): Promise<void> => {
        setConnectionLost(true);
        const delays = [1000, 2000, 4000, 8000, 15000];
        for (let attempt = 0; attempt <= delays.length; attempt++) {
          if (attempt > 0) {
            await new Promise((resolve) =>
              setTimeout(resolve, delays[attempt - 1]),
            );
            if (disposed) return;
          }
          try {
            const fresh = await reconnectGame(lost.reconnectionToken);
            if (disposed) {
              void fresh.leave();
              return;
            }
            attach(fresh);
            setConnectionLost(false);
            return;
          } catch (err) {
            // The matchmaker rejected the token outright (room disposed /
            // not found, token invalid or expired) — retrying it is
            // pointless; fall through to joinById. Anything else (network,
            // cold start, the ws-stage "seat reservation expired" — a plain
            // Error with no code) is transient: keep the ladder going.
            const code = (err as { code?: number }).code;
            if (
              code === ErrorCode.MATCHMAKE_INVALID_ROOM_ID ||
              code === ErrorCode.MATCHMAKE_EXPIRED
            ) {
              break;
            }
          }
        }
        try {
          const fresh = await joinGameById(lost.roomId, { spectate });
          if (disposed) {
            void fresh.leave();
            return;
          }
          attach(fresh);
          setConnectionLost(false);
          return;
        } catch {
          // Room gone for good. The hub offers Resume if the match survived.
        }
        if (disposed) return;
        setConnectionLost(false);
        void removeActiveGameToken(lost.roomId);
        setActiveRoom(null);
      };

      // Wire a (re)joined room: message handlers, state watchers, and the
      // unexpected-close recovery hook. Called for the initial join and again
      // for every recovered connection — each fresh Room instance needs its
      // own wiring.
      const attach = (r: Room<GameState>): void => {
        room = r;
        roomRef.current = r;
        setSelfId(r.sessionId);
        selfIdRef.current = r.sessionId;
        if (import.meta.env.DEV) {
          // Dev-only handle for E2E drivers (canvas state isn't reachable from
          // the DOM). Stripped from production builds by the DEV guard.
          (
            window as unknown as { __fortressRoom?: Room<GameState> }
          ).__fortressRoom = r;
        }

        // Cloud Run force-closes every WebSocket at its request timeout, and
        // instances go away on scale-to-zero and redeploys — an unexpected
        // close is routine, not an error. Recover in place; the server holds
        // this seat for the reconnect grace period. `disposed` covers our own
        // deliberate leave (cleanup), and a finished match just wound down.
        // 4000 = colyseus CloseCode.CONSENTED (not re-exported from the
        // package root): a deliberate close — our own leave racing the effect
        // cleanup, or the server's graceful-shutdown force-close. The room is
        // going away, so skip the ladder; the hub offers Resume if the match
        // survived.
        r.onLeave((code) => {
          if (disposed || roomRef.current !== r) return;
          if (phaseRef.current === 'ended') return;
          if (code === 4000) {
            setActiveRoom(null);
            return;
          }
          void recover(r);
        });

        // The room sends its pinned config snapshot once on join.
        r.onMessage(ServerMessage.Config, (config: GameConfig) =>
          setGameConfig(config),
        );

        // Another player took an action: show a transient popup anchored to them.
        // Ignore our own actions — the popup is for what *others* do on their turn.
        r.onMessage(
          ServerMessage.ActionPerformed,
          (notice: ActionNotice) => {
            if (!notice || notice.playerId === selfIdRef.current) return;
            pushNotice(notice);
          },
        );

        // It just became our turn — the server targets this only at the active
        // player. Pop the top-screen toast, and fire an OS notification when the
        // tab is in the background (and the player has granted permission).
        r.onMessage(ServerMessage.YourTurn, () => {
          audio.play(Math.random() < 0.5 ? 'turn' : 'turn2');
          setTurnToast(true);
          if (turnToastTimer.current !== undefined)
            window.clearTimeout(turnToastTimer.current);
          turnToastTimer.current = window.setTimeout(
            () => setTurnToast(false),
            TURN_TOAST_TTL,
          );
          if (
            typeof Notification !== 'undefined' &&
            Notification.permission === 'granted' &&
            document.hidden
          ) {
            new Notification("It's Your Turn", {
              body: `${DEFAULT_TITLE} — it's your move.`,
            });
          }
        });

        // Our queued-move auto-play stopped: a queued move was stale (the
        // board changed since it was planned) or a card effect needs live
        // input. The game is paused on our turn — surface why.
        r.onMessage(
          ServerMessage.QueueHalted,
          (payload: { label?: string; reason?: string }) => {
            setQueueHalt({
              label: payload?.label ?? '',
              reason: payload?.reason ?? '',
            });
            if (queueHaltTimer.current !== undefined)
              window.clearTimeout(queueHaltTimer.current);
            queueHaltTimer.current = window.setTimeout(
              () => setQueueHalt(null),
              QUEUE_HALT_TTL,
            );
          },
        );

        // A defense window just opened on us: buildings of ours fall when the
        // ending turn resolves. Pan to the first at-risk node, and raise an OS
        // notification when the tab is backgrounded (it isn't our turn, so the
        // player may well be elsewhere). The persistent banner + aim flow are
        // driven off the synced defense state, not this one-shot cue.
        r.onMessage(
          ServerMessage.UnderAttack,
          (payload: { nodeIds?: number[] }) => {
            const first = payload?.nodeIds?.[0];
            if (first !== undefined) gameRef.current?.focusNode(first);
            if (
              typeof Notification !== 'undefined' &&
              Notification.permission === 'granted' &&
              document.hidden
            ) {
              new Notification('Under Attack!', {
                body: `${DEFAULT_TITLE} — aim defenders before the turn resolves.`,
              });
            }
          },
        );

        // The game-over result is driven off synced state (`onGameResult`
        // below), not the one-shot GameOver broadcast — the synced fields also
        // reach reconnecting players and late-joining spectators. Registered
        // as a no-op so colyseus.js doesn't warn about the unhandled type.
        r.onMessage(ServerMessage.GameOver, () => {});

        // A touch client tapped a shared surface (mobile's stand-in for the
        // hover cursor): play a one-shot ripple where it landed — in the Pixi
        // layer for board taps, as a DOM ripple over a sidebar/lobby panel.
        r.onMessage(
          ServerMessage.CursorTap,
          (payload: {
            id?: string;
            area?: string;
            x?: number;
            y?: number;
            target?: string;
            tx?: number;
            ty?: number;
          }) => {
            const id = payload?.id ?? '';
            if (!id || id === selfIdRef.current) return;
            const player = playersRef.current.find((p) => p.id === id);
            const spec = player
              ? undefined
              : spectatorsRef.current.find((s) => s.id === id);
            if (!player && !spec) return;
            const colorNum = player
              ? (PLAYER_COLORS[player.seat % PLAYER_COLORS.length] ?? 0xffffff)
              : SPECTATOR_CURSOR_COLOR;
            const x = Number(payload?.x) || 0;
            const y = Number(payload?.y) || 0;
            if (payload?.area === 'board') {
              gameRef.current?.showRemoteTap({ x, y, color: colorNum });
              return;
            }
            const ripple: TapRippleView = {
              key: `${id}:${++tapSeqRef.current}`,
              area: payload?.area ?? '',
              x,
              y,
              target: payload?.target ?? '',
              tx: unitOrCenter(payload?.tx),
              ty: unitOrCenter(payload?.ty),
              color: `#${colorNum.toString(16).padStart(6, '0')}`,
            };
            setTapRipples((cur) => [...cur, ripple]);
            window.setTimeout(
              () => setTapRipples((cur) => cur.filter((t) => t !== ripple)),
              1100,
            );
          },
        );

        // Another client dropped an emoji reaction — play it at the same
        // spot (board point, or panel element via the target key).
        r.onMessage(
          ServerMessage.CursorEmoji,
          (payload: {
            id?: string;
            area?: string;
            x?: number;
            y?: number;
            target?: string;
            tx?: number;
            ty?: number;
            emoji?: string;
          }) => {
            const id = payload?.id ?? '';
            if (!id || id === selfIdRef.current) return;
            const emoji =
              typeof payload?.emoji === 'string' ? payload.emoji : '';
            if (!emoji) return;
            spawnEmoji({
              area: payload?.area ?? '',
              x: Number(payload?.x) || 0,
              y: Number(payload?.y) || 0,
              target: payload?.target ?? '',
              tx: unitOrCenter(payload?.tx),
              ty: unitOrCenter(payload?.ty),
              emoji,
            });
          },
        );

        watchState(r, {
          onNode: (node, seatOf) => {
            // Track ownership/building/resource for the build-range + cost calc.
            nodeInfoRef.current.set(node.id, {
              ownerId: node.ownerId,
              building: node.building,
              fortressOwnerId: node.fortressOwnerId,
              resource: node.resource,
              permanence: node.permanence,
              aimTargetId: node.aimTargetId,
              aimMode: node.aimMode,
              indestructible: node.indestructible,
            });
            const colorOf = (playerId: string): number | null =>
              playerId !== ''
                ? (PLAYER_COLORS[
                    (seatOf.get(playerId) ?? 0) % PLAYER_COLORS.length
                  ] ?? 0xffffff)
                : null;
            game?.setNodeState(node.id, {
              ownerColor: colorOf(node.ownerId),
              building: node.building,
              resource: node.resource,
              permanence: node.permanence,
              disconnected: node.disconnected,
              isHomeBase: node.isHomeBase,
              fortressColor: colorOf(node.fortressOwnerId),
            });
            setBoardVersion((v) => v + 1);
          },
          onTurn: setTurn,
          onRound: setRound,
          onActionsRemaining: setActionsRemaining,
          onCurrentPlayer: (id) => {
            setCurrentPlayerId(id);
            currentPlayerRef.current = id;
          },
          onFirstPlayer: setFirstPlayerId,
          onRoundStartCycle: setRoundCyclerId,
          onPhase: (p) => {
            setPhase(p);
            phaseRef.current = p;
            // Reconnect bookkeeping: while the match is live, keep this
            // room's reconnection token stored so the lobby hub can offer
            // "Reconnect" after a refresh (which rooms still qualify is the
            // server's call, via /lobbies/mine); a finished match drops its
            // token. Runs again after a reconnect, re-storing the freshly
            // minted token.
            const r = roomRef.current;
            if (!r) return;
            // A spectator holds no seat, so there is nothing to reconnect to —
            // never advertise their room on the hub's guest reconnect list.
            if (spectate) return;
            if (p === 'setup' || p === 'playing') {
              void setActiveGameToken(r.roomId, r.reconnectionToken);
            } else if (p === 'ended') {
              void removeActiveGameToken(r.roomId);
            }
          },
          onLobbyInfo: setLobbyInfo,
          onPlayers: (roster) => {
            setPlayers(roster);
            playersRef.current = roster;
          },
          onMarkets: setMarkets,
          onPlacements: (placements) => game?.applyPlacements(placements),
          onSevered: (severedEdges) => {
            severedRef.current = new Set(severedEdges);
            game?.setSeveredEdges(severedEdges);
          },
          onBridges: (bridges) => {
            bridgesRef.current = new Set(bridges);
            game?.setBridges(bridges);
            setBoardVersion((v) => v + 1);
          },
          onHistory: setHistory,
          onDefense: setDefense,
          onUpkeep: setUpkeepEntries,
          onSpectators: (list) => {
            spectatorsRef.current = list;
            setSpectators(list);
          },
          onCursors: (list) => {
            liveCursorsRef.current = list;
            // Board cursors go straight to Pixi (no React render at 5Hz)…
            pushBoardCursors();
            // …while sidebar cursors re-render only when they actually change.
            const sidebar = list.filter(
              (c) => c.area !== 'board' && c.id !== selfIdRef.current,
            );
            setSidebarCursors((prev) =>
              prev.length === sidebar.length &&
              prev.every((p, i) => {
                const n = sidebar[i]!;
                return (
                  p.id === n.id &&
                  p.area === n.area &&
                  p.target === n.target &&
                  p.x === n.x &&
                  p.y === n.y &&
                  p.tx === n.tx &&
                  p.ty === n.ty
                );
              })
                ? prev
                : sidebar,
            );
          },
          onGameResult: ({ winnerId, winCardId }) =>
            setGameOver({ winnerId: winnerId || null, winCardId }),
        });
      };

      try {
        const joined = reconnectionToken
          ? await reconnectGame(reconnectionToken)
          : await joinGameById(roomId, { spectate });
        if (disposed) {
          void joined.leave();
          return;
        }
        attach(joined);
      } catch (err) {
        console.error('Failed to join game:', err);
        // A failed reconnect means the seat is gone (grace lapsed / room
        // disposed) — drop this room's token so the hub stops offering it.
        if (reconnectionToken) {
          void removeActiveGameToken(roomId);
          setReconnectFailed(true);
        }
        setActiveRoom(null);
      }
    }

    void boot();

    return () => {
      disposed = true;
      void room?.leave();
      game?.destroy();
      roomRef.current = null;
      gameRef.current = null;
      room = null;
      game = null;
    };
  }, [activeRoom]);

  /** Classify a pointer position into a `CursorSample` — which shared surface
   * it's over (board canvas / market sidebar / roster sidebar / lobby card)
   * and where, in that surface's shared coordinate scheme. Board points are
   * camera-independent board-plane coords (same tile spot for everyone);
   * panels get normalised coords plus the hovered element's
   * `data-cursor-target` key and the position within it. Shared by the cursor
   * stream, touch taps and the emoji picker. */
  const samplePointer = useCallback(
    (clientX: number, clientY: number, el: Element | null): CursorSample => {
      const clamp01 = (v: number) => Math.max(0, Math.min(1, v));
      const norm = (rect: DOMRect) => ({
        x: clamp01(rect.width > 0 ? (clientX - rect.left) / rect.width : 0),
        y: clamp01(rect.height > 0 ? (clientY - rect.top) / rect.height : 0),
      });
      // Keyed element under the pointer + the pointer's position WITHIN it,
      // so receivers can put the cursor on the exact spot of their own copy
      // of that card/row (their element sizes differ from ours).
      const keyedEl = el?.closest('[data-cursor-target]') ?? null;
      const target = keyedEl?.getAttribute('data-cursor-target') ?? '';
      const within = keyedEl
        ? norm(keyedEl.getBoundingClientRect())
        : { x: 0.5, y: 0.5 };

      const sidebar = el?.closest('.sidebar-right, .sidebar-left');
      if (sidebar) {
        const p = norm(sidebar.getBoundingClientRect());
        return {
          area: sidebar.classList.contains('sidebar-right')
            ? 'market'
            : 'roster',
          x: p.x,
          y: p.y,
          target,
          tx: within.x,
          ty: within.y,
        };
      }
      // Pre-game ready-up screen: normalise against the lobby card itself
      // (the panel every client sees), not the full-screen wrapper, so the
      // fallback position maps sensibly across window sizes.
      const lobby = el?.closest('.lobby');
      if (lobby) {
        const card = lobby.querySelector('.lobby-card') ?? lobby;
        const p = norm(card.getBoundingClientRect());
        return {
          area: 'lobby',
          x: p.x,
          y: p.y,
          target,
          tx: within.x,
          ty: within.y,
        };
      }
      if (el?.closest('.stage')) {
        const p = gameRef.current?.boardPointFromClient(clientX, clientY);
        if (p) {
          return { area: 'board', x: p.x, y: p.y, target: '', tx: 0.5, ty: 0.5 };
        }
      }
      // Over neither shared surface (popovers, overlays, banners) — hidden.
      return HIDDEN_CURSOR;
    },
    [],
  );

  // Broadcast this client's own pointer to the room (see `GameState.cursors`).
  // One delegated window listener classifies where the pointer is (see
  // `samplePointer`) and sends trailing-throttled updates. The pointer hides
  // (one `area: ""` message) on window blur / tab hide / leaving the window /
  // 20s idle.
  useEffect(() => {
    if (!activeRoom) return;
    const SEND_MS = 100; // self-throttle; the room patches every 200ms
    const IDLE_HIDE_MS = 20_000;
    const TAP_MIN_MS = 150; // local guard for touch-tap broadcasts

    let sentRoom: Room<GameState> | null = null;
    let lastSent: CursorSample | null = null;
    let pending: { x: number; y: number; el: Element | null } | null = null;
    let throttleTimer: number | undefined;
    let idleTimer: number | undefined;

    const send = (c: CursorSample) => {
      const room = roomRef.current;
      if (!room) return;
      if (room !== sentRoom) {
        // Fresh connection (initial join or in-place reconnect): resend even
        // an unchanged sample — the new session starts with no cursor entry.
        sentRoom = room;
        lastSent = null;
      }
      if (
        lastSent &&
        lastSent.area === c.area &&
        lastSent.target === c.target &&
        Math.abs(lastSent.x - c.x) < 1e-3 &&
        Math.abs(lastSent.y - c.y) < 1e-3
      ) {
        return;
      }
      // Nothing shown yet and nothing to show — skip the no-op hide.
      if (lastSent === null && c.area === '') return;
      try {
        room.send(ClientMessage.Cursor, c);
        lastSent = c;
      } catch {
        // Socket mid-close during a reconnect — the next move retries.
      }
    };

    const flushPending = () => {
      throttleTimer = undefined;
      if (!pending) return;
      const { x, y, el } = pending;
      pending = null;
      send(samplePointer(x, y, el));
    };

    const hide = () => {
      pending = null;
      if (throttleTimer !== undefined) {
        window.clearTimeout(throttleTimer);
        throttleTimer = undefined;
      }
      if (idleTimer !== undefined) {
        window.clearTimeout(idleTimer);
        idleTimer = undefined;
      }
      send(HIDDEN_CURSOR);
    };

    const onPointerMove = (e: PointerEvent) => {
      // A touch "move" is a pan/pinch gesture, not pointing — touch presence
      // is broadcast as one-shot taps (below) instead of a streamed cursor.
      if (e.pointerType === 'touch') return;
      pending = {
        x: e.clientX,
        y: e.clientY,
        el: e.target instanceof Element ? e.target : null,
      };
      if (throttleTimer === undefined) {
        throttleTimer = window.setTimeout(flushPending, SEND_MS);
      }
      if (idleTimer !== undefined) window.clearTimeout(idleTimer);
      idleTimer = window.setTimeout(hide, IDLE_HIDE_MS);
    };
    // Touch tap → a one-shot ripple on everyone else's screen (mobile has no
    // hover cursor to stream). Primary contact only, so a two-finger
    // pinch/rotate doesn't fire twice.
    let lastTapAt = 0;
    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType !== 'touch' || !e.isPrimary) return;
      const now = performance.now();
      if (now - lastTapAt < TAP_MIN_MS) return;
      const s = samplePointer(
        e.clientX,
        e.clientY,
        e.target instanceof Element ? e.target : null,
      );
      if (s.area === '') return;
      lastTapAt = now;
      try {
        roomRef.current?.send(ClientMessage.CursorTap, s);
      } catch {
        /* socket mid-close — drop the tap */
      }
    };
    // Pointer left the window entirely (no relatedTarget to land on).
    const onPointerOut = (e: PointerEvent) => {
      if (e.relatedTarget === null) hide();
    };
    const onVisibility = () => {
      if (document.hidden) hide();
    };

    window.addEventListener('pointermove', onPointerMove, { passive: true });
    window.addEventListener('pointerdown', onPointerDown, { passive: true });
    window.addEventListener('pointerout', onPointerOut);
    window.addEventListener('blur', hide);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointerout', onPointerOut);
      window.removeEventListener('blur', hide);
      document.removeEventListener('visibilitychange', onVisibility);
      hide(); // best-effort: clear our cursor before leaving the room
    };
  }, [activeRoom, samplePointer]);

  // Emoji reaction trigger: right-click (desktop) or long-press (touch) on
  // any shared surface opens the picker at that point. The browser context
  // menu is suppressed only when the picker actually opens, so right-clicking
  // the page chrome still behaves normally. (On Android a long-press also
  // fires `contextmenu` — both paths open the same picker, harmlessly.)
  useEffect(() => {
    if (!activeRoom) return;
    const LONG_PRESS_MS = 500;
    const MOVE_TOL_PX = 10;
    let press: { id: number; x: number; y: number; timer: number } | null =
      null;

    const openAt = (
      clientX: number,
      clientY: number,
      el: Element | null,
    ): boolean => {
      const sample = samplePointer(clientX, clientY, el);
      if (sample.area === '') return false;
      setEmojiPicker({ screen: { x: clientX, y: clientY }, sample });
      return true;
    };

    const onContextMenu = (e: MouseEvent) => {
      const el = e.target instanceof Element ? e.target : null;
      if (openAt(e.clientX, e.clientY, el)) e.preventDefault();
    };
    const cancelPress = () => {
      if (press) {
        window.clearTimeout(press.timer);
        press = null;
      }
    };
    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType !== 'touch' || !e.isPrimary) return;
      cancelPress();
      const el = e.target instanceof Element ? e.target : null;
      const { clientX, clientY, pointerId } = e;
      press = {
        id: pointerId,
        x: clientX,
        y: clientY,
        timer: window.setTimeout(() => {
          press = null;
          openAt(clientX, clientY, el);
        }, LONG_PRESS_MS),
      };
    };
    // A drag/pan is not a long-press — cancel once the finger wanders.
    const onPointerMove = (e: PointerEvent) => {
      if (
        press &&
        e.pointerId === press.id &&
        Math.hypot(e.clientX - press.x, e.clientY - press.y) > MOVE_TOL_PX
      ) {
        cancelPress();
      }
    };
    const onPointerEnd = (e: PointerEvent) => {
      if (press && e.pointerId === press.id) cancelPress();
    };

    window.addEventListener('contextmenu', onContextMenu);
    window.addEventListener('pointerdown', onPointerDown, { passive: true });
    window.addEventListener('pointermove', onPointerMove, { passive: true });
    window.addEventListener('pointerup', onPointerEnd, { passive: true });
    window.addEventListener('pointercancel', onPointerEnd, { passive: true });
    return () => {
      cancelPress();
      window.removeEventListener('contextmenu', onContextMenu);
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerEnd);
      window.removeEventListener('pointercancel', onPointerEnd);
      setEmojiPicker(null);
    };
  }, [activeRoom, samplePointer]);

  const self = players.find((p) => p.id === selfId);
  /** This client's unresolved Barracks-upkeep choices, parsed from the synced
   * `"playerId|nodeId|foodCost"` entries. */
  const upkeepSelf = upkeepEntries
    .map((entry) => {
      const [playerId, nodeId, cost] = entry.split('|');
      return {
        playerId: playerId ?? '',
        nodeId: Number(nodeId),
        cost: Number(cost) || 0,
      };
    })
    .filter((e) => e.playerId === selfId);
  /** Watch-only session: this client joined via a Watch entry point. `self` is
   * undefined for spectators, so act-gated UI already stays hidden; this flag
   * drives the few explicit "you're spectating" touches. */
  const isSpectator = !!activeRoom?.spectate;
  /** Match-pinned fortress catalogue (falls back to code defaults pre-config). */
  const { byId: cardsById, byName: cardsByName } = useMemo(
    () => cardsFromConfig(gameConfig),
    [gameConfig],
  );
  const toggleReady = () => {
    roomRef.current?.send(ClientMessage.Ready, { ready: !self?.ready });
  };
  /** Dev-only force-start (server enforces `devMode`); no payload. */
  const sendAutoInit = () => {
    roomRef.current?.send(ClientMessage.AutoInit);
  };

  // Read the server's dev-mode flag once (memoised fetch) to decide whether to
  // surface the lobby Auto-Initialize button. The server still gates the command.
  useEffect(() => {
    getAppState()
      .then((s) => setDevMode(s.devMode))
      .catch(() => {});
  }, []);

  // During setup, highlight the legal home-base nodes only while it's my turn to
  // place and I haven't placed yet; drop any pending pick once that's no longer so.
  const myHomePlaced = !!self?.homePlaced;
  useEffect(() => {
    const placingHome =
      phase === 'setup' && currentPlayerId === selfId && !myHomePlaced;
    gameRef.current?.setHomePlacementActive(placingHome);
    if (!placingHome) setSelectedHomeNode(null);
  }, [phase, currentPlayerId, selfId, myHomePlaced]);

  // Reflect the tentatively-picked home node on the board.
  useEffect(() => {
    gameRef.current?.setHomeSelection(selectedHomeNode);
  }, [selectedHomeNode]);

  const confirmHomeBase = useCallback((nodeId: number) => {
    roomRef.current?.send(ClientMessage.PlaceHome, { nodeId });
    setSelectedHomeNode(null);
  }, []);

  const closeSelection = useCallback(() => setSelection(null), []);

  // --- Queued-move selection markers ----------------------------------------
  // Resolve the panel selection to its live queue entry's payload. Null when
  // nothing is selected or the entry is gone (cancelled / slots shifted when a
  // turn played) — the markers below key off this, so they clear too. The
  // payload STRING is the memo boundary: `self`'s identity churns on every
  // roster sync, and keying the decoded action off the stable string keeps the
  // ghost effect (with its focus pan) from re-firing on unrelated updates.
  const selectedQueuedPayload = useMemo(() => {
    if (!queuedSel || !self) return null;
    const move = self.queuedMoves.filter((m) => m.slot === queuedSel.slot)[
      queuedSel.index
    ];
    return move?.payload ?? null;
  }, [queuedSel, self]);
  const selectedQueued = useMemo(() => {
    if (selectedQueuedPayload === null) return null;
    try {
      return { action: JSON.parse(selectedQueuedPayload) as GameAction };
    } catch {
      return null;
    }
  }, [selectedQueuedPayload]);

  // Drop a selection whose entry no longer exists, so a stale slot/index can't
  // re-attach to a different move after the queue shifts.
  useEffect(() => {
    if (queuedSel && !selectedQueued) setQueuedSel(null);
  }, [queuedSel, selectedQueued]);

  // Board ghost for a selected queued construct (building or bridge): shown
  // only while that queue entry is selected, panned into view on selection.
  useEffect(() => {
    const game = gameRef.current;
    if (!game) return;
    const a = selectedQueued?.action;
    if (
      a &&
      (a.type === 'construct_building' || a.type === 'pay_penalty_with_token')
    ) {
      game.setQueuedGhost({
        kind: 'building',
        nodeId: a.nodeId,
        building: a.building,
        color:
          PLAYER_COLORS[(self?.seat ?? 0) % PLAYER_COLORS.length] ?? 0xffffff,
      });
      game.focusNode(a.nodeId);
    } else if (a && a.type === 'construct_bridge') {
      game.setQueuedGhost({ kind: 'bridge', from: a.from, to: a.to });
      game.focusNode(a.from);
    } else {
      game.setQueuedGhost(null);
    }
    return () => gameRef.current?.setQueuedGhost(null);
  }, [selectedQueued, self?.seat]);

  // Market badge for a selected queued Implement / Cycle: which row, and the
  // card (by id) or row slots (by index) to mark.
  const queuedMarketMark = useMemo(() => {
    const a = selectedQueued?.action;
    if (!a) return null;
    if (a.type === 'implement_fortress') {
      return { row: a.kind, mode: 'implement' as const, cardId: a.cardId };
    }
    if (a.type === 'cycle_market') {
      return {
        row: a.kind,
        mode: 'cycle' as const,
        indices: new Set<number>(a.indices),
      };
    }
    return null;
  }, [selectedQueued]);

  // A market-marked selection is invisible while the market rail is collapsed —
  // reveal the sidebar so selecting the move always shows its marker.
  useEffect(() => {
    if (queuedMarketMark) setMarketCollapsed(false);
  }, [queuedMarketMark]);

  // A hover preview shouldn't vanish the instant the cursor leaves its tile —
  // otherwise you can't reach the popover to read it. The close is deferred a
  // beat: moving onto the popover cancels it (`cancelPreviewClose`), and the
  // popover's own mouseleave reschedules it (`closePreviewSoon`). Pinned
  // (click-opened) details never take this path — their lifecycle is unchanged.
  const previewCloseTimer = useRef<number | null>(null);
  const cancelPreviewClose = useCallback(() => {
    if (previewCloseTimer.current !== null) {
      window.clearTimeout(previewCloseTimer.current);
      previewCloseTimer.current = null;
    }
  }, []);
  const closePreviewSoon = useCallback(() => {
    cancelPreviewClose();
    previewCloseTimer.current = window.setTimeout(() => {
      previewCloseTimer.current = null;
      setSelection((cur) => (cur && !cur.pinned ? null : cur));
    }, PREVIEW_CLOSE_DELAY);
  }, [cancelPreviewClose]);

  // --- Cycle Market flow ---------------------------------------------------
  // Per-card Food cost for a row, read from the match's pinned config.
  const cycleUnitFood = useCallback(
    (kind: FortressKind): number => {
      const fc = gameConfig?.fortressCards;
      if (!fc) return 0;
      const cost =
        kind === 'win' ? fc.cycleCostPerWinCard : fc.cycleCostPerRegularCard;
      // Costs are [minerals, food, vigor, obsidian]; cycling is paid in Food.
      return cost[1] ?? 0;
    },
    [gameConfig],
  );
  const cycleFoodCost = cycleSel
    ? cycleSel.indices.size * cycleUnitFood(cycleSel.kind)
    : 0;
  // Sub-aware: the Bench pays Food costs with Vigor, the Treadmill with
  // Obsidian — mirrors the server's canAffordFor. While planning, cost never
  // gates (a queued yield may fund it; auto-play re-validates).
  const cycleAffordable =
    planSlot !== null
      ? true
      : self
        ? canAffordWith(
            toAmount(self.resources),
            [0, cycleFoodCost, 0, 0],
            substitutionsFor(self.fortressCards),
          )
        : false;

  // The "Cycle Market" action toggles the flow: a second click leaves cycle
  // mode. Opening it reveals the market sidebar (if collapsed) and arms the
  // selection; the detail popover stays available for inspecting cards.
  const startCycleMarket = useCallback(() => {
    if (cycleMode) {
      setCycleMode(false);
      setCycleSel(null);
      return;
    }
    setMarketCollapsed(false);
    setCycleMode(true);
    setCycleSel(null);
    setBuildFlow(null);
    setBridgeFlow(null);
    setAimFlow(null);
    setImplementFlow(null);
    setEffectsFlow(null);
  }, [cycleMode]);

  // Toggle a card in/out of the selection. The first pick fixes the row; clicks
  // in the other row are ignored (no mixing). Emptying the selection unfixes it.
  const toggleCycleCard = useCallback((kind: FortressKind, index: number) => {
    setCycleSel((prev) => {
      if (!prev) return { kind, indices: new Set([index]) };
      if (prev.kind !== kind) return prev; // other row — locked out
      const indices = new Set(prev.indices);
      if (indices.has(index)) indices.delete(index);
      else indices.add(index);
      return indices.size === 0 ? null : { kind, indices };
    });
  }, []);

  const cancelCycle = useCallback(() => {
    setCycleMode(false);
    setCycleSel(null);
  }, []);

  const confirmCycle = useCallback(() => {
    if (!cycleSel || cycleSel.indices.size === 0) return;
    sendGameAction({
      type: 'cycle_market',
      kind: cycleSel.kind,
      indices: [...cycleSel.indices],
    });
    setCycleMode(false);
    setCycleSel(null);
  }, [cycleSel, sendGameAction]);

  // Leave the cycle flow if the turn moves on (auto-end, pass, disconnect) so it
  // can't linger into another player's turn — unless the player is planning
  // queued moves, which happens precisely while it is NOT their turn.
  useEffect(() => {
    if (currentPlayerId !== selfId && planSlot === null) {
      setCycleMode(false);
      setCycleSel(null);
    }
  }, [currentPlayerId, selfId, planSlot]);

  // --- Round-start cycle (the first player's free step) ---------------------

  /** The open round-start window waits on ME — the round is paused until I
   * cycle up to one card per market (free) or cycle nothing. */
  const rsWindowMine = roundCyclerId !== '' && roundCyclerId === selfId;

  // The window opening on us force-enters live picking (and reveals the
  // market); it closing force-leaves. The paid Cycle-Market flow can't be
  // live at the same time — no turn is running while the window is open.
  useEffect(() => {
    if (rsWindowMine) {
      setMarketCollapsed(false);
      setRsMode('live');
      setRsSel({ regular: null, win: null });
      setCycleMode(false);
      setCycleSel(null);
    } else {
      setRsMode((m) => (m === 'live' ? null : m));
    }
  }, [rsWindowMine]);

  // Queue-planning the choice is only offered while we hold the First-Turn
  // marker; losing it (someone else passed first) closes the picker.
  useEffect(() => {
    if (firstPlayerId !== selfId) {
      setRsMode((m) => (m === 'queue' ? null : m));
    }
  }, [firstPlayerId, selfId]);

  /** Toggle a card as its row's single round-start pick (max 1 per market). */
  const toggleRsCard = useCallback(
    (kind: FortressKind, cardId: string | null) => {
      if (!cardId) return;
      setRsSel((prev) =>
        kind === 'win'
          ? { ...prev, win: prev.win === cardId ? null : cardId }
          : { ...prev, regular: prev.regular === cardId ? null : cardId },
      );
    },
    [],
  );

  /** Send the round-start choice — live answer or queued plan, per `rsMode`.
   * `{ regular: null, win: null }` is the explicit "cycle nothing". */
  const confirmRoundStartCycle = useCallback(
    (choice: { regular: string | null; win: string | null }) => {
      const room = roomRef.current;
      if (!room || !rsMode) return;
      room.send(
        rsMode === 'queue'
          ? ClientMessage.QueueRoundStartCycle
          : ClientMessage.RoundStartCycle,
        choice,
      );
      setRsMode(null);
      setRsSel({ regular: null, win: null });
    },
    [rsMode],
  );

  /** Enter/leave queue-planning for the round-start cycle. */
  const toggleRsQueueMode = useCallback(() => {
    setRsSel({ regular: null, win: null });
    if (rsMode === 'queue') {
      setRsMode(null);
      return;
    }
    setMarketCollapsed(false);
    setRsMode('queue');
  }, [rsMode]);

  const clearQueuedRoundStartCycle = useCallback(() => {
    roomRef.current?.send(ClientMessage.QueueRoundStartCycle, { clear: true });
  }, []);

  // --- Construct Building flow ---------------------------------------------
  // Targets for `building`, split three ways (the caller renders the first two):
  //  - `allowed`  : empty + in build range + affordable  → green, clickable
  //  - `tooCostly`: empty + in range but unaffordable     → grey indicator
  //  - everything else (out of range) is left untouched.
  // Range mirrors the server's `inRange` (won't trace through an opponent's
  // blocking Barracks / Fortress marker or across a severed path) and cost
  // mirrors `buildCost`, so a green node always passes server validation.
  const computeBuildTargets = useCallback(
    (
      building: ConstructableBuilding,
      skipVigorPenalty = false,
    ): {
      allowed: Set<number>;
      tooCostly: Set<number>;
      vigorPenalty: Map<number, number>;
      oneTimeGain: Map<number, { resource: string; amount: number }>;
    } => {
      const allowed = new Set<number>();
      const tooCostly = new Set<number>();
      const vigorPenalty = new Map<number, number>();
      const oneTimeGain = new Map<
        number,
        { resource: string; amount: number }
      >();
      if (!gameConfig || !selfId || !self) {
        return { allowed, tooCostly, vigorPenalty, oneTimeGain };
      }
      const cfg = resolveBuilding(gameConfig, self, building);
      // The Vigor penalty a build here pays = the node's zone penalty (when the
      // building is subject to one). Shown beside each highlighted node.
      const zonePenalty = (id: number): number => {
        if (!cfg.vigorPenalty) return 0;
        const zone = NODE_ZONE[id] ?? 'forest';
        return resolveVigorZone(gameConfig, self, vigorZoneLevel(zone))
          .vigorPenalty;
      };
      const bank = toAmount(self.resources);
      const subs = substitutionsFor(self.fortressCards);
      const bypass =
        building === 'scout' &&
        self.fortressCards.some((card) => card.cardId === 'rabid-shoes');
      const sources: number[] = [];
      const blocked = new Set<number>();
      for (const [id, info] of nodeInfoRef.current) {
        if (info.ownerId === selfId && info.building !== '') sources.push(id);
        const isOpponent = info.ownerId !== '' && info.ownerId !== selfId;
        if (
          !bypass &&
          isOpponent &&
          info.building !== '' &&
          blocksOpponentBuild(
            gameConfig,
            playersRef.current,
            info.ownerId,
            info.building,
          )
        ) {
          blocked.add(id);
        }
        if (
          !bypass &&
          info.fortressOwnerId !== '' &&
          info.fortressOwnerId !== selfId
        ) {
          blocked.add(id);
        }
      }
      if (sources.length === 0) {
        return { allowed, tooCostly, vigorPenalty, oneTimeGain };
      }
      const reach = nodesWithinAny(
        BUILD_GRAPH,
        sources,
        cfg.range.build,
        blocked,
        severedRef.current,
      );
      for (const id of reach.keys()) {
        const info = nodeInfoRef.current.get(id);
        // Only empty nodes are buildable (the source buildings are occupied).
        if ((info?.building ?? '') !== '') continue;
        const cost = buildCostFor(
          gameConfig,
          self,
          building,
          NODE_ZONE[id] ?? 'forest',
          info?.resource ?? '',
          info?.permanence ?? '',
          skipVigorPenalty,
        );
        const pen = zonePenalty(id);
        if (skipVigorPenalty && pen <= 0) continue;
        const affordable = canAffordWith(bank, cost, subs);
        (affordable ? allowed : tooCostly).add(id);
        if (pen > 0) vigorPenalty.set(id, pen);
        // A one-time tile resource here would be collected by the build —
        // 1 + the zone's one-time bonus (mirrors the server's collectOneTime).
        if (info?.permanence === 'oneTime' && info.resource !== '') {
          const idx = (RESOURCES as readonly string[]).indexOf(info.resource);
          if (idx >= 0) {
            const zone = resolveVigorZone(
              gameConfig,
              self,
              vigorZoneLevel(NODE_ZONE[id] ?? 'forest'),
            );
            oneTimeGain.set(id, {
              resource: info.resource,
              amount: 1 + (zone.oneTimeResourceBonus[idx] ?? 0),
            });
          }
        }
      }
      return { allowed, tooCostly, vigorPenalty, oneTimeGain };
    },
    [gameConfig, selfId, self],
  );

  // The Construct-Building action button toggles the picker: open it from
  // scratch, or close whatever build flow is currently open (pick or place).
  const openBuildPicker = useCallback(() => {
    setCycleMode(false);
    setCycleSel(null);
    setBridgeFlow(null);
    setAimFlow(null);
    setImplementFlow(null);
    setEffectsFlow(null);
    closeSelection();
    setBuildFlow((prev) => (prev ? null : { stage: 'pick' }));
  }, [closeSelection]);

  const selectBuilding = useCallback(
    (building: ConstructableBuilding) => {
      const penaltyCardId = buildFlowRef.current?.penaltyCardId;
      // Planning queued moves: no range / cost / occupancy gating — a prior
      // queued move may supply the resources, range, or clear the node, so
      // EVERY node is offered. The server re-validates when the move actually
      // plays; an invalid one halts auto-play for manual intervention.
      if (planSlotRef.current !== null) {
        setBuildFlow({
          stage: 'place',
          building,
          allowed: new Set(nodeInfoRef.current.keys()),
          tooCostly: new Set<number>(),
          vigorPenalty: new Map<number, number>(),
          oneTimeGain: new Map<number, { resource: string; amount: number }>(),
          ...(penaltyCardId ? { penaltyCardId } : {}),
        });
        return;
      }
      const { allowed, tooCostly, vigorPenalty, oneTimeGain } =
        computeBuildTargets(building, !!penaltyCardId);
      setBuildFlow({
        stage: 'place',
        building,
        allowed,
        tooCostly,
        vigorPenalty,
        oneTimeGain,
        ...(penaltyCardId ? { penaltyCardId } : {}),
      });
    },
    [computeBuildTargets],
  );

  const cancelBuild = useCallback(() => setBuildFlow(null), []);

  /** Confirm the tentatively-selected node: send the construct (or token-build)
   * action for it. The board click only selects; this commits. */
  const confirmBuild = useCallback(() => {
    const bf = buildFlowRef.current;
    if (bf?.stage !== 'place' || bf.selected === undefined) return;
    sendGameAction(
      bf.penaltyCardId
        ? {
            type: 'pay_penalty_with_token',
            cardId: bf.penaltyCardId,
            nodeId: bf.selected,
            building: bf.building,
            ...(bf.doubleOneTime
              ? { riders: { doubleOneTime: bf.doubleOneTime } }
              : {}),
          }
        : {
            type: 'construct_building',
            nodeId: bf.selected,
            building: bf.building,
            ...(bf.doubleOneTime
              ? { riders: { doubleOneTime: bf.doubleOneTime } }
              : {}),
          },
    );
    setBuildFlow(null);
  }, [sendGameAction]);

  /** The usable Energized Bun-Bun for the build rider, if held. */
  const bunBun = useMemo(() => {
    if (!self) return null;
    for (const fc of self.fortressCards) {
      const fx = effectFor(fc.cardId);
      if (fx?.rider !== 'double_one_time' || fc.disabled) continue;
      const cap = fx.tokenCap ?? Number.POSITIVE_INFINITY;
      if (fc.tokens >= cap) continue;
      return { cardId: fc.cardId, tokens: fc.tokens, cap };
    }
    return null;
  }, [self]);

  /** Arm/disarm the Bun-Bun rider on the current placement. */
  const toggleDoubleOneTime = useCallback(() => {
    setBuildFlow((cur) => {
      if (cur?.stage !== 'place') return cur;
      return {
        ...cur,
        doubleOneTime: cur.doubleOneTime ? undefined : (bunBun?.cardId ?? undefined),
      };
    });
  }, [bunBun]);

  // --- Construct Bridge flow -----------------------------------------------
  // Eligible bridge paths, excluding paths already bridged: a CONNECTED path
  // needs BOTH endpoints within the bridge's build range (so a bridge never
  // reaches further than the range), while a SEVERED path (chasm) needs only ONE
  // — you anchor on the reachable bank and span the gap. Mirrors the server.
  // (Tiny gap: the server seeds its BFS at the candidate endpoint, so it also
  // allows a path whose only in-range end is a blocked opponent node; we omit
  // that rare case — harmless, the server re-validates.) Bridge cost is uniform,
  // so the split is all-or-nothing: affordable → `allowed`, else `tooCostly`.
  const computeBridgeTargets = useCallback((): {
    allowed: [number, number][];
    tooCostly: [number, number][];
    allowedKeys: Set<string>;
    wildcard: boolean;
    pending: { from: number; to: number } | null;
  } => {
    const allowed: [number, number][] = [];
    const tooCostly: [number, number][] = [];
    const allowedKeys = new Set<string>();
    if (!gameConfig || !selfId || !self) {
      return { allowed, tooCostly, allowedKeys, wildcard: false, pending: null };
    }
    const cfg = resolveBuilding(gameConfig, self, 'bridge');
    // Wildcard cost ("n of any resource") → the tap asks which resource pays.
    const wildcard = (cfg.cost as readonly number[]).length === 1;
    const bypass = self.fortressCards.some((card) => card.cardId === 'rabid-shoes');
    // Sub-aware, and — unlike a componentwise compare — correct for the
    // wildcard bridge cost Bridgette grants ("1 of any resource").
    const affordable = canAffordWith(
      toAmount(self.resources),
      cfg.cost,
      substitutionsFor(self.fortressCards),
    );
    const sources: number[] = [];
    const blocked = new Set<number>();
    for (const [id, info] of nodeInfoRef.current) {
      if (info.ownerId === selfId && info.building !== '') sources.push(id);
      const isOpponent = info.ownerId !== '' && info.ownerId !== selfId;
      if (
        !bypass &&
        isOpponent &&
        info.building !== '' &&
        blocksOpponentBuild(
          gameConfig,
          playersRef.current,
          info.ownerId,
          info.building,
        )
      ) {
        blocked.add(id);
      }
      if (
        !bypass &&
        info.fortressOwnerId !== '' &&
        info.fortressOwnerId !== selfId
      ) {
        blocked.add(id);
      }
    }
    if (sources.length === 0) {
      return { allowed, tooCostly, allowedKeys, wildcard, pending: null };
    }
    const reach = nodesWithinAny(
      BUILD_GRAPH,
      sources,
      cfg.range.build,
      blocked,
      severedRef.current,
    );
    for (const [a, b] of BUILD_GRAPH.edges) {
      const key = edgeKey(a, b);
      if (bridgesRef.current.has(key)) continue; // already bridged
      const inA = reach.has(a);
      const inB = reach.has(b);
      // Connected path → both ends in range; severed path → one end is enough.
      const inRange = severedRef.current.has(key) ? inA || inB : inA && inB;
      if (!inRange) continue;
      if (affordable) {
        allowed.push([a, b]);
        allowedKeys.add(key);
      } else {
        tooCostly.push([a, b]);
      }
    }
    return { allowed, tooCostly, allowedKeys, wildcard, pending: null };
  }, [gameConfig, selfId, self]);

  // The Construct-Bridge button toggles the edge-selection flow.
  const startBridge = useCallback(() => {
    setCycleMode(false);
    setCycleSel(null);
    setBuildFlow(null);
    setAimFlow(null);
    setImplementFlow(null);
    setEffectsFlow(null);
    closeSelection();
    // Planning: every unbridged path is offered — range and cost are the
    // auto-play validator's problem (a prior queued move may change both).
    if (planSlotRef.current !== null) {
      setBridgeFlow((prev) => {
        if (prev) return null;
        const allowed: [number, number][] = [];
        const allowedKeys = new Set<string>();
        for (const [a, b] of BUILD_GRAPH.edges) {
          const key = edgeKey(a, b);
          if (bridgesRef.current.has(key)) continue; // already bridged
          allowed.push([a, b]);
          allowedKeys.add(key);
        }
        // The resource pick still rides on a CURRENTLY wildcard cost, so the
        // queued move carries the choice if Bridgette is still in play then.
        const wildcard =
          !!gameConfig &&
          !!self &&
          (resolveBuilding(gameConfig, self, 'bridge').cost as readonly number[])
            .length === 1;
        return { allowed, tooCostly: [], allowedKeys, wildcard, pending: null };
      });
      return;
    }
    setBridgeFlow((prev) => (prev ? null : computeBridgeTargets()));
  }, [closeSelection, computeBridgeTargets, gameConfig, self]);

  const cancelBridge = useCallback(() => setBridgeFlow(null), []);

  // --- Aim Barracks flow -----------------------------------------------------
  /** Node ids currently under attack: targets of standing attack aims (absorbed
   * attackers excluded, mirroring the server's tallies). */
  const attackedTargets = useCallback((): Set<number> => {
    const attacked = new Set<number>();
    for (const info of nodeInfoRef.current.values()) {
      if (info.building !== 'barracks' || info.aimMode !== 'attack') continue;
      if (info.aimTargetId < 0 || info.fortressOwnerId !== '') continue;
      attacked.add(info.aimTargetId);
    }
    return attacked;
  }, []);

  /** Your unaimed Barracks — the legal sources to start an aim from. */
  const computeAimSources = useCallback((): Set<number> => {
    const sources = new Set<number>();
    if (!selfId) return sources;
    for (const [id, info] of nodeInfoRef.current) {
      if (info.ownerId !== selfId || info.building !== 'barracks') continue;
      if (info.aimTargetId >= 0) continue; // committed until the round ends
      sources.add(id);
    }
    return sources;
  }, [selfId]);

  // Legal targets for one Barracks + posture — mirrors the server's `validateAim`
  // (attack: an opponent's non-fortress, non-indestructible building in attack
  // range; defend: any building in defend range), tracing range with the same
  // enter-but-not-through blocker semantics (`nodesWithinInto`). The defense-
  // window variant additionally restricts to YOUR buildings under attack.
  /** Is `nodeId` covered by a DESTRUCTIBLE fortress (attackable despite the
   * fortress stamp)? Client mirror of the server's destructibleFortressAt. */
  const destructibleFortressNode = useCallback((nodeId: number): boolean => {
    const ownerId = nodeInfoRef.current.get(nodeId)?.fortressOwnerId ?? '';
    if (ownerId === '') return false;
    const owner = playersRef.current.find((p) => p.id === ownerId);
    return !!owner?.fortressCards.some(
      (fc) =>
        fc.nodeIds.includes(nodeId) && effectFor(fc.cardId)?.destructible,
    );
  }, []);

  const computeAimTargets = useCallback(
    (barracksId: number, mode: AimMode, defenseOnly: boolean): Set<number> => {
      const out = new Set<number>();
      // Planning (never defense reactions — those are live): any node may be
      // the target; a prior queued move may create the building or the range.
      if (planSlotRef.current !== null && !defenseOnly) {
        for (const id of nodeInfoRef.current.keys()) {
          if (id !== barracksId) out.add(id);
        }
        return out;
      }
      if (!gameConfig || !selfId || !self) return out;
      const cfg = resolveBuilding(gameConfig, self, 'barracks');
      const bypass =
        mode === 'attack' &&
        self.fortressCards.some((card) => card.cardId === 'telescopic-arm');
      const source = nodeInfoRef.current.get(barracksId);
      if (mode === 'attack' && (source?.fortressOwnerId ?? '') !== '') {
        return out; // absorbed into a Fortress — may only defend
      }
      const range = (mode === 'attack' ? cfg.range.attack : cfg.range.defend) ?? 0;
      const blocked = new Set<number>();
      for (const [id, info] of nodeInfoRef.current) {
        const isOpponent = info.ownerId !== '' && info.ownerId !== selfId;
        if (
          !bypass &&
          isOpponent &&
          info.building !== '' &&
          blocksOpponentBuild(
            gameConfig,
            playersRef.current,
            info.ownerId,
            info.building,
          )
        ) {
          blocked.add(id);
        }
        if (
          !bypass &&
          info.fortressOwnerId !== '' &&
          info.fortressOwnerId !== selfId
        ) {
          blocked.add(id);
        }
      }
      const reach = nodesWithinInto(
        BUILD_GRAPH,
        barracksId,
        range,
        blocked,
        severedRef.current,
      );
      const attacked = defenseOnly ? attackedTargets() : null;
      for (const id of reach.keys()) {
        if (id === barracksId) continue;
        const info = nodeInfoRef.current.get(id);
        if (!info || info.building === '') continue;
        if (mode === 'attack') {
          if (info.ownerId === selfId) continue;
          if (info.indestructible) continue;
          // Fortress nodes are unattackable unless the covering card is
          // destructible (Blimp-port, Traveling Booba) — mirrors the server.
          if (info.fortressOwnerId !== '' && !destructibleFortressNode(id))
            continue;
        } else if (attacked && (info.ownerId !== selfId || !attacked.has(id))) {
          continue; // reactions defend only your own attacked buildings
        }
        out.add(id);
      }
      return out;
    },
    [gameConfig, selfId, self, attackedTargets, destructibleFortressNode],
  );
  // Mirrored for the once-created board click handler (it can't see live state).
  useEffect(() => {
    computeAimTargetsRef.current = computeAimTargets;
  }, [computeAimTargets]);

  // The Aim-Barracks action button toggles the flow (normal, on-turn variant).
  const openAimFlow = useCallback(() => {
    setCycleMode(false);
    setCycleSel(null);
    setBuildFlow(null);
    setBridgeFlow(null);
    setImplementFlow(null);
    setEffectsFlow(null);
    closeSelection();
    setAimFlow((prev) =>
      prev
        ? null
        : {
            stage: 'pickBarracks',
            // Planning: any node may be the aiming Barracks — a queued build
            // may have just placed it. Live: your unaimed Barracks only.
            sources:
              planSlotRef.current !== null
                ? new Set(nodeInfoRef.current.keys())
                : computeAimSources(),
            defense: false,
          },
    );
  }, [closeSelection, computeAimSources]);

  /** The defense banner's Aim Defender button — the defend-locked variant. */
  const openDefenseAim = useCallback(() => {
    setAimFlow((prev) =>
      prev
        ? null
        : { stage: 'pickBarracks', sources: computeAimSources(), defense: true },
    );
  }, [computeAimSources]);

  /** Posture picked for the selected Barracks — advance to target selection. */
  const selectAimMode = useCallback(
    (mode: AimMode) => {
      setAimFlow((prev) => {
        if (prev?.stage !== 'mode') return prev;
        return {
          stage: 'target',
          barracksId: prev.barracksId,
          mode,
          defense: false,
          allowed: computeAimTargets(prev.barracksId, mode, false),
        };
      });
    },
    [computeAimTargets],
  );

  const cancelAim = useCallback(() => setAimFlow(null), []);

  const sendDefenseDone = useCallback(() => {
    setAimFlow(null);
    setDefenseProtect(null);
    roomRef.current?.send(ClientMessage.DefenseDone);
  }, []);

  // --- Reactive Scout protection (Godspeed, during a defense window) --------
  /** Your Scouts that would fall as the aims stand — mirrors the server's
   * would-be-destroyed tally (attackers minus aimed defenders; Scouts don't
   * defend themselves), so the Protect button appears exactly when the server
   * would accept the reaction. */
  const computeAtRiskOwnScouts = useCallback((): Set<number> => {
    const out = new Set<number>();
    if (!selfId) return out;
    const attackers = new Map<number, number>();
    const defenders = new Map<number, number>();
    for (const info of nodeInfoRef.current.values()) {
      if (info.building !== 'barracks' || info.aimTargetId < 0) continue;
      if (info.aimMode === 'attack') {
        if (info.fortressOwnerId !== '') continue; // absorbed — can't attack
        attackers.set(
          info.aimTargetId,
          (attackers.get(info.aimTargetId) ?? 0) + 1,
        );
      } else if (info.aimMode === 'defend') {
        defenders.set(
          info.aimTargetId,
          (defenders.get(info.aimTargetId) ?? 0) + 1,
        );
      }
    }
    for (const [targetId, atk] of attackers) {
      const t = nodeInfoRef.current.get(targetId);
      if (!t || t.ownerId !== selfId || t.building !== 'scout') continue;
      if (t.indestructible) continue;
      // Fortress scouts are safe — unless the fortress is destructible
      // (Traveling Booba), in which case Godspeed may still save them.
      if (t.fortressOwnerId !== '' && !destructibleFortressNode(targetId))
        continue;
      if (atk > (defenders.get(targetId) ?? 0)) out.add(targetId);
    }
    return out;
  }, [selfId, destructibleFortressNode]);

  /** The card that lets self reactively protect a Scout right now, if any. */
  const protectCardId = useMemo(() => {
    if (!self) return null;
    for (const fc of self.fortressCards) {
      if (!effectFor(fc.cardId)?.grants?.includes('make_scout_indestructible'))
        continue;
      if (canUseCardAction(self.fortressCards, fc.cardId, 'make_scout_indestructible').allowed) {
        return fc.cardId;
      }
    }
    return null;
  }, [self]);

  /** At-risk own Scouts while a window is open (drives the Protect button). */
  const atRiskScoutCount = useMemo(() => {
    void boardVersion;
    if (!defense.active) return 0;
    return computeAtRiskOwnScouts().size;
  }, [boardVersion, defense.active, computeAtRiskOwnScouts]);

  /** The protect-a-Scout board pick, or null when off. */
  const [defenseProtect, setDefenseProtect] = useState<{
    cardId: string;
    allowed: ReadonlySet<number>;
  } | null>(null);
  const defenseProtectRef = useRef<typeof defenseProtect>(null);
  useEffect(() => {
    defenseProtectRef.current = defenseProtect;
  }, [defenseProtect]);

  const startDefenseProtect = useCallback(() => {
    if (!protectCardId) return;
    setAimFlow(null);
    setDefenseProtect((prev) =>
      prev ? null : { cardId: protectCardId, allowed: computeAtRiskOwnScouts() },
    );
  }, [protectCardId, computeAtRiskOwnScouts]);

  // The protect pick lives and dies with the window.
  useEffect(() => {
    if (!defense.active) setDefenseProtect(null);
  }, [defense.active]);

  // Escape cancels the protect pick.
  useEffect(() => {
    if (!defenseProtect) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDefenseProtect(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [defenseProtect]);

  // Escape cancels the aim flow.
  useEffect(() => {
    if (!aimFlow) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAimFlow(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [aimFlow]);

  // A defense window opening freezes the (ending) turn — drop any on-turn flow
  // still open. A closing window (or our confirm) drops the defense aim flow.
  useEffect(() => {
    if (defense.active) {
      setBuildFlow(null);
      setBridgeFlow(null);
      setImplementFlow(null);
      setCycleMode(false);
      setCycleSel(null);
      setEffectsFlow((flow) => (flow?.stage === 'claim' ? flow : null));
      setAimFlow((cur) => (cur && !cur.defense ? null : cur));
    } else {
      setAimFlow((cur) => (cur?.defense ? null : cur));
    }
  }, [defense.active]);
  const iAmDefending = defense.active && !!selfId && defense.pending.includes(selfId);
  useEffect(() => {
    if (!iAmDefending) setAimFlow((cur) => (cur?.defense ? null : cur));
  }, [iAmDefending]);

  // Mirror every synced aim onto the board as arrows (attack red / defend blue).
  useEffect(() => {
    void boardVersion; // re-runs when node state changes
    const aims: { from: number; to: number; mode: string }[] = [];
    for (const [id, info] of nodeInfoRef.current) {
      if (info.building !== 'barracks' || info.aimTargetId < 0) continue;
      if (info.aimMode !== 'attack' && info.aimMode !== 'defend') continue;
      aims.push({ from: id, to: info.aimTargetId, mode: info.aimMode });
    }
    gameRef.current?.setAims(aims);
  }, [boardVersion]);

  // --- Card Effects flow -----------------------------------------------------
  // The tray of your implemented Fortress cards' effects. Availability comes
  // from the shared registry gate (`canUseCardAction` — ownership, grant,
  // exhaustion, token cap) plus the same turn/action-point rules the server
  // handlers enforce, so an enabled button always passes validation.

  /** Your Scouts not already protected — targets for "Protect a Scout". */
  const computeProtectableScouts = useCallback((): Set<number> => {
    const out = new Set<number>();
    if (!selfId) return out;
    for (const [id, info] of nodeInfoRef.current) {
      if (info.ownerId !== selfId || info.building !== 'scout') continue;
      if (info.indestructible) continue;
      out.add(id);
    }
    return out;
  }, [selfId]);

  /** Your aimed (committed) Barracks — candidates for "Ready Barracks". */
  const computeAimedBarracks = useCallback((): Set<number> => {
    const out = new Set<number>();
    if (!selfId) return out;
    for (const [id, info] of nodeInfoRef.current) {
      if (info.ownerId !== selfId || info.building !== 'barracks') continue;
      if (info.aimTargetId < 0) continue; // upright — nothing to ready
      out.add(id);
    }
    return out;
  }, [selfId]);

  /** Live board counts feeding effect availability (recomputed with the board). */
  const effectContext = useMemo((): EffectContext => {
    void boardVersion; // node state lives in refs — recompute when it changes
    return {
      // While planning queued moves it isn't our turn, so the live count
      // belongs to someone else — gate against a fresh turn's worth instead
      // (the server re-validates when the queued move actually plays).
      actionsRemaining:
        planSlot !== null ? Number.MAX_SAFE_INTEGER : actionsRemaining,
      protectableScouts: computeProtectableScouts().size,
      aimedBarracks: computeAimedBarracks().size,
      pendingClaims: self?.pendingResourceClaims ?? 0,
      // Planning bypasses resource gates (see effectAvailability) — a queued
      // yield may fund the effect by the time it plays.
      planning: planSlot !== null,
    };
  }, [
    boardVersion,
    actionsRemaining,
    planSlot,
    computeProtectableScouts,
    computeAimedBarracks,
    self,
  ]);

  /** How many (card, effect) pairs are usable right now — the bar button badge. */
  const usableEffectCount = useMemo(() => {
    if (!self) return 0;
    let count = 0;
    for (const fc of self.fortressCards) {
      for (const kind of effectFor(fc.cardId)?.grants ?? []) {
        if (effectAvailability(self, fc.cardId, kind, effectContext).allowed) {
          count += 1;
        }
      }
    }
    return count;
  }, [self, effectContext]);

  /** Whether the tray has anything to show at all (any card with an effect). */
  const hasEffectCards = useMemo(
    () =>
      !!self?.fortressCards.some((fc) => {
        const fx = effectFor(fc.cardId);
        return (fx?.grants?.length ?? 0) > 0 || (fx?.passives?.length ?? 0) > 0;
      }),
    [self],
  );

  /** The Card-Effects bar button toggles the tray. */
  const openEffects = useCallback(() => {
    setCycleMode(false);
    setCycleSel(null);
    setBuildFlow(null);
    setBridgeFlow(null);
    setAimFlow(null);
    setImplementFlow(null);
    closeSelection();
    setEffectsFlow((prev) => (prev ? null : { stage: 'list' }));
  }, [closeSelection]);

  const cancelEffects = useCallback(() => setEffectsFlow(null), []);
  /** Sub-flow back button — return to the tray list. */
  const backToEffectsList = useCallback(
    () => setEffectsFlow({ stage: 'list' }),
    [],
  );

  /** A tray Use button — route the effect kind to its sub-flow (or send it
   * outright when it needs no further input). */
  const startEffect = useCallback(
    (cardId: string, kind: SpecialActionKind) => {
      switch (kind) {
        case 'pay_penalty_with_token':
          setEffectsFlow(null);
          setBuildFlow({ stage: 'pick', penaltyCardId: cardId });
          return;
        case 'replicate_fortress': {
          const owned = self?.fortressCards.find((card) => card.cardId === cardId);
          const definition = cardsById.get(cardId);
          if (!owned || !definition || owned.zoneLevel < 0) return;
          const allowed = new Set<number>();
          for (const anchor of BUILD_GRAPH.nodes) {
            const match = matchFortressPattern(
              BUILD_GRAPH,
              () => null,
              definition.pattern,
              {
                anchorNodeId: anchor.id,
                bridgeAt: (a, b) =>
                  areAdjacent(BUILD_GRAPH, a, b) &&
                  vigorZoneLevel(NODE_ZONE[a] ?? 'forest') === owned.zoneLevel &&
                  vigorZoneLevel(NODE_ZONE[b] ?? 'forest') === owned.zoneLevel &&
                  !bridgesRef.current.has(edgeKey(a, b)),
              },
            );
            if (match) allowed.add(anchor.id);
          }
          setEffectsFlow({ stage: 'replicate', cardId, allowed });
          return;
        }
        case 'double_banked_resources':
        case 'force_end_round':
          setEffectsFlow({ stage: 'confirm', cardId, kind });
          return;
        case 'buy_extra_action':
          sendGameAction({ type: kind, cardId });
          return;
        case 'make_scout_indestructible':
          setEffectsFlow({
            stage: 'pickScout',
            cardId,
            allowed: computeProtectableScouts(),
          });
          return;
        case 'untap_barracks': {
          const card = self?.fortressCards.find((fc) => fc.cardId === cardId);
          const cap = tokenCapOf(cardId);
          const limit = Number.isFinite(cap)
            ? Math.max(0, cap - (card?.tokens ?? 0))
            : Number.MAX_SAFE_INTEGER;
          setEffectsFlow({
            stage: 'untap',
            cardId,
            allowed: computeAimedBarracks(),
            selected: new Set<number>(),
            limit,
          });
          return;
        }
        case 'adjust_fortress_token':
          setEffectsFlow({ stage: 'adjustToken', cardId });
          return;
        case 'set_fortress_lock':
        case 'free_cycle_market':
          setMarketCollapsed(false);
          setEffectsFlow({ stage: 'market', cardId, kind });
          return;
        case 'reorder_fortress_deck':
          setEffectsFlow({ stage: 'reorderMarket', cardId });
          return;
        case 'move_fortress': {
          // Empty, un-hardened nodes within your Barracks ATTACK range of the
          // fortress's current nodes — mirrors the server's move validation.
          const fc = self?.fortressCards.find((c) => c.cardId === cardId);
          const card = cardsById.get(cardId);
          if (!fc || !card || !gameConfig || !self) return;
          const range =
            resolveBuilding(gameConfig, self, 'barracks').range.attack ?? 0;
          const bypass = self.fortressCards.some(
            (card) => card.cardId === 'telescopic-arm',
          );
          // Same LOS obstructions the server's aimInRange traces with.
          const blocked = new Set<number>();
          for (const [id, info] of nodeInfoRef.current) {
            const isOpponent = info.ownerId !== '' && info.ownerId !== selfId;
            if (
              !bypass &&
              isOpponent &&
              info.building !== '' &&
              blocksOpponentBuild(
                gameConfig,
                playersRef.current,
                info.ownerId,
                info.building,
              )
            ) {
              blocked.add(id);
            }
            if (
              !bypass &&
              info.fortressOwnerId !== '' &&
              info.fortressOwnerId !== selfId
            ) {
              blocked.add(id);
            }
          }
          const allowed = new Set<number>();
          for (const from of fc.nodeIds) {
            const reach = nodesWithinInto(
              BUILD_GRAPH,
              from,
              range,
              blocked,
              severedRef.current,
            );
            for (const id of reach.keys()) {
              const info = nodeInfoRef.current.get(id);
              if (!info || info.building !== '' || info.fortressOwnerId !== '')
                continue;
              allowed.add(id);
            }
          }
          setEffectsFlow({
            stage: 'moveFortress',
            cardId,
            allowed,
            selected: new Set<number>(),
            limit: card.pattern.buildings.length,
          });
          return;
        }
        case 'claim_resources':
          setEffectsFlow({
            stage: 'claim',
            cardId,
            picked: [],
            remaining: self?.pendingResourceClaims ?? 0,
          });
          return;
        default:
          return; // no client flow (stubs / build-rider kinds) — button is disabled
      }
    },
    [
      self,
      selfId,
      gameConfig,
      cardsById,
      computeProtectableScouts,
      computeAimedBarracks,
      sendGameAction,
    ],
  );

  /** Move-Fortress selection confirmed — relocate to the chosen nodes. */
  const confirmMoveFortress = useCallback(() => {
    const flow = effectsFlowRef.current;
    if (flow?.stage !== 'moveFortress' || flow.selected.size !== flow.limit)
      return;
    sendGameAction({
      type: 'move_fortress',
      cardId: flow.cardId,
      nodeIds: [...flow.selected],
      ...(flow.doubleOneTime
        ? { riders: { doubleOneTime: flow.doubleOneTime } }
        : {}),
    });
    setEffectsFlow(null);
  }, [sendGameAction]);

  const toggleMoveDoubleOneTime = useCallback(() => {
    setEffectsFlow((flow) => {
      if (flow?.stage !== 'moveFortress') return flow;
      return {
        ...flow,
        doubleOneTime: flow.doubleOneTime
          ? undefined
          : (bunBun?.cardId ?? undefined),
      };
    });
  }, [bunBun]);

  /** Curious Clit: start the server-side shuffle for one market row. */
  const startReorderMarket = useCallback(
    (market: FortressKind) => {
      const flow = effectsFlowRef.current;
      if (flow?.stage !== 'reorderMarket') return;
      sendGameAction({
        type: 'reorder_fortress_deck',
        cardId: flow.cardId,
        market,
        order: [],
      });
    },
    [sendGameAction],
  );

  /** The private top ten arrive through the owning player's StateView. */
  useEffect(() => {
    if (!self) return;
    setEffectsFlow((current) => {
      if (self.pendingReorderCards.length === 0) {
        return current?.stage === 'reorderDeck' ? null : current;
      }
      if (current?.stage === 'reorderDeck' && current.submitted) return current;
      const cardId =
        self.fortressCards.find((card) => card.cardId === 'curious-clit')?.cardId ??
        'curious-clit';
      return {
        stage: 'reorderDeck',
        cardId,
        market: self.pendingReorderMarket === 'win' ? 'win' : 'REGULAR',
        order: [...self.pendingReorderCards],
      };
    });
  }, [self]);

  const moveReorderCard = useCallback((index: number, delta: -1 | 1) => {
    setEffectsFlow((flow) => {
      if (flow?.stage !== 'reorderDeck') return flow;
      const to = index + delta;
      if (to < 0 || to >= flow.order.length) return flow;
      const order = [...flow.order];
      [order[index], order[to]] = [order[to]!, order[index]!];
      return { ...flow, order };
    });
  }, []);

  const confirmReorder = useCallback(() => {
    const flow = effectsFlowRef.current;
    if (flow?.stage !== 'reorderDeck') return;
    roomRef.current?.send(ClientMessage.Action, {
      type: 'reorder_fortress_deck',
      cardId: flow.cardId,
      market: flow.market,
      order: flow.order,
    });
    setEffectsFlow({ ...flow, submitted: true });
  }, []);

  /** Claim stage: add one resource pick; the second pick sends the claim. */
  const pickClaimResource = useCallback((kind: string) => {
    setEffectsFlow((cur) => {
      if (cur?.stage !== 'claim') return cur;
      if (cur.submitted) return cur;
      const picked = [...cur.picked, kind];
      if (picked.length < 2) return { ...cur, picked };
      roomRef.current?.send(ClientMessage.Action, {
        type: 'claim_resources',
        cardId: cur.cardId,
        resources: picked,
      });
      return { ...cur, picked, submitted: true };
    });
  }, []);

  /** iCarlie awards are chosen as soon as they are triggered, including during
   * another player's turn. */
  useEffect(() => {
    if (!self || self.pendingResourceClaims <= 0 || effectsFlow) return;
    const card = self.fortressCards.find((fc) => fc.cardId === 'icarlie');
    if (card) {
      setEffectsFlow({
        stage: 'claim',
        cardId: card.cardId,
        picked: [],
        remaining: self.pendingResourceClaims,
      });
    }
  }, [self, effectsFlow]);

  useEffect(() => {
    if (effectsFlow?.stage !== 'claim' || !effectsFlow.submitted || !self) return;
    if (self.pendingResourceClaims >= effectsFlow.remaining) return;
    setEffectsFlow(
      self.pendingResourceClaims > 0
        ? {
            ...effectsFlow,
            picked: [],
            remaining: self.pendingResourceClaims,
            submitted: false,
          }
        : null,
    );
  }, [self, effectsFlow]);

  /** Confirm stage accepted — send the pending one-shot effect. */
  const confirmEffect = useCallback(() => {
    const flow = effectsFlowRef.current;
    if (flow?.stage !== 'confirm') return;
    sendGameAction({
      type: flow.kind,
      cardId: flow.cardId,
    });
    setEffectsFlow(null);
  }, [sendGameAction]);

  /** Untap selection confirmed — ready the chosen Barracks. */
  const confirmUntap = useCallback(() => {
    const flow = effectsFlowRef.current;
    if (flow?.stage !== 'untap' || flow.selected.size === 0) return;
    sendGameAction({
      type: 'untap_barracks',
      cardId: flow.cardId,
      nodeIds: [...flow.selected],
    });
    setEffectsFlow(null);
  }, [sendGameAction]);

  /** Tokenizer: adjust a token on one of your fortresses. */
  const sendAdjustToken = useCallback(
    (targetCardId: string, delta: 1 | -1) => {
      const flow = effectsFlowRef.current;
      if (flow?.stage !== 'adjustToken') return;
      sendGameAction({
        type: 'adjust_fortress_token',
        cardId: flow.cardId,
        targetCardId,
        delta,
      });
      setEffectsFlow(null);
    },
    [sendGameAction],
  );

  /** Supreme Bondage: lock or unlock a face-up market card. */
  const sendFortressLock = useCallback(
    (market: FortressKind, marketCardId: string, locked: boolean) => {
      const flow = effectsFlowRef.current;
      if (flow?.stage !== 'market' || flow.kind !== 'set_fortress_lock') return;
      sendGameAction({
        type: 'set_fortress_lock',
        cardId: flow.cardId,
        market,
        marketCardId,
        locked,
      });
      setEffectsFlow(null);
    },
    [sendGameAction],
  );

  /** Cyclopz: cycle one face-up market card free of cost and action. */
  const sendFreeCycle = useCallback(
    (market: FortressKind, index: number) => {
      const flow = effectsFlowRef.current;
      if (flow?.stage !== 'market' || flow.kind !== 'free_cycle_market') return;
      sendGameAction({
        type: 'free_cycle_market',
        cardId: flow.cardId,
        market,
        index,
      });
      setEffectsFlow(null);
    },
    [sendGameAction],
  );

  // Escape closes the effects flow (sub-flows fall back to the tray list).
  useEffect(() => {
    if (!effectsFlow) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setEffectsFlow((cur) =>
        cur?.stage === 'reorderDeck' || cur?.stage === 'claim'
          ? cur
          : cur && cur.stage !== 'list'
            ? { stage: 'list' }
            : null,
      );
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [effectsFlow]);

  // Reflect the untap selection as persistent gold rings (the pickable set
  // pulses via the shared highlight effect below). Any other stage — including
  // backing out to the tray list — clears them, so rings can't outlive the
  // selection they represent.
  useEffect(() => {
    const game = gameRef.current;
    if (!game) return;
    if (effectsFlow?.stage === 'untap' || effectsFlow?.stage === 'moveFortress') {
      game.highlightNodes(
        effectsFlow.selected.size ? [...effectsFlow.selected] : null,
      );
    } else {
      game.highlightNodes(null);
    }
  }, [effectsFlow]);

  // --- Implement Fortress flow ---------------------------------------------
  // The revealed (face-up) cards whose pattern your FREE buildings (those not
  // already in one of your fortresses) realise somewhere on the board, in any
  // orientation. Mirrors the server's `implementFortress`, so a card shown here
  // (and affordable) will pass server validation. `implementable` (kept live —
  // recomputed via `boardVersion` when the board changes) feeds the implement
  // picker and the always-on "buildable" outlines on the market tiles; picking
  // a card then enumerates EVERY placement so the player chooses the location
  // (and orientation) on the board before confirming.
  // Per-card matcher inputs shared by the implement picker, the placement
  // stage, and the always-on market outlines: the cell / bridge / marker
  // constraints, mirroring the server's `implementFortress` exactly.
  const implementMatcherArgs = useCallback(
    (cardId: string) => {
      if (!self || !selfId) return null;
      const card = cardsById.get(cardId);
      if (!card) return null;
      const fx = effectFor(cardId);
      const committed = committedFortressNodes(self.fortressCards);
      // Zone placement constraint, folded into the matcher like the server.
      const zone = fx?.placement?.zone;
      const zoneOk = (id: number): boolean => {
        if (!zone) return true;
        const level = vigorZoneLevel(NODE_ZONE[id] ?? 'forest');
        return level >= (zone.min ?? 0) && level <= (zone.max ?? ZONE_HIGHEST);
      };
      // Exodial: ANY player's free (non-fortress) building fills a cell.
      const anyOwner = fx?.placement?.useOpponentBuildings === true;
      const cellAt = (id: number): FortressBuilding | null => {
        if (!zoneOk(id)) return null;
        if (committed.has(id)) return null; // already part of a fortress
        const info = nodeInfoRef.current.get(id);
        if (!info) return null;
        if (anyOwner) {
          if (info.ownerId === '' || info.fortressOwnerId !== '') return null;
        } else if (info.ownerId !== selfId) {
          return null;
        }
        const b = info.building;
        return b === 'scout' || b === 'barracks' || b === 'mine' ? b : null;
      };
      const matchOpts = {
        bridgeAt: (a: number, b: number) =>
          zoneOk(a) && zoneOk(b) && bridgesRef.current.has(edgeKey(a, b)),
        // Mirrors the server: an X can't take ANY fortress-hardened node
        // (own included — shared nodes corrupt on unravel/move), and zone
        // constraints bind the X nodes too.
        markerBlockedAt: (id: number) => {
          if (!zoneOk(id)) return true;
          return (nodeInfoRef.current.get(id)?.fortressOwnerId ?? '') !== '';
        },
        acceptMatch: (candidate: FortressMatch) => {
          const required = fx?.placement?.requireBuildingInZone;
          if (
            required !== undefined &&
            !candidate.buildingNodeIds.some(
              (id) => vigorZoneLevel(NODE_ZONE[id] ?? 'forest') === required,
            )
          ) {
            return false;
          }
          if (fx?.placement?.sameZone) {
            const ids = [
              ...candidate.buildingNodeIds,
              ...candidate.markerNodeIds,
              ...candidate.bridgeNodeIdPairs.flat(),
            ];
            if (
              new Set(
                ids.map((id) => vigorZoneLevel(NODE_ZONE[id] ?? 'forest')),
              ).size !== 1
            ) {
              return false;
            }
          }
          return true;
        },
      };
      return { card, cellAt, matchOpts };
    },
    [self, selfId, cardsById],
  );

  const implementable = useMemo((): ImplementCandidate[] => {
    void boardVersion; // reruns this memo when node/bridge refs change
    const out: ImplementCandidate[] = [];
    if (!self || !selfId) return out;
    const bank = toAmount(self.resources);
    const subs = substitutionsFor(self.fortressCards);
    // Client mirror of the server's GateQuery — registry build gates ("mine in
    // every zone", "6+ fortresses") hide a card here exactly when the server
    // would reject it.
    const gateQuery: GateQuery = {
      fortressCount: self.fortressCards.length,
      hasMineInEveryZone: () => {
        const levels = new Set<number>();
        for (const [id, info] of nodeInfoRef.current) {
          if (info.ownerId === selfId && info.building === 'mine') {
            levels.add(vigorZoneLevel(NODE_ZONE[id] ?? 'forest'));
          }
        }
        for (let level = 0; level <= ZONE_HIGHEST; level += 1) {
          if (!levels.has(level)) return false;
        }
        return true;
      },
      allOwnBuildingsInZone: (level) => {
        for (const [id, info] of nodeInfoRef.current) {
          if (info.ownerId === selfId && info.building !== '') {
            if (vigorZoneLevel(NODE_ZONE[id] ?? 'forest') !== level) return false;
          }
        }
        return true;
      },
    };
    const consider = (
      cardIds: readonly string[],
      marketKind: FortressKind | null,
      fromHand = false,
    ) => {
      for (const cardId of cardIds) {
        const args = implementMatcherArgs(cardId);
        if (!args) continue;
        const kind = marketKind ?? args.card.kind;
        const fx = effectFor(cardId);
        if (fx?.canImplement?.(gateQuery)) continue; // gate unmet — server rejects
        const match = matchFortressPattern(
          BUILD_GRAPH,
          args.cellAt,
          args.card.pattern,
          args.matchOpts,
        );
        if (!match) continue;
        // The price THIS player pays (fortress discounts applied), payable
        // through their substitutions. Hand cards are 1 obsidian cheaper.
        const cost = fortressCostFor(self.fortressCards, args.card);
        if (fromHand) cost[3] = Math.max(0, (cost[3] ?? 0) - 1);
        out.push({
          cardId,
          kind,
          name: args.card.name,
          cost: [...cost],
          affordable: canAffordWith(bank, cost, subs),
          nodeIds: nodesOfMatch(match),
          ...(fromHand ? { fromHand: true } : {}),
        });
      }
    };
    consider(markets.regular.cards, 'REGULAR');
    consider(markets.win.cards, 'win');
    // Dungeon Dice Martyr's face-down hand — only its holder ever has these.
    consider(self.hiddenCards, null, true);
    return out;
  }, [self, selfId, markets, boardVersion, implementMatcherArgs]);

  /** cardId → market-tile outline: 'ready' when the pattern is on the board AND
   * the Obsidian is in hand; 'poor' when only the pattern is. */
  const buildableById = useMemo(() => {
    const m = new Map<string, 'ready' | 'poor'>();
    for (const c of implementable) {
      m.set(c.cardId, c.affordable ? 'ready' : 'poor');
    }
    return m;
  }, [implementable]);

  // The Implement-Fortress button toggles the picker.
  const openImplement = useCallback(() => {
    setCycleMode(false);
    setCycleSel(null);
    setBuildFlow(null);
    setBridgeFlow(null);
    setAimFlow(null);
    setEffectsFlow(null);
    closeSelection();
    // Planning: EVERY face-up card (and hand card) is offered, no pattern
    // match or Obsidian required — a queued build may complete the pattern
    // and a queued yield may fund it. Real costs still shown; auto-play
    // re-validates and halts if the pattern/funds never materialise.
    if (planSlotRef.current !== null && self) {
      setImplementFlow((prev) => {
        if (prev) return null;
        const out: ImplementCandidate[] = [];
        const seen = new Set<string>();
        const consider = (
          ids: readonly string[],
          kind: FortressKind | null,
          fromHand = false,
        ) => {
          for (const cardId of ids) {
            const card = cardsById.get(cardId);
            if (!card || seen.has(`${cardId}:${fromHand}`)) continue;
            seen.add(`${cardId}:${fromHand}`);
            const cost = fortressCostFor(self.fortressCards, card);
            if (fromHand) cost[3] = Math.max(0, (cost[3] ?? 0) - 1);
            out.push({
              cardId,
              kind: kind ?? card.kind,
              name: card.name,
              cost: [...cost],
              affordable: true,
              nodeIds: [],
              ...(fromHand ? { fromHand: true } : {}),
            });
          }
        };
        consider(markets.regular.cards, 'REGULAR');
        consider(markets.win.cards, 'win');
        consider(self.hiddenCards, null, true);
        return { stage: 'pick', cards: out };
      });
      return;
    }
    setImplementFlow((prev) =>
      prev ? null : { stage: 'pick', cards: implementable },
    );
  }, [closeSelection, implementable, self, markets, cardsById]);

  const cancelImplement = useCallback(() => setImplementFlow(null), []);

  const sendImplement = useCallback(
    (
      cardId: string,
      kind: FortressKind,
      choice?: { resource: string },
      placement?: { anchorNodeId: number; orientation: number },
    ) => {
      sendGameAction({
        type: 'implement_fortress',
        kind,
        cardId,
        ...(choice ? { choice } : {}),
        ...(placement ? { placement } : {}),
      });
      setImplementFlow(null);
    },
    [sendGameAction],
  );

  /** A card picked in the Implement picker. Live play moves to the placement
   * stage — every possible location pulses and the player picks one on the
   * board. Planning sends the queued action straight away (no placement: the
   * board may change before the move plays, so the server auto-finds one). */
  const pickImplementCard = useCallback(
    (cardId: string, kind: FortressKind, choice?: { resource: string }) => {
      if (planSlotRef.current !== null) {
        sendImplement(cardId, kind, choice);
        return;
      }
      const flow = implementFlowRef.current;
      if (flow?.stage !== 'pick') return;
      const candidate = flow.cards.find(
        (c) => c.cardId === cardId && c.kind === kind,
      );
      const args = candidate ? implementMatcherArgs(cardId) : null;
      if (!candidate || !args) return;
      const matches = matchAllFortressPatterns(
        BUILD_GRAPH,
        args.cellAt,
        args.card.pattern,
        args.matchOpts,
      );
      if (matches.length === 0) return;
      const candidates: ReadonlySet<number> = new Set(
        matches.flatMap(nodesOfMatch),
      );
      // A single possible placement is preselected — Confirm is one tap away.
      const selected =
        matches.length === 1
          ? { nodeId: nodesOfMatch(matches[0]!)[0]!, options: [0], pos: 0 }
          : null;
      setImplementFlow({
        stage: 'place',
        cards: flow.cards,
        card: candidate,
        matches,
        candidates,
        selected,
      });
    },
    [sendImplement, implementMatcherArgs],
  );

  /** Rotate: cycle to the next possible placement over the picked node. */
  const rotateImplement = useCallback(() => {
    setImplementFlow((cur) => {
      if (cur?.stage !== 'place' || !cur.selected) return cur;
      const { options, pos } = cur.selected;
      if (options.length < 2) return cur;
      return {
        ...cur,
        selected: { ...cur.selected, pos: (pos + 1) % options.length },
      };
    });
  }, []);

  /** Confirm the selected placement — send the implement pinned to it. */
  const confirmImplement = useCallback(
    (choice?: { resource: string }) => {
      const cur = implementFlowRef.current;
      if (cur?.stage !== 'place' || !cur.selected) return;
      const match = cur.matches[cur.selected.options[cur.selected.pos]!];
      if (!match) return;
      sendImplement(cur.card.cardId, cur.card.kind, choice, {
        anchorNodeId: match.anchorNodeId,
        orientation: match.orientation,
      });
    },
    [sendImplement],
  );

  /** Back out of the placement stage to the card picker. */
  const backToImplementPick = useCallback(() => {
    setImplementFlow((cur) =>
      cur?.stage === 'place' ? { stage: 'pick', cards: cur.cards } : cur,
    );
  }, []);

  // The implement flow's gold-ring channel: the placement stage rings the
  // currently selected placement (the pick stage's card hover drives the same
  // channel directly); anything else clears it.
  useEffect(() => {
    const game = gameRef.current;
    if (!game) return;
    if (implementFlow?.stage === 'place' && implementFlow.selected) {
      const { options, pos } = implementFlow.selected;
      const match = implementFlow.matches[options[pos]!];
      game.highlightNodes(match ? nodesOfMatch(match) : null);
      return;
    }
    game.highlightNodes(null);
  }, [implementFlow]);

  // Escape steps the implement flow back: placement → card picker → closed.
  useEffect(() => {
    if (!implementFlow) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setImplementFlow((cur) =>
        cur?.stage === 'place' ? { stage: 'pick', cards: cur.cards } : null,
      );
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [implementFlow]);

  // Reflect the bridge flow on the board: highlight the eligible paths.
  useEffect(() => {
    const game = gameRef.current;
    if (!game) return;
    game.setBridgeHighlight(
      bridgeFlow?.allowed ?? null,
      bridgeFlow?.tooCostly ?? null,
    );
  }, [bridgeFlow]);

  // Escape cancels the bridge flow.
  useEffect(() => {
    if (!bridgeFlow) return;
    const onKey = (e: KeyboardEvent) => {
      // Esc steps back from the wildcard resource pick; otherwise it cancels.
      if (e.key === 'Escape') {
        setBridgeFlow((cur) => (cur?.pending ? { ...cur, pending: null } : null));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [bridgeFlow]);

  // Reflect the build/aim/effects flows on the board (they're mutually
  // exclusive; one effect owns the node highlight so none can clobber the
  // others'): pulse the build flow's legal targets and swap the cursor while
  // placing, pulse the aim flow's pickable Barracks / legal targets (the chosen
  // Barracks stays lit while picking a posture), or pulse a card effect's
  // pickable nodes (Scouts to protect / Barracks to ready); clear otherwise.
  useEffect(() => {
    const game = gameRef.current;
    if (!game) return;
    if (buildFlow?.stage === 'place') {
      game.setBuildHighlight(
        [...buildFlow.allowed],
        [...buildFlow.tooCostly],
        buildFlow.vigorPenalty,
        buildFlow.oneTimeGain,
      );
      game.setBuildSelection(buildFlow.selected ?? null);
      game.setBuildCursor(buildingCursor(buildFlow.building));
      return;
    }
    game.setBuildCursor(null);
    game.setBuildSelection(null);
    if (defenseProtect) {
      game.setBuildHighlight([...defenseProtect.allowed]);
      return;
    }
    if (aimFlow) {
      game.setBuildHighlight(
        aimFlow.stage === 'pickBarracks'
          ? [...aimFlow.sources]
          : aimFlow.stage === 'mode'
            ? [aimFlow.barracksId]
            : [...aimFlow.allowed],
      );
      return;
    }
    if (
      effectsFlow?.stage === 'pickScout' ||
      effectsFlow?.stage === 'replicate' ||
      effectsFlow?.stage === 'untap' ||
      effectsFlow?.stage === 'moveFortress'
    ) {
      game.setBuildHighlight([...effectsFlow.allowed]);
      return;
    }
    // Implement-Fortress placement: pulse every node the card's pattern can
    // land on (the selected placement rides the gold-ring channel instead).
    if (implementFlow?.stage === 'place') {
      game.setBuildHighlight([...implementFlow.candidates]);
      return;
    }
    game.setBuildHighlight(null);
  }, [buildFlow, aimFlow, effectsFlow, defenseProtect, implementFlow]);

  // Escape cancels the build flow; and leaving your turn ends it.
  useEffect(() => {
    if (!buildFlow) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setBuildFlow(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [buildFlow]);

  // While picking a building, a tap/click anywhere outside the picker dismisses
  // it. Only during the "pick" stage: in "place" the board itself is the target
  // surface (a click there places the building), so we mustn't intercept it. The
  // Construct-Building button is excluded so its own onClick handles the toggle.
  useEffect(() => {
    if (buildFlow?.stage !== 'pick') return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as HTMLElement | null;
      if (t?.closest('.build-picker')) return;
      if (t?.closest('[data-action="construct_building"]')) return;
      setBuildFlow(null);
    };
    window.addEventListener('pointerdown', onDown);
    return () => window.removeEventListener('pointerdown', onDown);
  }, [buildFlow]);

  useEffect(() => {
    // Planning queued moves keeps the flows alive off-turn — they close when
    // planning ends (planSlot returns to null re-runs this effect).
    if (currentPlayerId !== selfId && planSlot === null) {
      setBuildFlow(null);
      setBridgeFlow(null);
      setImplementFlow(null);
      setEffectsFlow((flow) => (flow?.stage === 'claim' ? flow : null));
      // The defense-window variant legitimately runs off-turn; only the normal
      // on-turn aim flow ends with the turn.
      setAimFlow((cur) => (cur && !cur.defense ? null : cur));
    }
  }, [currentPlayerId, selfId, planSlot]);

  // Entering your own turn ends planning — the same pickers now act live (and
  // this turn's queued moves have just auto-played or halted server-side).
  useEffect(() => {
    if (currentPlayerId === selfId) setPlanSlot(null);
  }, [currentPlayerId, selfId]);

  // Market rows and the player roster both mutate live (deal / cycle; join /
  // leave / play). When the tile under an open popover unmounts, React does NOT
  // synthesise an onMouseLeave, so a hover preview (or a pinned detail) would be
  // left orphaned at a stale anchor. Drop any open detail whose target is gone.
  useEffect(() => {
    const cardIds = new Set([...markets.regular.cards, ...markets.win.cards]);
    const playerIds = new Set(players.map((p) => p.id));
    setSelection((cur) => {
      if (!cur) return cur;
      const present =
        cur.kind === 'card'
          ? cardIds.has(cur.card.id)
          : cur.kind === 'player'
            ? playerIds.has(cur.player.id)
            : cur.kind === 'fortressCard'
              ? // keep while its owning player still exists (instanceKey is
                // `${playerId}:${index}`).
                playerIds.has(
                  cur.instanceKey.slice(0, cur.instanceKey.lastIndexOf(':')),
                )
              : // historyCard: not tied to the live market — its anchor (a
                // history row) persists, and leaving History mode closes it.
                true;
      return present ? cur : null;
    });
  }, [markets, players]);

  // Hover previews and click-to-pin, shared by market cards and player rows.
  // Functional updates keep these independent of the current selection, so the
  // handler identities can stay stable across renders.
  const selectionHandlers = useMemo<SelectionHandlers>(
    () => ({
      onPreview: (target, anchorEl) => {
        // Entering any tile cancels a pending close (e.g. sliding tile→tile, or
        // back onto the tile from its popover).
        cancelPreviewClose();
        setSelection((cur) =>
          // A pinned target wins over hover, so passing over other tiles doesn't
          // yank an explicitly-opened detail away.
          cur?.pinned ? cur : { ...target, anchorEl, pinned: false },
        );
      },
      onPreviewEnd: (target) => {
        // Defer the close so the cursor can travel onto the popover and keep it
        // alive; only drop it if this same hover target is still showing.
        cancelPreviewClose();
        previewCloseTimer.current = window.setTimeout(() => {
          previewCloseTimer.current = null;
          setSelection((cur) =>
            cur &&
            !cur.pinned &&
            cur.kind === target.kind &&
            targetId(cur) === targetId(target)
              ? null
              : cur,
          );
        }, PREVIEW_CLOSE_DELAY);
      },
      onPin: (target, anchorEl) => {
        cancelPreviewClose();
        setSelection({ ...target, anchorEl, pinned: true });
      },
    }),
    [cancelPreviewClose],
  );

  // Ref mirrors for the once-created board hover handler.
  useEffect(() => {
    playersRef.current = players;
  }, [players]);
  useEffect(() => {
    cardsByIdRef.current = cardsById;
  }, [cardsById]);
  useEffect(() => {
    selectionHandlersRef.current = selectionHandlers;
  }, [selectionHandlers]);

  // The player popover resolves the LIVE player by id each render, so tokens and
  // hand size update while it's open instead of showing the hover-time snapshot.
  const selectedPlayer =
    selection?.kind === 'player'
      ? players.find((p) => p.id === selection.player.id)
      : undefined;

  // While a fortress-card detail is open (hover or pinned), ring its nodes on
  // the board; clear the highlight whenever it's not a fortress selection.
  const fortressKey =
    selection?.kind === 'fortressCard' ? selection.instanceKey : null;
  const fortressNodes =
    selection?.kind === 'fortressCard' ? selection.nodeIds : null;
  useEffect(() => {
    gameRef.current?.highlightNodes(fortressNodes);
    // `fortressKey` keys the effect to the fortress identity (not the array
    // ref, which changes every hover) so the pulse isn't restarted needlessly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fortressKey]);

  /** "View on board": pan + zoom the board to the open fortress's nodes. */
  const focusFortress = useCallback((nodeIds: number[]) => {
    gameRef.current?.focusNodes(nodeIds);
  }, []);

  const signOut = useCallback(async () => {
    setSigningOut(true);
    await logout();
    window.location.replace('/auth');
  }, []);

  // Deploy detector: this bundle bakes in the APP_VERSION it was built from;
  // the server reports its own (live) one via /app-state. Server higher → a
  // new build shipped since this tab loaded → raise the sticky refresh banner.
  // Checked at boot and re-polled; once raised it never clears (the only way
  // forward is the reload it asks for).
  const [newVersionAvailable, setNewVersionAvailable] = useState(false);
  useEffect(() => {
    let stopped = false;
    const check = async () => {
      const server = await fetchServerAppVersion();
      if (!stopped && server !== null && server > APP_VERSION) {
        setNewVersionAvailable(true);
      }
    };
    void check();
    const id = window.setInterval(() => void check(), APP_VERSION_POLL_MS);
    return () => {
      stopped = true;
      window.clearInterval(id);
    };
  }, []);

  return (
    <div className="app">
      {newVersionAvailable && (
        <div className="version-banner" role="alert">
          <span className="version-banner-text">
            A new version of Fortress is available.
          </span>
          <button
            type="button"
            className="version-banner-refresh"
            onClick={() => window.location.reload()}
          >
            ⟳ REFRESH
          </button>
        </div>
      )}
      {!activeRoom ? (
        <>
          {authenticated && (
            <button
              type="button"
              className="sign-out-button"
              onClick={() => void signOut()}
              disabled={signingOut}
            >
              {signingOut ? 'Signing out…' : 'Sign out'}
            </button>
          )}
          <LobbyHub
            onEnterRoom={(roomId, opts) => {
              setReconnectFailed(false);
              setActiveRoom({ roomId, spectate: opts?.spectate });
            }}
            onReconnect={({ roomId, reconnectionToken }) => {
              setReconnectFailed(false);
              setActiveRoom({ roomId, reconnectionToken });
            }}
            reconnectFailed={reconnectFailed}
          />
        </>
      ) : (
        <>
      <div className="stage" ref={stageRef} />
      {/* Anchor for board-hover fortress popovers: an invisible fixed point
          moved to the hovered node's screen position. */}
      <div
        ref={boardAnchorRef}
        aria-hidden
        style={{
          position: 'fixed',
          left: 0,
          top: 0,
          width: 1,
          height: 1,
          pointerEvents: 'none',
        }}
      />
      {/* Turn cues are moot once the game has ended (and would overlap the
          result banner, which shares the top-center slot). */}
      {turnToast && phase !== 'ended' && (
        <div className="turn-toast" role="status">
          It&apos;s Your Turn
        </div>
      )}
      {queueHalt && phase !== 'ended' && (
        <div className="turn-toast queue-halt-toast" role="status">
          <strong>Queued moves stopped</strong>
          <span>
            {queueHalt.label
              ? `"${queueHalt.label}" — ${queueHalt.reason || 'no longer valid.'}`
              : queueHalt.reason || 'Take your turn.'}
          </span>
        </div>
      )}
      {connectionLost && (
        <div className="conn-toast" role="status">
          Connection lost — reconnecting…
        </div>
      )}
      {/* Game-over result banner — a top strip, NOT a modal: the match is
          over (no more actions or turns), but everyone stays in the room to
          study the final board, the win condition and each player's cards.
          The winning card is a hover/click chip that opens the same fortress
          detail popover the roster and history use. */}
      {(gameOver !== null || phase === 'ended') && (
        <div className="gameover-banner" role="status">
          {(() => {
            const winner = gameOver?.winnerId
              ? players.find((p) => p.id === gameOver.winnerId)
              : undefined;
            const title = winner
              ? winner.id === selfId
                ? '🏆 Victory!'
                : `🏆 ${winner.name} wins`
              : gameOver?.winnerId === null && gameOver
                ? 'Game over — a draw'
                : 'Game over';
            const winCard = gameOver?.winCardId
              ? cardsById.get(gameOver.winCardId)
              : undefined;
            // Prefer the winner's LIVE fortress instance (so the popover can
            // highlight it and pan the board to it); fall back to the plain
            // catalogue card if the instance isn't on their board (e.g. a
            // hand-count win) or on old rooms without the synced card id.
            const idx =
              winner && winCard
                ? winner.fortressCards.findIndex(
                    (fc) => fc.cardId === winCard.id,
                  )
                : -1;
            const fc = idx >= 0 ? winner?.fortressCards[idx] : undefined;
            const target: SelectionTarget | null = winCard
              ? winner && fc
                ? {
                    kind: 'fortressCard',
                    card: winCard,
                    nodeIds: [...fc.nodeIds],
                    tokens: fc.tokens,
                    disabled: fc.disabled,
                    instanceKey: `${winner.id}:${idx}`,
                  }
                : { kind: 'historyCard', card: winCard }
              : null;
            return (
              <>
                <strong className="gameover-title">{title}</strong>
                <span className="gameover-sub">
                  {winCard && target ? (
                    <>
                      won with{' '}
                      <button
                        type="button"
                        className="gameover-card-link"
                        onMouseEnter={(e) =>
                          selectionHandlers.onPreview(target, e.currentTarget)
                        }
                        onMouseLeave={() =>
                          selectionHandlers.onPreviewEnd(target)
                        }
                        onClick={(e) =>
                          selectionHandlers.onPin(target, e.currentTarget)
                        }
                      >
                        {winCard.name}
                      </button>
                    </>
                  ) : (
                    'A fortress win condition was met.'
                  )}
                </span>
                <span className="gameover-hint">
                  The board stays open for review — no more turns.
                </span>
              </>
            );
          })()}
          <button
            type="button"
            className="turn-action turn-action-end"
            onClick={() => {
              void roomRef.current?.leave();
              setActiveRoom(null);
              setPhase('lobby');
              setPlayers([]);
              setSpectators([]);
              setSelfId('');
              setGameOver(null);
            }}
          >
            Back to lobbies
          </button>
        </div>
      )}
      {(phase === 'setup' || phase === 'playing' || phase === 'ended') && (
        <PlayerSidebar
          players={players}
          round={round}
          turn={turn}
          phase={phase}
          currentPlayerId={currentPlayerId}
          timePerTurnMs={gameConfig?.timePerTurnMs}
          selfId={selfId}
          spectators={spectators}
          handlers={selectionHandlers}
          registerRow={registerRow}
          history={history}
          historyMode={historyMode}
          cardsById={cardsById}
          cardsByName={cardsByName}
          onToggleHistory={() => {
            setHistoryMode((h) => !h);
            closeSelection();
          }}
          onFocusNode={(nodeId) => gameRef.current?.focusNode(nodeId)}
          collapsed={playersCollapsed}
          onToggle={() => {
            setPlayersCollapsed((c) => !c);
            closeSelection();
          }}
          self={self}
          planSlot={planSlot}
          onPlanSlot={setPlanSlot}
          onUnqueueMove={(slot, index) =>
            roomRef.current?.send(ClientMessage.UnqueueMove, { slot, index })
          }
          queuedSel={queuedSel}
          onSelectQueued={setQueuedSel}
          expectedFirst={firstPlayerId === selfId && roundCyclerId === ''}
          rsQueueActive={rsMode === 'queue'}
          onToggleRsQueue={toggleRsQueueMode}
          onClearQueuedRoundStart={clearQueuedRoundStartCycle}
        />
      )}
      {/* Action notifications — a transient popup beside the roster row of each
          player who just acted. Anchored to the live row element, so it follows
          the sidebar as the roster reflows; shown in both setup and playing. */}
      {(phase === 'setup' || phase === 'playing') &&
        notices.map((n) => {
          const anchorEl = playerRowEls.current.get(n.playerId);
          if (!anchorEl) return null;
          return (
            <ActionNoticePopover
              key={n.key}
              notice={n}
              anchorEl={anchorEl}
              onDismiss={() => dismissNotice(n.playerId)}
            />
          );
        })}
      {/* Other players' live pointers over the sidebars ("Alice is looking at
          this fortress card"). Board-area cursors render inside the Pixi
          canvas instead (see pushBoardCursors). */}
      <RemoteSidebarCursors
        cursors={sidebarCursors}
        players={players}
        spectators={spectators}
      />
      {/* Remote touch taps over the panels (board taps render in Pixi). */}
      <RemoteTapRipples taps={tapRipples} />
      {/* Emoji reactions over the panels (board reactions render in Pixi). */}
      <RemoteEmojiPops pops={emojiPops} />
      {/* Right-click / long-press emoji picker: picking sends the reaction to
          everyone, pinned to the pressed point, and echoes it locally. */}
      {emojiPicker && (
        <EmojiPicker
          at={emojiPicker.screen}
          onClose={() => setEmojiPicker(null)}
          onPick={(emoji) => {
            pushRecentEmoji(emoji);
            try {
              roomRef.current?.send(ClientMessage.CursorEmoji, {
                ...emojiPicker.sample,
                emoji,
              });
            } catch {
              /* socket mid-close — still echo locally */
            }
            spawnEmoji({ ...emojiPicker.sample, emoji });
            setEmojiPicker(null);
          }}
        />
      )}
      {/* Player detail popover — the roster sidebar (and its hover) is shown in
          both setup and playing, so this lives outside the playing-only block. */}
      {selection?.kind === 'player' && selectedPlayer && (
        <PlayerDetailPopover
          player={selectedPlayer}
          cardsById={cardsById}
          anchorEl={selection.anchorEl}
          pinned={selection.pinned}
          onClose={closeSelection}
          onHoverEnter={cancelPreviewClose}
          onHoverLeave={closePreviewSoon}
        />
      )}
      {phase === 'setup' && (
        <StartupOverlay
          self={self}
          players={players}
          currentPlayerId={currentPlayerId}
          selfId={selfId}
          startingProduction={gameConfig?.startingProduction ?? 0}
          selectedNode={selectedHomeNode}
          onConfirmHome={confirmHomeBase}
          onCancelHome={() => setSelectedHomeNode(null)}
          onConfirmProduction={(amounts) =>
            roomRef.current?.send(ClientMessage.SetProduction, amounts)
          }
        />
      )}
      {/* The board view (markets, card popovers, zoom, library) stays up on an
          ended game for the post-match review; only the action UI below is
          gated to the live `playing` phase. */}
      {(phase === 'playing' || phase === 'ended') && (
        <>
          <MarketSidebar
            markets={markets}
            queuedMark={queuedMarketMark}
            collapsed={marketCollapsed}
            onToggle={() => {
              setMarketCollapsed((c) => !c);
              closeSelection();
            }}
            onOpenLibrary={() => {
              setLibraryOpen(true);
              closeSelection();
            }}
            handlers={selectionHandlers}
            cardsById={cardsById}
            // No implement outlines on an ended game — nothing is buildable.
            buildable={phase === 'playing' ? buildableById : EMPTY_BUILDABLE}
            cycle={{
              active: cycleMode,
              selectedKind: cycleSel?.kind ?? null,
              selectedIndices: cycleSel?.indices ?? EMPTY_SELECTION,
              foodCost: cycleFoodCost,
              affordable: cycleAffordable,
              onToggleCard: toggleCycleCard,
              onConfirm: confirmCycle,
              onCancel: cancelCycle,
            }}
            roundStart={
              rsMode
                ? {
                    mode: rsMode,
                    sel: rsSel,
                    onToggle: toggleRsCard,
                    onConfirm: () => confirmRoundStartCycle(rsSel),
                    onSkip: () =>
                      confirmRoundStartCycle({ regular: null, win: null }),
                    onCancel:
                      rsMode === 'queue'
                        ? () => setRsMode(null)
                        : undefined,
                  }
                : null
            }
          />
          {phase === 'playing' && self?.pendingMartyrDiscards[0] && (
            <MartyrDiscardPrompt
              key={`${self.pendingMartyrDiscards[0]}:${self.pendingMartyrDiscards.length}`}
              pending={self.pendingMartyrDiscards[0]}
              victimName={
                players.find(
                  (player) =>
                    player.id === self.pendingMartyrDiscards[0]!.split('|')[0],
                )?.name ?? 'Opponent'
              }
              onChoose={(victimId, index) =>
                roomRef.current?.send(ClientMessage.MartyrDiscard, {
                  victimId,
                  index,
                })
              }
            />
          )}
          {phase === 'playing' && self && upkeepSelf.length > 0 && (
            <BarracksUpkeepPrompt
              key={upkeepSelf.map((e) => e.nodeId).join(',')}
              entries={upkeepSelf.map(({ nodeId, cost }) => ({
                nodeId,
                cost,
                zone: NODE_ZONE[nodeId] ?? 'forest',
                affordable: canAffordWith(
                  toAmount(self.resources),
                  [0, cost, 0, 0],
                  substitutionsFor(self.fortressCards),
                ),
              }))}
              onFocus={(nodeId) => gameRef.current?.focusNode(nodeId)}
              onResolve={(nodeId, keep) =>
                roomRef.current?.send(ClientMessage.BarracksUpkeep, {
                  nodeId,
                  keep,
                })
              }
            />
          )}
          {(selection?.kind === 'card' ||
            selection?.kind === 'historyCard') && (
            <CardDetailPopover
              card={selection.card}
              anchorEl={selection.anchorEl}
              pinned={selection.pinned}
              onClose={closeSelection}
              onHoverEnter={cancelPreviewClose}
              onHoverLeave={closePreviewSoon}
            />
          )}
          {selection?.kind === 'fortressCard' &&
            (() => {
              // Resolve the owner's LIVE card state each render (instanceKey is
              // `${playerId}:${index}`), so tokens / exhausted update while the
              // popover is open instead of freezing the hover-time snapshot.
              // Falls back to the snapshot if the instance moved (e.g. the
              // owner's list changed under an open popover).
              const sep = selection.instanceKey.lastIndexOf(':');
              const ownerId = selection.instanceKey.slice(0, sep);
              const index = Number(selection.instanceKey.slice(sep + 1));
              const live = players.find((p) => p.id === ownerId)
                ?.fortressCards[index];
              const fresh =
                live && live.cardId === selection.card.id ? live : undefined;
              return (
                <FortressCardDetailPopover
                  card={selection.card}
                  tokens={fresh?.tokens ?? selection.tokens}
                  disabled={fresh?.disabled ?? selection.disabled}
                  hasNodes={selection.nodeIds.length > 0}
                  // Hovering the fortress on the board already has the
                  // viewport on it — the pan-to action would be a no-op.
                  onViewOnBoard={
                    selection.fromBoard
                      ? undefined
                      : () => focusFortress(selection.nodeIds)
                  }
                  anchorEl={selection.anchorEl}
                  pinned={selection.pinned}
                  onClose={closeSelection}
                  onHoverEnter={cancelPreviewClose}
                  onHoverLeave={closePreviewSoon}
                />
              );
            })()}
          <div
            className={`zoom-controls${marketCollapsed ? '' : ' is-market-expanded'}`}
          >
            <button
              type="button"
              className="zoom-btn"
              onClick={() => gameRef.current?.zoomByFactor(1.25)}
              title="Zoom in"
              aria-label="Zoom in"
            >
              +
            </button>
            <button
              type="button"
              className="zoom-btn"
              onClick={() => gameRef.current?.zoomByFactor(0.8)}
              title="Zoom out"
              aria-label="Zoom out"
            >
              −
            </button>
          </div>
          {phase === 'playing' && aimFlow && (
            <AimPicker
              flow={aimFlow}
              range={
                gameConfig && self
                  ? resolveBuilding(gameConfig, self, 'barracks').range
                  : gameConfig?.buildings.barracks?.range
              }
              onMode={selectAimMode}
              onCancel={cancelAim}
            />
          )}
          {phase === 'playing' && iAmDefending && (
            <DefenseBar
              aiming={!!aimFlow}
              protecting={!!defenseProtect}
              canProtect={protectCardId !== null && atRiskScoutCount > 0}
              onAim={openDefenseAim}
              onProtect={startDefenseProtect}
              onDone={sendDefenseDone}
            />
          )}
          {currentPlayerId === selfId && self && defense.active && (
            <StatusBanner
              title="Turn ending"
              sub="Attacked players may aim defenders — combat resolves when they're done."
            />
          )}
          {/* Round-start cycle window: the round is paused on the first
              player's free "cycle up to 1 card per market" step. */}
          {rsWindowMine && (
            <StatusBanner
              title="Round start — your call"
              sub="Cycle out up to 1 card from each Fortress market for free, or cycle nothing — choose in the market panel."
            />
          )}
          {roundCyclerId !== '' && !rsWindowMine && (
            <StatusBanner
              title="Round starting"
              sub={`${
                players.find((p) => p.id === roundCyclerId)?.name ??
                'The first player'
              } may cycle a card out of each market before the round begins.`}
            />
          )}
          {phase === 'playing' &&
            (currentPlayerId === selfId || planSlot !== null) &&
            self &&
            !defense.active && (
            <>
              {buildFlow && (
                <BuildPicker
                  flow={buildFlow}
                  // Planning assumes infinite resources — every building is
                  // pickable; the auto-play validator settles affordability.
                  resources={
                    planSlot !== null ? PLANNING_BANK : self.resources
                  }
                  subs={substitutionsFor(self.fortressCards)}
                  bunBun={bunBun}
                  onToggleRider={toggleDoubleOneTime}
                  buildings={
                    gameConfig
                      ? {
                          scout: resolveBuilding(gameConfig, self, 'scout'),
                          barracks: resolveBuilding(
                            gameConfig,
                            self,
                            'barracks',
                          ),
                          mine: resolveBuilding(gameConfig, self, 'mine'),
                          bridge: resolveBuilding(gameConfig, self, 'bridge'),
                        }
                      : undefined
                  }
                  onSelect={selectBuilding}
                  onConfirm={confirmBuild}
                  onCancel={cancelBuild}
                />
              )}
              {bridgeFlow && (
                <div
                  className="build-picker"
                  role="dialog"
                  aria-label="Construct a bridge"
                >
                  <div className="build-picker-head">
                    <span className="build-picker-title">
                      {bridgeFlow.pending
                        ? 'Build bridge — pay with which resource?'
                        : bridgeFlow.allowed.length > 0
                          ? 'Tap a path to build a bridge'
                          : 'No bridgeable paths in range'}
                    </span>
                    <button
                      type="button"
                      className="build-picker-cancel"
                      onClick={
                        bridgeFlow.pending
                          ? () =>
                              setBridgeFlow((cur) =>
                                cur ? { ...cur, pending: null } : cur,
                              )
                          : cancelBridge
                      }
                      title={bridgeFlow.pending ? 'Back (Esc)' : 'Cancel (Esc)'}
                      aria-label={bridgeFlow.pending ? 'Back' : 'Cancel'}
                    >
                      ✕
                    </button>
                  </div>
                  {bridgeFlow.pending && gameConfig && self && (
                    <div className="build-picker-options">
                      {RESOURCE_KINDS.map((kind) => {
                        const cost = wildcardCostAs(
                          resolveBuilding(gameConfig, self, 'bridge').cost,
                          kind,
                        );
                        // Planning queues anything; live play gates on the
                        // same sub-aware answer the server validates with.
                        const affordable =
                          planSlot !== null ||
                          canAffordWith(
                            toAmount(self.resources),
                            cost,
                            substitutionsFor(self.fortressCards),
                          );
                        return (
                          <button
                            key={kind}
                            type="button"
                            className="build-option"
                            disabled={!affordable}
                            onClick={() => {
                              const p = bridgeFlow.pending;
                              if (!p) return;
                              sendGameAction({
                                type: 'construct_bridge',
                                from: p.from,
                                to: p.to,
                                payWith: kind,
                              });
                              setBridgeFlow(null);
                            }}
                            title={
                              affordable
                                ? `Pay with ${RESOURCE_LABELS[kind]}`
                                : `Not enough ${RESOURCE_LABELS[kind]}`
                            }
                          >
                            <ResourceIcon kind={kind} size={36} />
                            <span className="build-option-name">
                              {RESOURCE_LABELS[kind]}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              {implementFlow?.stage === 'pick' && (
                <ImplementPicker
                  cards={implementFlow.cards}
                  deferChoice={planSlot === null}
                  onPick={pickImplementCard}
                  onHover={(nodeIds) =>
                    gameRef.current?.highlightNodes(nodeIds)
                  }
                  onCancel={cancelImplement}
                />
              )}
              {implementFlow?.stage === 'place' && (
                <ImplementPlacementBar
                  card={implementFlow.card}
                  selected={implementFlow.selected}
                  onRotate={rotateImplement}
                  onConfirm={confirmImplement}
                  onBack={backToImplementPick}
                />
              )}
              {effectsFlow && (
                <EffectsPicker
                  flow={effectsFlow}
                  player={self}
                  cardsById={cardsById}
                  markets={markets}
                  context={effectContext}
                  bunBun={bunBun}
                  onStart={startEffect}
                  onConfirm={confirmEffect}
                  onConfirmUntap={confirmUntap}
                  onConfirmMove={confirmMoveFortress}
                  onToggleMoveRider={toggleMoveDoubleOneTime}
                  onStartReorder={startReorderMarket}
                  onMoveReorder={moveReorderCard}
                  onConfirmReorder={confirmReorder}
                  onPickClaim={pickClaimResource}
                  onAdjustToken={sendAdjustToken}
                  onLock={sendFortressLock}
                  onFreeCycle={sendFreeCycle}
                  onBack={backToEffectsList}
                  onCancel={cancelEffects}
                />
              )}
              <TurnActionBar
                player={self}
                actionsRemaining={actionsRemaining}
                actionsPerTurn={Math.max(
                  gameConfig?.actionsPerTurn ?? 0,
                  self.fortressCards.some((card) => card.cardId === 'action-blaster')
                    ? 4
                    : players.some((player) =>
                          player.fortressCards.some(
                            (card) => card.cardId === 'action-blaster',
                          ),
                        )
                      ? 3
                      : 0,
                )}
                anyPassed={players.some((p) => p.passed)}
                cycleActive={cycleMode}
                buildActive={!!buildFlow}
                bridgeActive={!!bridgeFlow}
                implementActive={!!implementFlow}
                aimActive={!!aimFlow}
                effectsActive={!!effectsFlow}
                hasEffectCards={hasEffectCards}
                usableEffectCount={usableEffectCount}
                onConstructBuilding={openBuildPicker}
                onConstructBridge={startBridge}
                onImplementFortress={openImplement}
                onCycleMarket={startCycleMarket}
                onAimBarracks={openAimFlow}
                onEffects={openEffects}
                onEndTurn={() => roomRef.current?.send(ClientMessage.EndTurn)}
                onPass={() => roomRef.current?.send(ClientMessage.Pass)}
                planningSlot={planSlot}
                onEndPlanning={() => setPlanSlot(null)}
                onQueuePass={() => {
                  if (planSlot === null) return;
                  roomRef.current?.send(ClientMessage.QueueMove, {
                    slot: planSlot,
                    action: { type: 'pass' },
                    label: 'Pass',
                  });
                  setPlanSlot(null);
                }}
              />
            </>
          )}
          {phase === 'playing' &&
            currentPlayerId !== selfId &&
            planSlot === null &&
            self &&
            effectsFlow?.stage === 'claim' && (
              <EffectsPicker
                flow={effectsFlow}
                player={self}
                cardsById={cardsById}
                markets={markets}
                context={effectContext}
                bunBun={bunBun}
                onStart={startEffect}
                onConfirm={confirmEffect}
                onConfirmUntap={confirmUntap}
                onConfirmMove={confirmMoveFortress}
                onToggleMoveRider={toggleMoveDoubleOneTime}
                onStartReorder={startReorderMarket}
                onMoveReorder={moveReorderCard}
                onConfirmReorder={confirmReorder}
                onPickClaim={pickClaimResource}
                onAdjustToken={sendAdjustToken}
                onLock={sendFortressLock}
                onFreeCycle={sendFreeCycle}
                onBack={backToEffectsList}
                onCancel={cancelEffects}
              />
            )}
          {libraryOpen && (
            <div className="fortress-modal" role="dialog" aria-modal="true">
              <FortressLibrary onClose={() => setLibraryOpen(false)} />
            </div>
          )}
        </>
      )}
      {phase === 'lobby' && (
        <Lobby
          players={players}
          self={self}
          lobbyInfo={lobbyInfo}
          spectators={spectators}
          isSpectator={isSpectator}
          onToggleReady={toggleReady}
          disabled={!self}
          devMode={devMode}
          onAutoInit={sendAutoInit}
          onLeave={() => {
            void roomRef.current?.leave();
            setActiveRoom(null);
            setPhase('lobby');
            setPlayers([]);
            setSpectators([]);
            setSelfId('');
            setLobbyInfo({
              hostUserId: '',
              inviteCode: '',
              visibility: 'public',
              maxPlayers: 8,
              autoStartAtMs: 0,
            });
          }}
        />
      )}
        </>
      )}
    </div>
  );
}

/** A player's four resource counts → the canonical `[m, f, v, o]` tuple. */
function toAmount(c: ResourceCounts): ResourceAmount {
  return [c.minerals, c.food, c.vigor, c.obsidian];
}

/** A simple centred status banner for the setup phase (e.g. "waiting on X"). */
function StatusBanner({ title, sub }: { title?: string; sub: string }) {
  return (
    <div className="setup-banner" role="status">
      {title && <strong className="setup-banner-title">{title}</strong>}
      <span className="setup-banner-sub">{sub}</span>
    </div>
  );
}

/** Home-base placement prompt for the player whose turn it is to place. */
function HomePlacementBanner({
  selectedNode,
  onConfirm,
  onCancel,
}: {
  selectedNode: number | null;
  onConfirm: (nodeId: number) => void;
  onCancel: () => void;
}) {
  return (
    <div className="setup-banner" role="status">
      <strong className="setup-banner-title">Place your home base</strong>
      <span className="setup-banner-sub">
        {selectedNode === null
          ? 'Click a highlighted node on an outer tile.'
          : 'Confirm your pick, or click another node to change it.'}
      </span>
      {selectedNode !== null && (
        <div className="setup-banner-actions">
          <button
            type="button"
            className="setup-confirm"
            onClick={() => onConfirm(selectedNode)}
          >
            Confirm home base
          </button>
          <button type="button" className="setup-cancel" onClick={onCancel}>
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

/** The whole starting phase, from this player's point of view. Home-base
 * placement is turn-based (only the current placer picks); once *you've* placed,
 * you assign production right away — in parallel with everyone else — and the
 * game starts when all have confirmed. So the overlay shows: your placement
 * prompt, a "waiting to place" status, your production panel, or a "ready" status. */
function StartupOverlay({
  self,
  players,
  currentPlayerId,
  selfId,
  startingProduction,
  selectedNode,
  onConfirmHome,
  onCancelHome,
  onConfirmProduction,
}: {
  self: LobbyPlayer | undefined;
  players: LobbyPlayer[];
  currentPlayerId: string;
  selfId: string;
  startingProduction: number;
  selectedNode: number | null;
  onConfirmHome: (nodeId: number) => void;
  onCancelHome: () => void;
  onConfirmProduction: (amounts: number[]) => void;
}) {
  if (!self?.homePlaced) {
    if (currentPlayerId === selfId) {
      return (
        <HomePlacementBanner
          selectedNode={selectedNode}
          onConfirm={onConfirmHome}
          onCancel={onCancelHome}
        />
      );
    }
    const current = players.find((p) => p.id === currentPlayerId);
    return (
      <StatusBanner
        title="Get ready"
        sub={`Waiting for ${current?.name ?? 'another player'} to place their home base…`}
      />
    );
  }
  if (!self.productionSet) {
    return (
      <ProductionPanel
        startingProduction={startingProduction}
        onConfirm={onConfirmProduction}
      />
    );
  }
  return (
    <StatusBanner
      title="Ready"
      sub="Waiting for the other players to finish setting up…"
    />
  );
}

/** Bottom bar shown on your turn: the core ("base") actions you may spend an
 * action point on, plus an End-Turn / Pass control. The action list comes
 * straight from the shared `ACTION_KINDS` (with labels/descriptions from
 * `config/actions.ts`), and each button's availability from the shared
 * `canPerformAction` — so e.g. Cycle Market disables when you can't afford it
 * (no Food). A disabled button shows the reason on hover.
 *
 * The bar's border glows (CSS) whenever it's shown — i.e. the moment it becomes
 * your turn — to draw the eye. The header shows how many of your turn's actions
 * you've spent ("action n/total").
 *
 * The End-Turn control's meaning depends on whether you've acted yet:
 *  - You've spent ≥1 action → "End turn": yield your turn but stay in the round.
 *  - You've spent none → there's nothing to end, so it's a Pass (out for the
 *    round). If you'd be the round's first passer, you take the First-Turn marker
 *    (lead next round), so the button says so.
 *
 * Each core action button opens its own flow. */
function TurnActionBar({
  player,
  actionsRemaining,
  actionsPerTurn,
  anyPassed,
  cycleActive,
  buildActive,
  bridgeActive,
  implementActive,
  aimActive,
  effectsActive,
  hasEffectCards,
  usableEffectCount,
  onConstructBuilding,
  onConstructBridge,
  onImplementFortress,
  onCycleMarket,
  onAimBarracks,
  onEffects,
  onEndTurn,
  onPass,
  planningSlot,
  onEndPlanning,
  onQueuePass,
}: {
  player: LobbyPlayer;
  actionsRemaining: number;
  actionsPerTurn: number;
  /** True if any player has already passed this round (so you wouldn't be first). */
  anyPassed: boolean;
  /** True while the Cycle-Market selection flow is open (highlights the button). */
  cycleActive: boolean;
  /** True while the Construct-Building flow is open (highlights the button). */
  buildActive: boolean;
  /** True while the Construct-Bridge flow is open (highlights the button). */
  bridgeActive: boolean;
  /** True while the Implement-Fortress picker is open (highlights the button). */
  implementActive: boolean;
  /** True while the Aim-Barracks flow is open (highlights the button). */
  aimActive: boolean;
  /** True while the Card-Effects tray is open (highlights the button). */
  effectsActive: boolean;
  /** Whether any implemented card carries an effect (shows the tray button). */
  hasEffectCards: boolean;
  /** How many effects are usable right now (the tray button's badge). */
  usableEffectCount: number;
  /** Open the building picker to construct a Scout / Barracks / Mine. */
  onConstructBuilding: () => void;
  /** Start the bridge flow (pick a path to bridge). */
  onConstructBridge: () => void;
  /** Open the Implement-Fortress picker (list of realisable revealed cards). */
  onImplementFortress: () => void;
  /** Open the market sidebar and start choosing cards to cycle. */
  onCycleMarket: () => void;
  /** Start the aim flow (pick a Barracks, a posture, then a target). */
  onAimBarracks: () => void;
  /** Open the Card-Effects tray (your implemented cards' granted actions). */
  onEffects: () => void;
  onEndTurn: () => void;
  onPass: () => void;
  /** Non-null while queue-planning (off-turn): the upcoming-turn slot picked
   * actions are queued into. The bar relabels and swaps End Turn/Pass for a
   * Done button — actions themselves route to the queue upstream. */
  planningSlot?: number | null;
  /** Leave queue-planning mode (the Done button). */
  onEndPlanning?: () => void;
  /** Queue the Pass pseudo-move as this planned turn's last move: after the
   * queued moves play, pass for the round instead of pausing live. */
  onQueuePass?: () => void;
}) {
  const state: PlayerActionState = {
    bank: toAmount(player.resources),
    production: toAmount(player.production),
    fortressCards: player.fortressCards,
  };
  // Nothing done yet → the only exit is to pass (out for the round). Otherwise
  // "End turn" yields the turn but keeps you in the round.
  const isPass = actionsRemaining === actionsPerTurn;
  // First to pass this round → take the First-Turn marker (lead next round).
  const takesMarker = isPass && !anyPassed;
  const endLabel = takesMarker
    ? 'Pass and take first turn marker'
    : isPass
      ? 'Pass until next round'
      : 'End turn';
  const endTitle = takesMarker
    ? 'Pass for the rest of this round and take the First-Turn marker — you lead next round.'
    : isPass
      ? 'Pass for the rest of this round.'
      : 'End your turn; you keep your place in this round.';
  const planning = planningSlot != null;
  return (
    <div
      className={`turn-action-bar${planning ? ' is-planning' : ''}`}
      role="toolbar"
      aria-label={
        planning
          ? 'Planning queued moves — available actions'
          : 'Your turn — available actions'
      }
    >
      <div className="turn-action-bar-head">
        <span className="turn-action-bar-label">
          {planning
            ? `Queueing · ${planningSlot === 1 ? 'next turn' : `in ${planningSlot} turns`}`
            : 'Your turn'}
        </span>
        <span className="turn-action-bar-count">
          {planning
            ? '(picked actions are queued)'
            : `(${actionsRemaining} action${actionsRemaining === 1 ? '' : 's'} left)`}
        </span>
      </div>
      <div className="turn-action-main">
        <div className="turn-action-list">
          {ACTION_KINDS.map((kind) => {
            // Planning ignores every availability gate (cost, board state):
            // queue anything; the auto-play validator is the arbiter.
            const { allowed, reason } = planning
              ? { allowed: true, reason: undefined }
              : canPerformAction(kind, state);
            // Every core action opens its own flow.
            const isCycle = kind === 'cycle_market';
            const isBuild = kind === 'construct_building';
            const isBridge = kind === 'construct_bridge';
            const isImplement = kind === 'implement_fortress';
            const isAim = kind === 'aim_barracks';
            const onClick = isBuild
              ? onConstructBuilding
              : isBridge
                ? onConstructBridge
                : isImplement
                  ? onImplementFortress
                  : isCycle
                    ? onCycleMarket
                    : isAim
                      ? onAimBarracks
                      : undefined;
            const active =
              (isCycle && cycleActive) ||
              (isBuild && buildActive) ||
              (isBridge && bridgeActive) ||
              (isImplement && implementActive) ||
              (isAim && aimActive);
            return (
              <button
                key={kind}
                type="button"
                data-action={kind}
                className={`turn-action${active ? ' is-active' : ''}`}
                disabled={!allowed}
                onClick={onClick}
                title={
                  allowed
                    ? ACTION_DESCRIPTIONS[kind]
                    : (reason ?? ACTION_DESCRIPTIONS[kind])
                }
              >
                {ACTION_LABELS[kind]}
              </button>
            );
          })}
          {hasEffectCards && (
            <button
              type="button"
              data-action="card_effects"
              className={`turn-action${effectsActive ? ' is-active' : ''}`}
              onClick={onEffects}
              title="Use an effect granted by one of your implemented Fortresses."
            >
              Card Effects
              {usableEffectCount > 0 && (
                <span className="turn-action-badge" aria-hidden>
                  {usableEffectCount}
                </span>
              )}
            </button>
          )}
        </div>
        {planning ? (
          <>
            <button
              type="button"
              className="turn-action turn-action-end is-pass"
              onClick={onQueuePass}
              title="End this queued turn with a Pass: after the moves above play, you pass for the round (taking the First-Turn marker if you're first). Without it, the turn pauses on you with your remaining actions."
            >
              Queue Pass
            </button>
            <button
              type="button"
              className="turn-action turn-action-end"
              onClick={onEndPlanning}
              title="Stop queueing moves. They play automatically when the turn comes; you keep any actions they don't spend."
            >
              Done queueing
            </button>
          </>
        ) : (
          <button
            type="button"
            className={`turn-action turn-action-end${isPass ? ' is-pass' : ''}`}
            onClick={isPass ? onPass : onEndTurn}
            title={endTitle}
          >
            {endLabel}
          </button>
        )}
      </div>
    </div>
  );
}

/** The Construct-Building picker, floating just above the turn action bar. Shows
 * Scout / Barracks / Mine with their base cost; a building unaffordable even at
 * base cost is disabled. Picking one enters placement mode (the parent highlights
 * legal nodes and swaps the cursor); the selected building stays highlighted so
 * it's clear what you're placing, and you can switch or cancel. */
function BuildPicker({
  flow,
  resources,
  buildings,
  subs,
  bunBun,
  onToggleRider,
  onSelect,
  onConfirm,
  onCancel,
}: {
  flow: BuildFlow;
  resources: ResourceCounts;
  buildings: Record<BuildingType, BuildingConfig> | undefined;
  /** The player's payment substitutions (Treadmill / Bench). */
  subs: PaymentSubstitutions;
  /** A usable Energized Bun-Bun (double one-time pickups), if held. */
  bunBun: { cardId: string; tokens: number; cap: number } | null;
  /** Arm/disarm Bun-Bun's double on the pending placement. */
  onToggleRider: () => void;
  onSelect: (building: ConstructableBuilding) => void;
  /** Commit the tentatively-selected node (only shown once one is picked). */
  onConfirm: () => void;
  onCancel: () => void;
}) {
  if (!flow) return null;
  const placing = flow.stage === 'place' ? flow.building : null;
  const riderArmed = flow.stage === 'place' && !!flow.doubleOneTime;
  const hasSelection = flow.stage === 'place' && flow.selected !== undefined;
  const bank = toAmount(resources);
  return (
    <div
      className="build-picker"
      role="dialog"
      aria-label="Construct a building"
    >
      <div className="build-picker-head">
        <span className="build-picker-title">
          {placing
            ? `${flow.penaltyCardId ? 'Token-build' : 'Placing'} ${BUILDING_LABELS[placing]}`
            : flow.penaltyCardId
              ? 'Pay Penalty with Token'
              : 'Construct Building'}
        </span>
        <button
          type="button"
          className="build-picker-cancel"
          onClick={onCancel}
          title="Cancel (Esc)"
          aria-label="Cancel"
        >
          ✕
        </button>
      </div>
      <div className="build-picker-options">
        {CONSTRUCTABLE_BUILDINGS.map((b) => {
          const cost = buildings?.[b]?.cost ?? [];
          // Conditional surcharge: a Mine built on a permanent resource node
          // pays this much extra, charged in that node's resource (see
          // buildCostFor / server buildCost). Shown as an "if" hint here since
          // the target node isn't known until placement.
          const permCost = buildings?.[b]?.permanentResourceCost ?? 0;
          const range = buildings?.[b]?.range;
          // Base affordability only (the per-node Vigor penalty is enforced
          // server-side); a base-unaffordable building can never be built.
          const affordable = canAffordWith(bank, cost, subs);
          return (
            <button
              key={b}
              type="button"
              className={`build-option${placing === b ? ' is-selected' : ''}`}
              disabled={!affordable}
              onClick={() => onSelect(b)}
              title={affordable ? BUILDING_BLURBS[b] : 'Not enough resources'}
            >
              <BuildingIcon kind={b} />
              <span className="build-option-name">{BUILDING_LABELS[b]}</span>
              <span className="build-option-cost">
                {RESOURCE_KINDS.map((k, i) =>
                  (cost[i] ?? 0) > 0 ? (
                    <span key={k} className="build-cost-item">
                      <ResourceIcon kind={k} size={36} />
                      <span className="build-cost-num">{cost[i]}</span>
                    </span>
                  ) : null,
                )}
              </span>
              {range ? (
                <span
                  className="build-option-range"
                  title="Build range, in nodes along edges"
                >
                  Range {range.build}
                </span>
              ) : null}
              {permCost > 0 ? (
                <span
                  className="build-option-extra"
                  title="Extra cost when built on a permanent resource node — charged in that node's own resource (shown as ?)"
                >
                  +{permCost}? on permanent resource
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
      {placing && bunBun && (
        <label
          className="build-picker-hint effect-rider-toggle"
          title="Energized Bun-Bun: double the one-time resource this build collects — spends 1 of its 5 uses (only if something is actually collected)."
        >
          <input
            type="checkbox"
            checked={riderArmed}
            onChange={onToggleRider}
          />{' '}
          Double one-time pickup (Bun-Bun {bunBun.tokens}/{bunBun.cap} used)
        </label>
      )}
      {placing && hasSelection && (
        <button type="button" className="setup-confirm" onClick={onConfirm}>
          Confirm {BUILDING_LABELS[placing]}
        </button>
      )}
      <div className="build-picker-hint">
        {placing
          ? hasSelection
            ? 'Confirm to build, or click another highlighted node · Esc to cancel'
            : 'Click a highlighted node to select the spot · Esc to cancel'
          : 'Pick a building, then click a highlighted node'}
      </div>
    </div>
  );
}

/** The Aim-Barracks flow's floating panel. Stage 1: tap one of your (pulsing)
 * unaimed Barracks. Stage 2: choose the posture — Attack (destroy the target at
 * end of turn if attackers outnumber defenders) or Defend (add a defender to a
 * building, yours or another player's). Stage 3: tap a highlighted target. The
 * defense-window variant skips stage 2 (defend-locked). */
function AimPicker({
  flow,
  range,
  onMode,
  onCancel,
}: {
  flow: AimFlow;
  /** The Barracks' ranges from the match config (for the posture hints). */
  range: { attack?: number; defend?: number } | undefined;
  onMode: (mode: AimMode) => void;
  onCancel: () => void;
}) {
  if (!flow) return null;
  const title =
    flow.stage === 'pickBarracks'
      ? flow.sources.size === 0
        ? 'No unaimed Barracks available'
        : flow.defense
          ? 'Defend: tap a Barracks to aim'
          : 'Tap one of your unaimed Barracks'
      : flow.stage === 'mode'
        ? 'Attack or defend?'
        : flow.allowed.size === 0
          ? 'No legal targets in range'
          : flow.mode === 'attack'
            ? 'Tap an enemy building to attack'
            : 'Tap a building to defend';
  return (
    <div className="build-picker" role="dialog" aria-label="Aim Barracks">
      <div className="build-picker-head">
        <span className="build-picker-title">{title}</span>
        <button
          type="button"
          className="build-picker-cancel"
          onClick={onCancel}
          title="Cancel (Esc)"
          aria-label="Cancel"
        >
          ✕
        </button>
      </div>
      {flow.stage === 'mode' && (
        <div className="build-picker-options">
          <button
            type="button"
            className="build-option"
            disabled={!flow.attackable}
            onClick={() => onMode('attack')}
            title={
              flow.attackable
                ? 'Destroyed at end of turn if attackers outnumber its defenders'
                : 'A Barracks inside a Fortress can only defend'
            }
          >
            <span className="build-option-name">⚔️ Attack</span>
            <span className="build-option-range">
              Range {range?.attack ?? '—'}
            </span>
          </button>
          <button
            type="button"
            className="build-option"
            onClick={() => onMode('defend')}
            title="Adds a defender to a building — yours or another player's"
          >
            <span className="build-option-name">🛡️ Defend</span>
            <span className="build-option-range">
              Range {range?.defend ?? '—'}
            </span>
          </button>
        </div>
      )}
      <div className="build-picker-hint">
        {flow.stage === 'mode'
          ? 'An aimed Barracks is committed until the round ends'
          : 'Click a highlighted node · Esc to cancel'}
      </div>
    </div>
  );
}

/** Blind attacker choice required by Dungeon Dice Martyr. Card identities stay
 * hidden; the attacker chooses only a face-down position. */
function MartyrDiscardPrompt({
  pending,
  victimName,
  onChoose,
}: {
  pending: string;
  victimName: string;
  onChoose: (victimId: string, index: number) => void;
}) {
  const [submitted, setSubmitted] = useState(false);
  const [victimId, rawCount] = pending.split('|');
  const count = Math.max(1, Number(rawCount) || 1);
  return (
    <div className="build-picker effects-picker" role="dialog" aria-modal="true">
      <div className="build-picker-head">
        <span className="build-picker-title">Dungeon Dice Martyr</span>
      </div>
      <span className="build-picker-hint">
        Choose one of {victimName}&apos;s face-down fortress cards to discard.
      </span>
      <div className="build-picker-options">
        {Array.from({ length: count }, (_, index) => (
          <button
            key={index}
            type="button"
            className="build-option effect-use"
            disabled={submitted}
            onClick={() => {
              setSubmitted(true);
              onChoose(victimId ?? '', index);
            }}
          >
            {submitted ? 'Discarding...' : `Face-down card ${index + 1}`}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Post-combat Barracks upkeep (pay-to-keep): each of your Barracks that just
 * attacked must be fed its zone's Food upkeep or it starves and is destroyed.
 * One keep-or-starve decision per row; the ending turn stays frozen until every
 * owner has answered (the turn clock backstops a no-show). */
function BarracksUpkeepPrompt({
  entries,
  onFocus,
  onResolve,
}: {
  entries: {
    nodeId: number;
    cost: number;
    zone: string;
    affordable: boolean;
  }[];
  /** Pan the board to the Barracks in question. */
  onFocus: (nodeId: number) => void;
  onResolve: (nodeId: number, keep: boolean) => void;
}) {
  const [sent, setSent] = useState<ReadonlySet<number>>(new Set());
  const answer = (nodeId: number, keep: boolean) => {
    setSent((prev) => new Set(prev).add(nodeId));
    onResolve(nodeId, keep);
  };
  return (
    <div className="build-picker effects-picker" role="dialog" aria-modal="true">
      <div className="build-picker-head">
        <span className="build-picker-title">Barracks Upkeep</span>
      </div>
      <span className="build-picker-hint">
        Your Barracks attacked — pay each one&apos;s Food upkeep to keep it, or
        let it starve.
      </span>
      <div className="effects-list">
        {entries.map(({ nodeId, cost, zone, affordable }) => (
          <div className="effect-row" key={nodeId}>
            <div className="effect-row-head">
              <button
                type="button"
                className="effect-row-name"
                onClick={() => onFocus(nodeId)}
                title="Show this Barracks on the board"
              >
                Barracks — {zone}
              </button>
            </div>
            <div className="effect-row-actions">
              <button
                type="button"
                className="build-option effect-use"
                disabled={sent.has(nodeId) || !affordable}
                title={
                  affordable
                    ? 'Feed the troops that made the assault'
                    : 'Not enough Food'
                }
                onClick={() => answer(nodeId, true)}
              >
                Pay {cost} Food
              </button>
              <button
                type="button"
                className="build-option effect-use"
                disabled={sent.has(nodeId)}
                title="The unfed Barracks is destroyed"
                onClick={() => answer(nodeId, false)}
              >
                Let it starve
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Off-turn banner while YOUR defense window is open: buildings of yours fall
 * when the ending turn resolves. Aim reactive defenders (free of action points),
 * then confirm Done so combat can resolve and the turn pass on. */
function DefenseBar({
  aiming,
  protecting,
  canProtect,
  onAim,
  onProtect,
  onDone,
}: {
  aiming: boolean;
  /** True while the protect-a-Scout pick is on the board (highlights button). */
  protecting: boolean;
  /** The player holds a usable Godspeed charge AND a Scout is about to fall. */
  canProtect: boolean;
  onAim: () => void;
  /** Start the reactive protect flow (free during the window). */
  onProtect: () => void;
  onDone: () => void;
}) {
  return (
    <div className="turn-action-bar" role="toolbar" aria-label="Defense window">
      <div className="turn-action-bar-head">
        <span className="turn-action-bar-label">Under attack!</span>
        <span className="turn-action-bar-count">
          aim defenders before the turn resolves
        </span>
      </div>
      <div className="turn-action-main">
        <div className="turn-action-list">
          <button
            type="button"
            className={`turn-action${aiming ? ' is-active' : ''}`}
            onClick={onAim}
            title="Aim an unaimed Barracks to defend an attacked building — free during a defense window"
          >
            Aim Defender
          </button>
          {canProtect && (
            <button
              type="button"
              className={`turn-action${protecting ? ' is-active' : ''}`}
              onClick={onProtect}
              title="Godspeed Machine: place a token to protect a Scout that is about to be destroyed — free during a defense window"
            >
              Protect a Scout
            </button>
          )}
        </div>
        <button
          type="button"
          className="turn-action turn-action-end"
          onClick={onDone}
          title="Finish defending — combat resolves once every attacked player is done"
        >
          Done
        </button>
      </div>
    </div>
  );
}

/** Production allocation panel: distribute `startingProduction` points across the
 * four resources (total must equal the budget) then Confirm. Shown to a player
 * once they've placed their home base; players allocate in parallel. */
function ProductionPanel({
  startingProduction,
  onConfirm,
}: {
  startingProduction: number;
  onConfirm: (amounts: number[]) => void;
}) {
  // Allocation as a [minerals, food, vigor, obsidian] tuple (RESOURCE_KINDS order).
  const [alloc, setAlloc] = useState<number[]>([0, 0, 0, 0]);
  const used = alloc.reduce((sum, n) => sum + n, 0);
  const remaining = startingProduction - used;
  const bump = (i: number, d: number) =>
    setAlloc((a) => a.map((n, j) => (j === i ? n + d : n)));

  return (
    <div
      className="production-panel"
      role="dialog"
      aria-label="Assign production"
    >
      <strong className="setup-banner-title">Assign your production</strong>
      <span className="setup-banner-sub">
        Distribute {startingProduction} production · {remaining} left
      </span>
      <div className="production-rows">
        {RESOURCE_KINDS.map((kind, i) => (
          <div className="production-row" key={kind}>
            <span className="production-res">
              <ResourceIcon kind={kind} size={16} /> {RESOURCE_LABELS[kind]}
            </span>
            <div className="production-stepper">
              <button
                type="button"
                aria-label={`Less ${RESOURCE_LABELS[kind]}`}
                disabled={alloc[i] === 0}
                onClick={() => bump(i, -1)}
              >
                −
              </button>
              <span className="production-count">{alloc[i]}</span>
              <button
                type="button"
                aria-label={`More ${RESOURCE_LABELS[kind]}`}
                disabled={remaining === 0}
                onClick={() => bump(i, 1)}
              >
                +
              </button>
            </div>
          </div>
        ))}
      </div>
      <button
        type="button"
        className="setup-confirm"
        disabled={remaining !== 0 || startingProduction === 0}
        onClick={() => onConfirm(alloc)}
      >
        Confirm production
      </button>
    </div>
  );
}

/** The Implement-Fortress picker: the revealed cards you can realise right now
 * (your free buildings match the pattern somewhere on the board). Hovering a
 * card highlights where it lands; clicking picks it — disabled if you can't
 * afford its Obsidian. Live play then moves on to placing it on the board;
 * planning sends the queued action directly, so a card whose implement effect
 * consumes a choice (Artifact of Atrocity's "gain 1 of any resource") asks for
 * it here first — unless `deferChoice` (live play, where the placement stage
 * asks at Confirm instead). Floats above the turn action bar, like the Build
 * picker. */
function ImplementPicker({
  cards,
  deferChoice,
  onPick,
  onHover,
  onCancel,
}: {
  cards: ImplementCandidate[];
  deferChoice: boolean;
  onPick: (
    cardId: string,
    kind: FortressKind,
    choice?: { resource: string },
  ) => void;
  onHover: (nodeIds: number[] | null) => void;
  onCancel: () => void;
}) {
  /** Card awaiting its resource pick, or null. */
  const [choosing, setChoosing] = useState<ImplementCandidate | null>(null);
  return (
    <div
      className="build-picker"
      role="dialog"
      aria-label="Implement a fortress"
      onMouseLeave={() => onHover(null)}
    >
      <div className="build-picker-head">
        <span className="build-picker-title">
          {choosing ? `${choosing.name} — gain which resource?` : 'Implement Fortress'}
        </span>
        <button
          type="button"
          className="build-picker-cancel"
          onClick={choosing ? () => setChoosing(null) : onCancel}
          title={choosing ? 'Back' : 'Cancel (Esc)'}
          aria-label={choosing ? 'Back' : 'Cancel'}
        >
          ✕
        </button>
      </div>
      {choosing ? (
        <div className="build-picker-options">
          {RESOURCE_KINDS.map((kind) => (
            <button
              key={kind}
              type="button"
              className="build-option"
              onClick={() =>
                onPick(choosing.cardId, choosing.kind, { resource: kind })
              }
              title={`Gain 1 ${RESOURCE_LABELS[kind]}`}
            >
              <ResourceIcon kind={kind} size={36} />
              <span className="build-option-name">{RESOURCE_LABELS[kind]}</span>
            </button>
          ))}
        </div>
      ) : (
        <div className="build-picker-options">
          {cards.length === 0 ? (
            <span style={{ padding: '8px 4px', opacity: 0.7 }}>
              No revealed fortress matches your buildings.
            </span>
          ) : (
            cards.map((c) => (
              <button
                key={`${c.kind}:${c.cardId}`}
                type="button"
                className="build-option"
                disabled={!c.affordable}
                onClick={() =>
                  !deferChoice &&
                  effectFor(c.cardId)?.implementChoice === 'resource'
                    ? setChoosing(c)
                    : onPick(c.cardId, c.kind)
                }
                onMouseEnter={() => onHover(c.nodeIds)}
                title={c.affordable ? c.name : 'Not enough Obsidian'}
              >
                <span className="build-option-name">
                  {c.name}
                  {c.fromHand && (
                    <span
                      className="effect-chip"
                      title="From your face-down hand (1 Obsidian cheaper)"
                    >
                      Hand
                    </span>
                  )}
                </span>
                <span className="build-option-cost">
                  <span className="build-cost-item">
                    <ResourceIcon kind="obsidian" size={36} />
                    <span className="build-cost-num">{c.cost[3] ?? 0}</span>
                  </span>
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

/** The Implement-Fortress placement bar (live play, after a card is picked):
 * every node the pattern can land on pulses on the board; tapping one selects
 * a concrete placement there (gold rings — tapping it again, or Rotate, cycles
 * the other placements over that node) and Confirm implements it. A card whose
 * implement effect consumes a resource choice asks for it on Confirm. Floats
 * above the turn action bar, like the Build picker. */
function ImplementPlacementBar({
  card,
  selected,
  onRotate,
  onConfirm,
  onBack,
}: {
  card: ImplementCandidate;
  selected: { nodeId: number; options: number[]; pos: number } | null;
  onRotate: () => void;
  onConfirm: (choice?: { resource: string }) => void;
  onBack: () => void;
}) {
  /** Waiting on the card's resource pick (after Confirm)? */
  const [choosing, setChoosing] = useState(false);
  const needsChoice = effectFor(card.cardId)?.implementChoice === 'resource';
  return (
    <div
      className="build-picker"
      role="dialog"
      aria-label={`Place ${card.name}`}
    >
      <div className="build-picker-head">
        <span className="build-picker-title">
          {choosing
            ? `${card.name} — gain which resource?`
            : selected
              ? `Place ${card.name}: location ${selected.pos + 1} of ${selected.options.length} at this spot`
              : `Place ${card.name}: tap a highlighted spot`}
        </span>
        <button
          type="button"
          className="build-picker-cancel"
          onClick={choosing ? () => setChoosing(false) : onBack}
          title="Back (Esc)"
          aria-label="Back"
        >
          ✕
        </button>
      </div>
      {choosing ? (
        <div className="build-picker-options">
          {RESOURCE_KINDS.map((kind) => (
            <button
              key={kind}
              type="button"
              className="build-option"
              onClick={() => onConfirm({ resource: kind })}
              title={`Gain 1 ${RESOURCE_LABELS[kind]}`}
            >
              <ResourceIcon kind={kind} size={36} />
              <span className="build-option-name">{RESOURCE_LABELS[kind]}</span>
            </button>
          ))}
        </div>
      ) : (
        <div className="build-picker-options">
          <button
            type="button"
            className="build-option"
            disabled={!selected || selected.options.length < 2}
            onClick={onRotate}
            title={
              !selected
                ? 'Pick a spot first'
                : selected.options.length > 1
                  ? 'Show the next placement over this spot'
                  : 'Only one placement over this spot'
            }
          >
            <span className="build-option-name">⟳ Rotate</span>
          </button>
          <button
            type="button"
            className="build-option"
            disabled={!selected}
            onClick={() => (needsChoice ? setChoosing(true) : onConfirm())}
            title={
              selected ? `Implement ${card.name} here` : 'Pick a spot first'
            }
          >
            <span className="build-option-name">✓ Confirm</span>
          </button>
        </div>
      )}
    </div>
  );
}

/** One row of the effects tray: the card's name + live token/exhausted state,
 * with a Use button per effect the card grants. Passive-only cards render
 * their rule chips instead of a button. */
function EffectCardRow({
  card,
  view,
  player,
  context,
  onStart,
}: {
  card: FortressCard | undefined;
  view: FortressCardView;
  player: LobbyPlayer;
  context: EffectContext;
  onStart: (cardId: string, kind: SpecialActionKind) => void;
}) {
  const fx = effectFor(view.cardId);
  const target = tokenTargetOf(view.cardId);
  const name = card?.name ?? view.cardId;
  return (
    <div className="effect-row">
      <div className="effect-row-head">
        <span className="effect-row-name" title={card?.description}>
          {name}
        </span>
        {(view.tokens > 0 || Number.isFinite(target)) && (
          <span className="effect-chip" title="Tokens on this fortress">
            ● {view.tokens}
            {Number.isFinite(target) ? `/${target}` : ''}
          </span>
        )}
        {view.disabled && (
          <span className="effect-chip is-off" title="Effect already used">
            Exhausted
          </span>
        )}
      </div>
      <div className="effect-row-actions">
        {(fx?.grants ?? []).map((kind) => {
          const { allowed, reason } = effectAvailability(
            player,
            view.cardId,
            kind,
            context,
          );
          return (
            <button
              key={kind}
              type="button"
              className="build-option effect-use"
              disabled={!allowed}
              onClick={() => onStart(view.cardId, kind)}
              title={reason ?? card?.description}
            >
              {EFFECT_LABELS[kind] ?? kind}
            </button>
          );
        })}
        {(fx?.grants?.length ?? 0) === 0 && (
          <span className="effect-passive" title={card?.description}>
            Passive — always on
          </span>
        )}
      </div>
    </div>
  );
}

/** The Card-Effects tray, floating above the turn action bar like the Build
 * picker. The list stage shows every implemented card that carries an effect;
 * sub-stages walk one effect's inputs (confirm / board pick / token adjust /
 * market pick). Board-pick stages keep the tray up as a hint banner — the
 * actual picking happens on the board. */
function EffectsPicker({
  flow,
  player,
  cardsById,
  markets,
  context,
  bunBun,
  onStart,
  onConfirm,
  onConfirmUntap,
  onConfirmMove,
  onToggleMoveRider,
  onStartReorder,
  onMoveReorder,
  onConfirmReorder,
  onPickClaim,
  onAdjustToken,
  onLock,
  onFreeCycle,
  onBack,
  onCancel,
}: {
  flow: NonNullable<EffectsFlow>;
  player: LobbyPlayer;
  cardsById: ReadonlyMap<string, FortressCard>;
  markets: MarketView;
  context: EffectContext;
  bunBun: { cardId: string; tokens: number; cap: number } | null;
  onStart: (cardId: string, kind: SpecialActionKind) => void;
  onConfirm: () => void;
  onConfirmUntap: () => void;
  onConfirmMove: () => void;
  onToggleMoveRider: () => void;
  onStartReorder: (market: FortressKind) => void;
  onMoveReorder: (index: number, delta: -1 | 1) => void;
  onConfirmReorder: () => void;
  onPickClaim: (kind: string) => void;
  onAdjustToken: (targetCardId: string, delta: 1 | -1) => void;
  onLock: (market: FortressKind, marketCardId: string, locked: boolean) => void;
  onFreeCycle: (market: FortressKind, index: number) => void;
  onBack: () => void;
  onCancel: () => void;
}) {
  const title =
    flow.stage === 'list'
      ? 'Card Effects'
      : (cardsById.get(flow.cardId)?.name ?? 'Card Effect');
  const withEffects = player.fortressCards.filter((fc) => {
    const fx = effectFor(fc.cardId);
    return (fx?.grants?.length ?? 0) > 0 || (fx?.passives?.length ?? 0) > 0;
  });
  const lockedTotal = markets.regular.locked.length + markets.win.locked.length;
  const marketRows: { kind: FortressKind; label: string; pile: MarketPile }[] = [
    { kind: 'REGULAR', label: 'Regular', pile: markets.regular },
    { kind: 'win', label: 'Win', pile: markets.win },
  ];
  return (
    <div className="build-picker effects-picker" role="dialog" aria-label={title}>
      <div className="build-picker-head">
        {flow.stage !== 'list' &&
          flow.stage !== 'reorderDeck' &&
          flow.stage !== 'claim' && (
          <button
            type="button"
            className="build-picker-cancel"
            onClick={onBack}
            title="Back (Esc)"
            aria-label="Back"
          >
            ‹
          </button>
        )}
        <span className="build-picker-title">{title}</span>
        {flow.stage !== 'reorderDeck' && flow.stage !== 'claim' && <button
          type="button"
          className="build-picker-cancel"
          onClick={onCancel}
          title="Close"
          aria-label="Close"
        >
          ✕
        </button>}
      </div>

      {flow.stage === 'list' && (
        <div className="effects-list">
          {withEffects.length === 0 ? (
            <span className="build-picker-hint">
              None of your fortresses carries an effect.
            </span>
          ) : (
            withEffects.map((fc, i) => (
              <EffectCardRow
                key={`${fc.cardId}-${i}`}
                card={cardsById.get(fc.cardId)}
                view={fc}
                player={player}
                context={context}
                onStart={onStart}
              />
            ))
          )}
        </div>
      )}

      {flow.stage === 'confirm' && (
        <div className="effects-confirm">
          <p className="effects-blurb">
            {flow.kind === 'double_banked_resources'
              ? 'Double every resource in your bank. Once per game — the card flips afterward. Costs 1 action.'
              : 'End the round immediately for everyone and take the First-Turn marker. Adds a token to the card.'}
          </p>
          <div className="effects-confirm-actions">
            <button type="button" className="build-option" onClick={onConfirm}>
              Confirm
            </button>
            <button type="button" className="build-option" onClick={onBack}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {flow.stage === 'pickScout' && (
        <span className="build-picker-hint">
          {flow.allowed.size > 0
            ? 'Tap one of your highlighted Scouts to protect it for this round.'
            : 'No unprotected Scouts — nothing to pick.'}
        </span>
      )}

      {flow.stage === 'replicate' && (
        <span className="build-picker-hint">
          {flow.allowed.size > 0
            ? 'Tap a highlighted anchor to place the same bridge fortress in its original energy level. This costs no resources or action point.'
            : 'No empty placement fits in the original energy level.'}
        </span>
      )}

      {flow.stage === 'untap' && (
        <div className="effects-confirm">
          <span className="build-picker-hint">
            Tap up to {flow.limit} aimed Barracks to stand back upright (
            {flow.selected.size}/{flow.limit} selected).
          </span>
          <div className="effects-confirm-actions">
            <button
              type="button"
              className="build-option"
              disabled={flow.selected.size === 0}
              onClick={onConfirmUntap}
            >
              Ready {flow.selected.size || ''} Barracks
            </button>
          </div>
        </div>
      )}

      {flow.stage === 'moveFortress' && (
        <div className="effects-confirm">
          <span className="build-picker-hint">
            Tap {flow.limit} adjacent destination nodes within your attack range
            ({flow.selected.size}/{flow.limit} picked). Moving costs the energy
            penalty of the highest zone entered.
          </span>
          {bunBun && (
            <button
              type="button"
              className={`build-option${flow.doubleOneTime ? ' is-selected' : ''}`}
              onClick={onToggleMoveRider}
            >
              {flow.doubleOneTime
                ? 'Double pickups armed'
                : 'Double one-time pickups'}
            </button>
          )}
          <div className="effects-confirm-actions">
            <button
              type="button"
              className="build-option"
              disabled={flow.selected.size !== flow.limit}
              onClick={onConfirmMove}
            >
              Move here
            </button>
          </div>
        </div>
      )}

      {flow.stage === 'claim' && (
        <div className="effects-confirm">
          <span className="build-picker-hint">
            Pick two resources to gain ({flow.picked.length}/2
            {flow.picked.length > 0 ? ` — ${flow.picked.join(', ')}` : ''}).
          </span>
          <div className="build-picker-options">
            {RESOURCE_KINDS.map((kind) => (
              <button
                key={kind}
                type="button"
                className="build-option effect-use"
                disabled={flow.submitted}
                onClick={() => onPickClaim(kind)}
              >
                <ResourceIcon kind={kind} size={36} />
                {RESOURCE_LABELS[kind]}
              </button>
            ))}
          </div>
        </div>
      )}

      {flow.stage === 'adjustToken' && (
        <div className="effects-list">
          <span className="build-picker-hint">
            Add or remove one token on one of your fortresses. Costs 1 action;
            the Tokenizer flips until next round.
          </span>
          {player.fortressCards.map((fc, i) => {
            const cap = tokenCapOf(fc.cardId);
            const name = cardsById.get(fc.cardId)?.name ?? fc.cardId;
            return (
              <div className="effect-row" key={`${fc.cardId}-${i}`}>
                <div className="effect-row-head">
                  <span className="effect-row-name">{name}</span>
                  <span className="effect-chip">
                    ● {fc.tokens}
                    {Number.isFinite(cap) ? `/${cap}` : ''}
                  </span>
                </div>
                <div className="effect-row-actions">
                  <button
                    type="button"
                    className="build-option effect-use"
                    disabled={fc.tokens >= cap}
                    onClick={() => onAdjustToken(fc.cardId, 1)}
                    title={
                      fc.tokens >= cap ? 'At its token cap.' : 'Add a token'
                    }
                  >
                    +1
                  </button>
                  <button
                    type="button"
                    className="build-option effect-use"
                    disabled={fc.tokens <= 0}
                    onClick={() => onAdjustToken(fc.cardId, -1)}
                    title={fc.tokens <= 0 ? 'No tokens to remove.' : 'Remove a token'}
                  >
                    −1
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {flow.stage === 'reorderMarket' && (
        <div className="effects-confirm">
          <span className="build-picker-hint">
            Choose the row to combine and shuffle. You will privately arrange
            its top ten cards.
          </span>
          <div className="effects-confirm-actions">
            <button
              type="button"
              className="build-option"
              onClick={() => onStartReorder('REGULAR')}
            >
              Regular
            </button>
            <button
              type="button"
              className="build-option"
              onClick={() => onStartReorder('win')}
            >
              Win
            </button>
          </div>
        </div>
      )}

      {flow.stage === 'reorderDeck' && (
        <div className="effects-list">
          <span className="build-picker-hint">
            Arrange the top card first. This private prompt must be completed
            before another action.
          </span>
          {flow.order.map((id, index) => (
            <div className="effect-row" key={id}>
              <div className="effect-row-head">
                <span className="effect-chip">{index + 1}</span>
                <span className="effect-row-name">
                  {cardsById.get(id)?.name ?? id}
                </span>
              </div>
              <div className="effect-row-actions">
                <button
                  type="button"
                  className="build-option effect-use"
                  disabled={flow.submitted || index === 0}
                  onClick={() => onMoveReorder(index, -1)}
                  aria-label="Move up"
                >
                  â†‘
                </button>
                <button
                  type="button"
                  className="build-option effect-use"
                  disabled={flow.submitted || index === flow.order.length - 1}
                  onClick={() => onMoveReorder(index, 1)}
                  aria-label="Move down"
                >
                  â†“
                </button>
              </div>
            </div>
          ))}
          <button
            type="button"
            className="build-option"
            disabled={flow.submitted}
            onClick={onConfirmReorder}
          >
            {flow.submitted ? 'Saving...' : 'Confirm order'}
          </button>
        </div>
      )}

      {flow.stage === 'market' && (
        <div className="effects-list">
          <span className="build-picker-hint">
            {flow.kind === 'set_fortress_lock'
              ? `Lock a face-up card so it can't be cycled (max 2 locked; ${lockedTotal}/2 in use). Costs 1 action.`
              : 'Cycle one face-up card out of the market — free of cost and action.'}
          </span>
          {marketRows.map(({ kind, label, pile }) =>
            pile.cards.map((id, index) => {
              const name = cardsById.get(id)?.name ?? id;
              const isLocked = pile.locked.includes(id);
              return (
                <div className="effect-row" key={`${kind}:${id}`}>
                  <div className="effect-row-head">
                    <span className="effect-row-name">{name}</span>
                    <span className="effect-chip">{label}</span>
                    {isLocked && <span className="effect-chip is-off">Locked</span>}
                  </div>
                  <div className="effect-row-actions">
                    {flow.kind === 'set_fortress_lock' ? (
                      <button
                        type="button"
                        className="build-option effect-use"
                        disabled={!isLocked && lockedTotal >= 2}
                        onClick={() => onLock(kind, id, !isLocked)}
                        title={
                          !isLocked && lockedTotal >= 2
                            ? 'At most 2 cards may be locked.'
                            : undefined
                        }
                      >
                        {isLocked ? 'Unlock' : 'Lock'}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="build-option effect-use"
                        disabled={isLocked}
                        onClick={() => onFreeCycle(kind, index)}
                        title={isLocked ? 'Locked cards can’t be cycled.' : undefined}
                      >
                        Cycle out
                      </button>
                    )}
                  </div>
                </div>
              );
            }),
          )}
        </div>
      )}
    </div>
  );
}

/** A player's setup-phase status — what they're doing in the starting phase. */
function setupStatus(
  p: LobbyPlayer,
  currentPlayerId: string,
): { label: string; cls: string } {
  if (p.productionSet) return { label: 'Ready', cls: 'is-ready' };
  if (p.homePlaced) return { label: 'Assigning production', cls: 'is-active' };
  if (p.id === currentPlayerId)
    return { label: 'Placing home base', cls: 'is-active' };
  return { label: 'Waiting', cls: 'is-waiting' };
}

/** The current player's turn countdown, shown beside their turn flag when the
 * match has a per-turn limit. `mm:ss`, flagged `is-low` under 10s. Display only —
 * the server is authoritative on when the turn actually auto-ends. */
function TurnTimer({ remainingMs }: { remainingMs: number }) {
  const totalSec = Math.ceil(remainingMs / 1000);
  const mins = Math.floor(totalSec / 60);
  const secs = totalSec % 60;
  return (
    <span
      className={`turn-timer${totalSec <= 10 ? ' is-low' : ''}`}
      title="Time left this turn"
    >
      ⏱ {mins}:{secs.toString().padStart(2, '0')}
    </span>
  );
}

/** Multi-user badge in the left sidebar's expanded header, just left of the
 * collapse arrow: shows how many people are watching the match. Hovering opens
 * a popup listing them by name; clicking pins it (click again — or anywhere
 * outside — to close). The popup renders `position: fixed` from the badge's
 * screen rect so the sidebar's `overflow: hidden` can't clip it. */
function SpectatorBadge({ spectators }: { spectators: SpectatorView[] }) {
  const btnRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState(false);
  const [pinned, setPinned] = useState(false);
  const open = hovered || pinned;

  // A pinned popup closes on any press outside the badge and the popup.
  useEffect(() => {
    if (!pinned) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || popupRef.current?.contains(t)) return;
      setPinned(false);
    };
    window.addEventListener('pointerdown', onDown);
    return () => window.removeEventListener('pointerdown', onDown);
  }, [pinned]);

  const rect = open ? btnRef.current?.getBoundingClientRect() : undefined;
  const count = spectators.length;
  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={`sidebar-spectators${pinned ? ' is-active' : ''}`}
        title={`${count} spectator${count === 1 ? '' : 's'}`}
        aria-label={`${count} spectator${count === 1 ? '' : 's'}`}
        aria-expanded={open}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={() => setPinned((p) => !p)}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.8}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
        <span className="sidebar-spectators-count">{count}</span>
      </button>
      {open &&
        rect &&
        // Portalled to <body> like the detail popovers: the sidebar's
        // backdrop-filter makes it the containing block for fixed-position
        // descendants, so an in-tree popup would be clipped by its overflow.
        createPortal(
          <div
            ref={popupRef}
            className="spectator-popup"
            style={{ left: rect.left, top: rect.bottom + 8 }}
            role="dialog"
            aria-label="Spectators"
          >
            <div className="spectator-popup-title">Spectators</div>
            {count === 0 ? (
              <p className="spectator-popup-empty">No one is watching.</p>
            ) : (
              <ul className="spectator-popup-list">
                {spectators.map((s) => (
                  <li key={s.id}>{s.name}</li>
                ))}
              </ul>
            )}
          </div>,
          document.body,
        )}
    </>
  );
}

interface PlayerSidebarProps {
  players: LobbyPlayer[];
  round: number | null;
  turn: number | null;
  /** Current match phase — drives the setup-status display vs. round/turn. */
  phase: string;
  currentPlayerId: string;
  /** Per-turn time limit (ms) from the match config, or undefined when untimed.
   * When set, the current player's row shows a live countdown. */
  timePerTurnMs?: number;
  selfId: string;
  handlers: SelectionHandlers;
  /** Registers each player row's live element (by id) so an action-notice popup
   * can anchor beside it; called with `null` on unmount to drop the entry. */
  registerRow: (id: string, el: HTMLElement | null) => void;
  /** Full move history (oldest first); shown when `historyMode` is on. */
  history: HistoryEntryView[];
  /** When true, the sidebar lists the Turn History instead of the player roster. */
  historyMode: boolean;
  /** Match-pinned fortress catalogue (id + name indexes). */
  cardsById: ReadonlyMap<string, FortressCard>;
  cardsByName: ReadonlyMap<string, FortressCard>;
  /** Toggle between the player roster and the Turn History list. */
  onToggleHistory: () => void;
  /** Pan/zoom the board to a history entry's node (when it has one). */
  onFocusNode: (nodeId: number) => void;
  collapsed: boolean;
  onToggle: () => void;
  /** Everyone watching this match — shown behind the header's multi-user
   * badge (hover/click lists them). */
  spectators: SpectatorView[];
  /** Our own roster entry — carries the private queued moves. */
  self?: LobbyPlayer;
  /** Active queue-planning slot (1..MAX_QUEUE_TURNS), or null. */
  planSlot: number | null;
  /** Enter/leave queue-planning for a slot (null leaves). */
  onPlanSlot: (slot: number | null) => void;
  /** Remove one queued move (`index` within `slot`). */
  onUnqueueMove: (slot: number, index: number) => void;
  /** The queued move whose preview markers are showing, or null. */
  queuedSel: { slot: number; index: number } | null;
  /** Select a queued move (shows its board/market markers); null deselects. */
  onSelectQueued: (sel: { slot: number; index: number } | null) => void;
  /** We hold the First-Turn marker (and no round-start window is live) — the
   * round-start cycle choice may be queued for the next round. */
  expectedFirst: boolean;
  /** The round-start queue picker is open in the market sidebar. */
  rsQueueActive: boolean;
  /** Open/close the round-start queue picker. */
  onToggleRsQueue: () => void;
  /** Remove the queued round-start cycle decision. */
  onClearQueuedRoundStart: () => void;
}

/** Left sidebar: round/turn (or setup) header + every player. Shown through both
 * the starting phase and live play. Collapsible to a thin edge handle. */
function PlayerSidebar({
  players,
  round,
  turn,
  phase,
  currentPlayerId,
  timePerTurnMs,
  selfId,
  handlers,
  registerRow,
  history,
  historyMode,
  cardsById,
  cardsByName,
  onToggleHistory,
  onFocusNode,
  collapsed,
  onToggle,
  spectators,
  self,
  planSlot,
  onPlanSlot,
  onUnqueueMove,
  queuedSel,
  onSelectQueued,
  expectedFirst,
  rsQueueActive,
  onToggleRsQueue,
  onClearQueuedRoundStart,
}: PlayerSidebarProps) {
  const isSetup = phase === 'setup';
  // Drive the per-turn countdown: re-render every second while the match is timed
  // and live, so the current player's remaining time ticks down. Untimed matches
  // (or setup) keep this idle. `now` is read against each row's `turnStartedMs`.
  const timed = !!timePerTurnMs && phase === 'playing';
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!timed) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [timed]);
  return (
    <aside
      className={`sidebar sidebar-left${collapsed ? ' is-collapsed' : ''}`}
    >
      <div className="sidebar-header">
        <button
          type="button"
          className={`sidebar-action${historyMode ? ' is-active' : ''}`}
          onClick={onToggleHistory}
          title={
            historyMode
              ? 'Show the player roster'
              : 'Show every action taken this match'
          }
          aria-pressed={historyMode}
        >
          {historyMode ? '👥 All Players' : '🕑 Turn History'}
        </button>
        <div className="sidebar-header-right">
          {!collapsed && <SpectatorBadge spectators={spectators} />}
          <button
            type="button"
            className="sidebar-toggle"
            onClick={onToggle}
            title={collapsed ? 'Show players' : 'Hide players'}
            aria-label={
              collapsed ? 'Expand players panel' : 'Collapse players panel'
            }
          >
            {collapsed ? '›' : '‹'}
          </button>
        </div>
      </div>
      {collapsed ? (
        <div className="sidebar-rail">
          {!isSetup && (
            <div
              className="rail-round"
              title={`Round ${round ?? '—'}, Turn ${turn ?? '—'}`}
            >
              <span>R{round ?? '—'}</span>
              <span>T{turn ?? '—'}</span>
            </div>
          )}
          {players.map((p) => (
            <button
              key={p.id}
              type="button"
              ref={(el) => registerRow(p.id, el)}
              className="rail-player"
              data-cursor-target={`player:${p.id}`}
              title={p.name}
              aria-label={`${p.name}${p.id === selfId ? ' (you)' : ''}`}
              onMouseEnter={(e) =>
                handlers.onPreview(
                  { kind: 'player', player: p },
                  e.currentTarget,
                )
              }
              onMouseLeave={() =>
                handlers.onPreviewEnd({ kind: 'player', player: p })
              }
              onClick={(e) =>
                handlers.onPin({ kind: 'player', player: p }, e.currentTarget)
              }
            >
              <PlayerIcon
                name={p.name}
                seat={p.seat}
                connected={p.connected}
                size={26}
                current={p.id === currentPlayerId}
              />
            </button>
          ))}
        </div>
      ) : (
        <div className="sidebar-body">
        {historyMode ? (
          <TurnHistoryList
            history={history}
            onFocusNode={onFocusNode}
            handlers={handlers}
            cardsByName={cardsByName}
          />
        ) : (
          <>
            <div className="round-header">
              {isSetup ? (
                <span className="round-num">Starting phase</span>
              ) : (
                <>
                  <span className="round-num">Round {round ?? '—'}</span>
                  <span className="turn-num">Turn {turn ?? '—'}</span>
                </>
              )}
            </div>
            <ul className="player-list">
              {players.map((p) => {
                const isCurrent = p.id === currentPlayerId;
                const status = isSetup ? setupStatus(p, currentPlayerId) : null;
                return (
                  <li
                    key={p.id}
                    ref={(el) => registerRow(p.id, el)}
                    className={`player-row is-selectable${isCurrent ? ' is-current' : ''}`}
                    data-cursor-target={`player:${p.id}`}
                    onMouseEnter={(e) =>
                      handlers.onPreview(
                        { kind: 'player', player: p },
                        e.currentTarget,
                      )
                    }
                    onMouseLeave={() =>
                      handlers.onPreviewEnd({ kind: 'player', player: p })
                    }
                    onClick={(e) =>
                      handlers.onPin(
                        { kind: 'player', player: p },
                        e.currentTarget,
                      )
                    }
                  >
                    <div className="player-top">
                      <PlayerIcon
                        name={p.name}
                        seat={p.seat}
                        connected={p.connected}
                      />
                      <span className="player-name">
                        {p.name}
                        {p.id === selfId ? ' (you)' : ''}
                      </span>
                      {status ? (
                        <span className={`setup-status ${status.cls}`}>
                          {status.label}
                        </span>
                      ) : (
                        <>
                          {isCurrent && (
                            <span className="turn-flag">● turn</span>
                          )}
                          {isCurrent && timed && p.turnStartedMs > 0 && (
                            <TurnTimer
                              remainingMs={Math.max(
                                0,
                                timePerTurnMs! - (now - p.turnStartedMs),
                              )}
                            />
                          )}
                          {p.passed && (
                            <span className="passed-flag">passed</span>
                          )}
                        </>
                      )}
                    </div>
                    <div className="player-stats">
                      <ResourceRow
                        label="Bank"
                        resources={p.resources}
                        oneTime
                      />
                      <ResourceRow
                        label="Production"
                        resources={p.production}
                      />
                    </div>
                    <FortressCards
                      faceUp={p.fortressCards}
                      faceDown={p.hiddenCards}
                      handlers={handlers}
                      playerId={p.id}
                      cardsById={cardsById}
                    />
                  </li>
                );
              })}
              {players.length === 0 && (
                <li className="player-row empty">No players</li>
              )}
            </ul>
          </>
        )}
        </div>
      )}
      {!collapsed && phase === 'playing' && self && (
        <QueuePanel
          self={self}
          isMyTurn={currentPlayerId === selfId}
          planSlot={planSlot}
          onPlanSlot={onPlanSlot}
          onUnqueueMove={onUnqueueMove}
          queuedSel={queuedSel}
          onSelectQueued={onSelectQueued}
          cardsById={cardsById}
          expectedFirst={expectedFirst}
          rsQueueActive={rsQueueActive}
          onToggleRsQueue={onToggleRsQueue}
          onClearQueuedRoundStart={onClearQueuedRoundStart}
        />
      )}
    </aside>
  );
}

/** Sticky footer of the left sidebar: your privately queued moves, grouped by
 * upcoming turn. Turn slots unlock progressively — "Next turn" is always
 * offered, and each further turn (up to MAX_QUEUE_TURNS) appears only once the
 * turn before it holds a move. The + button enters planning mode — the normal
 * action bar/pickers open (off-turn) and every picked action lands in that
 * turn's queue instead of playing live; once a turn holds moves, the list of
 * what's queued replaces the + button. Clicking a queued move SELECTS it,
 * revealing its preview markers (board ghost / market badge) and its Cancel
 * control; clicking again deselects. Only you (and the server) can see any of
 * this; opponents' clients never receive the queue. */
function QueuePanel({
  self,
  isMyTurn,
  planSlot,
  onPlanSlot,
  onUnqueueMove,
  queuedSel,
  onSelectQueued,
  cardsById,
  expectedFirst,
  rsQueueActive,
  onToggleRsQueue,
  onClearQueuedRoundStart,
}: {
  self: LobbyPlayer;
  isMyTurn: boolean;
  planSlot: number | null;
  onPlanSlot: (slot: number | null) => void;
  onUnqueueMove: (slot: number, index: number) => void;
  queuedSel: { slot: number; index: number } | null;
  onSelectQueued: (sel: { slot: number; index: number } | null) => void;
  cardsById: ReadonlyMap<string, FortressCard>;
  expectedFirst: boolean;
  rsQueueActive: boolean;
  onToggleRsQueue: () => void;
  onClearQueuedRoundStart: () => void;
}) {
  const slots = Array.from({ length: MAX_QUEUE_TURNS }, (_, i) => i + 1);
  const movesBySlot = new Map(
    slots.map((slot) => [
      slot,
      self.queuedMoves.filter((m) => m.slot === slot),
    ]),
  );
  const slotName = (slot: number) =>
    slot === 1 ? 'Next turn' : `In ${slot} turns`;
  // The one empty slot offered for planning: the earliest without moves. Later
  // empty slots stay hidden until every turn before them is queued.
  const offeredSlot = slots.find((s) => (movesBySlot.get(s)?.length ?? 0) === 0);
  return (
    <div className="queue-panel">
      <div className="queue-panel-head">
        <span className="queue-panel-title">Queued moves</span>
        {planSlot !== null && (
          <span className="queue-panel-hint">
            queueing · {slotName(planSlot).toLowerCase()}
          </span>
        )}
      </div>
      {/* The round-start cycle slot: offered only while holding the First-Turn
          marker (you're EXPECTED to open the next round). The queued choice
          executes at round start only if you actually do — if someone else
          takes the marker by passing first, it is dropped unexecuted. */}
      {(expectedFirst || self.queuedRoundStartCycle !== '') && (
        <div className={`queue-slot${rsQueueActive ? ' is-active' : ''}`}>
          <div className="queue-slot-head">
            <span className="queue-slot-name">Round start</span>
            {self.queuedRoundStartCycle === '' && (
              <button
                type="button"
                className={`queue-slot-plan${rsQueueActive ? ' is-active' : ''}`}
                onClick={onToggleRsQueue}
                title={
                  rsQueueActive
                    ? 'Close the round-start picker.'
                    : 'You hold the First-Turn marker: pre-pick up to 1 card per market to cycle out (free) when the next round starts — or queue "cycle nothing". Skipped if someone else ends up going first.'
                }
              >
                {rsQueueActive ? 'Done' : '+ Queue'}
              </button>
            )}
          </div>
          {self.queuedRoundStartCycle !== '' && (
            <ul className="queue-move-list">
              <li className="queue-move">
                <span className="queue-move-label">
                  {roundStartCycleLabel(self.queuedRoundStartCycle, cardsById)}
                </span>
                <button
                  type="button"
                  className="queue-move-remove"
                  onClick={onClearQueuedRoundStart}
                  title="Cancel this queued round-start cycle"
                  aria-label="Cancel queued round-start cycle"
                >
                  ✕
                </button>
              </li>
            </ul>
          )}
        </div>
      )}
      {slots.map((slot) => {
        const moves = movesBySlot.get(slot) ?? [];
        const active = planSlot === slot;
        // Visible: holds moves, is being planned, or is the offered next slot.
        if (moves.length === 0 && !active && slot !== offeredSlot) return null;
        return (
          <div key={slot} className={`queue-slot${active ? ' is-active' : ''}`}>
            <div className="queue-slot-head">
              <span className="queue-slot-name">{slotName(slot)}</span>
              {(moves.length === 0 || active) && (
                <button
                  type="button"
                  className={`queue-slot-plan${active ? ' is-active' : ''}`}
                  disabled={isMyTurn}
                  onClick={() => onPlanSlot(active ? null : slot)}
                  title={
                    isMyTurn
                      ? "It's your turn — just play; queue moves on other players' turns."
                      : active
                        ? 'Stop queueing into this turn.'
                        : `Queue moves for this turn — they auto-play when it starts, and you keep any actions they don't spend (end with Queue Pass to flow past unattended). If one is no longer valid by then, play pauses on your turn instead.`
                  }
                >
                  {active ? 'Done' : '+ Queue'}
                </button>
              )}
            </div>
            {moves.length > 0 && (
              <ul className="queue-move-list">
                {moves.map((move, index) => {
                  const isSel =
                    queuedSel?.slot === slot && queuedSel.index === index;
                  return (
                    <li
                      key={`${slot}:${index}`}
                      className={`queue-move is-selectable${isSel ? ' is-selected' : ''}`}
                      onClick={() =>
                        onSelectQueued(isSel ? null : { slot, index })
                      }
                      title={
                        isSel
                          ? 'Hide this move’s preview'
                          : 'Preview this move on the board / market'
                      }
                    >
                      <span className="queue-move-label">{move.label}</span>
                      <button
                        type="button"
                        className="queue-move-remove"
                        onClick={(e) => {
                          e.stopPropagation();
                          onUnqueueMove(slot, index);
                        }}
                        title="Cancel this queued move"
                        aria-label={`Cancel queued move: ${move.label}`}
                      >
                        ✕
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** The Turn History view: every action taken in the match, newest first. Each
 * row is a seat-coloured action card (the same title + labelled groups the
 * action-notice popup shows), tagged with the round/turn it happened on. Backed
 * by the synced `GameState.moveHistory`, so it's identical for every player and
 * survives reconnects. */
function TurnHistoryList({
  history,
  onFocusNode,
  handlers,
  cardsByName,
}: {
  history: HistoryEntryView[];
  onFocusNode: (nodeId: number) => void;
  handlers: SelectionHandlers;
  cardsByName: ReadonlyMap<string, FortressCard>;
}) {
  if (history.length === 0) {
    return (
      <div className="turn-history">
        <div className="round-header">
          <span className="round-num">Turn History</span>
        </div>
        <p className="turn-history-empty">No actions taken yet.</p>
      </div>
    );
  }
  // Stored oldest-first; show newest at the top.
  const ordered = [...history].reverse();
  return (
    <div className="turn-history">
      <div className="round-header">
        <span className="round-num">Turn History</span>
        <span className="turn-num">{history.length} actions</span>
      </div>
      <ul className="history-list">
        {ordered.map((e) => {
          const focusable = e.focusNodeId >= 0;
          return (
            <li
              className={`history-row${focusable ? ' is-focusable' : ''}`}
              key={e.id}
              data-cursor-target={`history:${e.id}`}
              onClick={focusable ? () => onFocusNode(e.focusNodeId) : undefined}
              title={focusable ? 'Show this on the board' : undefined}
            >
              <div className="history-row-head">
                <span
                  className="seat-dot"
                  style={{ background: seatColor(e.seat) }}
                  aria-hidden
                />
                <span className="history-actor">{e.playerName || '—'}</span>
                <span
                  className="history-when"
                  title={`Round ${e.round}, turn ${e.turn}`}
                >
                  R{e.round}·T{e.turn}
                </span>
              </div>
              <div className="history-title">{e.title}</div>
              {e.groups.map((g, i) => (
                <div className="history-group" key={`${g.label}-${i}`}>
                  <span className="history-group-label">{g.label}</span>
                  <div className="history-group-items">
                    {g.items.length === 0 ? (
                      <span className="history-group-item">—</span>
                    ) : (
                      g.items.map((item, j) => {
                        // Resolve card-name items (e.g. a Cycle's removed/added
                        // cards) to a hover/click detail popover; other items
                        // (building names, etc.) stay as plain text. One per line.
                        const card = cardsByName.get(item);
                        const target: SelectionTarget | null = card
                          ? { kind: 'historyCard', card }
                          : null;
                        return (
                          <span className="history-group-item" key={j}>
                            {target ? (
                              <button
                                type="button"
                                className="history-card-link"
                                onMouseEnter={(ev) =>
                                  handlers.onPreview(target, ev.currentTarget)
                                }
                                onMouseLeave={() =>
                                  handlers.onPreviewEnd(target)
                                }
                                onClick={(ev) => {
                                  // Keep the row's focus-node click from firing.
                                  ev.stopPropagation();
                                  handlers.onPin(target, ev.currentTarget);
                                }}
                              >
                                {item}
                              </button>
                            ) : (
                              item
                            )}
                          </span>
                        );
                      })
                    )}
                  </div>
                </div>
              ))}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** A labelled line of the four resource counts, in canonical order. Icons come
 * from the shared resource definitions (`resources.tsx`) so they match the board
 * tiles and the tile library everywhere. */
function ResourceRow({
  label,
  resources,
  oneTime = false,
}: {
  label: string;
  resources: ResourceCounts;
  /** Use the one-time (ghosted) icon art instead of the solid one. */
  oneTime?: boolean;
}) {
  return (
    <div className="res-row">
      <span className="res-label">{label}</span>
      <span className="resources">
        {RESOURCE_KINDS.map((key) => (
          <span
            key={key}
            className={`res res-${key}`}
            title={RESOURCE_LABELS[key]}
          >
            <ResourceIcon kind={key} size={36} oneTime={oneTime} />
            {resources[key]}
          </span>
        ))}
      </span>
    </div>
  );
}

/** A player's Fortress cards: implemented (face-up), split into the Regular and
 * Win decks, plus the owner's face-down hand. Each implemented fortress is a
 * hover/click tile that opens its detail popover (same behaviour as the market
 * rows) — which highlights it on the board and offers "View on board". Renders
 * nothing when the player holds no cards. */
function FortressCards({
  faceUp,
  faceDown,
  handlers,
  playerId,
  cardsById,
}: {
  faceUp: FortressCardView[];
  faceDown: string[];
  handlers: SelectionHandlers;
  playerId: string;
  cardsById: ReadonlyMap<string, FortressCard>;
}) {
  // Resolve to catalogue cards, keeping the original index for a stable
  // per-instance key (a player can hold two copies of the same card).
  const resolved = faceUp.map((fc, i) => ({
    fc,
    i,
    card: cardsById.get(fc.cardId),
  }));
  const regular = resolved.filter((r) => r.card?.kind === 'REGULAR');
  const win = resolved.filter((r) => r.card?.kind === 'win');

  const renderTile = ({
    fc,
    i,
    card,
  }: {
    fc: FortressCardView;
    i: number;
    card: FortressCard | undefined;
  }) => {
    const name = card?.name ?? fc.cardId;
    const target: SelectionTarget | null = card
      ? {
          kind: 'fortressCard',
          card,
          nodeIds: fc.nodeIds,
          tokens: fc.tokens,
          disabled: fc.disabled,
          instanceKey: `${playerId}:${i}`,
        }
      : null;
    return (
      <li
        key={`${fc.cardId}-${i}`}
        className={`fortress-tile${fc.disabled ? ' is-disabled' : ''}`}
        title={name}
        onMouseEnter={
          target
            ? (e) => handlers.onPreview(target, e.currentTarget)
            : undefined
        }
        onMouseLeave={target ? () => handlers.onPreviewEnd(target) : undefined}
        onClick={
          target
            ? (e) => {
                // Don't let the click bubble to the player row (which would
                // pin the whole-player popover instead).
                e.stopPropagation();
                handlers.onPin(target, e.currentTarget);
              }
            : undefined
        }
      >
        {card && (
          <span className="fortress-tile-pattern" aria-hidden>
            <PatternDiagram
              pattern={card.pattern}
              maxW={32}
              maxH={28}
              pad={3}
              iconScale={1}
            />
          </span>
        )}
        <span className="fortress-tile-name">{name}</span>
        {fc.tokens > 0 && (
          <span
            className="fortress-tile-tokens"
            title={`${fc.tokens} token(s) on this fortress`}
          >
            ● {fc.tokens}
          </span>
        )}
        {fc.disabled && (
          <span
            className="fortress-tile-disabled"
            title="Exhausted"
            aria-label="Exhausted"
          >
            ⊘
          </span>
        )}
      </li>
    );
  };

  return (
    <div className="player-cards">
      <div className="fortress-section">
        <span className="fortress-section-label">Regular Fortresses</span>
        {regular.length > 0 ? (
          <ul className="fortress-tile-list">{regular.map(renderTile)}</ul>
        ) : (
          <span className="fortress-empty">None</span>
        )}
      </div>
      <div className="fortress-section">
        <span className="fortress-section-label">Win Fortresses</span>
        {win.length > 0 ? (
          <ul className="fortress-tile-list">{win.map(renderTile)}</ul>
        ) : (
          <span className="fortress-empty">None</span>
        )}
      </div>
      {faceDown.length > 0 && (
        <div className="fc-section">
          <span className="fc-label">Hand</span>
          <span className="fc-chips">
            {faceDown.map((id, i) => {
              // Face-down cards sync only to their owner while the match is
              // live; at game end the server reveals every hand to everyone.
              // Each chip opens the plain card-face popover (`historyCard` —
              // hand cards aren't in a market row, so never pruned).
              const card = cardsById.get(id);
              const name = card?.name ?? id;
              const target: SelectionTarget | null = card
                ? { kind: 'historyCard', card }
                : null;
              return (
                <span
                  key={`${id}-${i}`}
                  className="fc-chip is-hidden"
                  title={name}
                  onMouseEnter={
                    target
                      ? (e) => handlers.onPreview(target, e.currentTarget)
                      : undefined
                  }
                  onMouseLeave={
                    target ? () => handlers.onPreviewEnd(target) : undefined
                  }
                  onClick={
                    target
                      ? (e) => {
                          // Keep the click off the player row (which would pin
                          // the whole-player popover instead).
                          e.stopPropagation();
                          handlers.onPin(target, e.currentTarget);
                        }
                      : undefined
                  }
                >
                  {name}
                </span>
              );
            })}
          </span>
        </div>
      )}
    </div>
  );
}

/** Market marker for the SELECTED queued move: badge the card a queued
 * Implement will buy (`cardId`) or the row slots a queued Cycle will discard
 * (`indices`), within row `row`. */
interface QueuedMarketMark {
  row: FortressKind;
  mode: 'implement' | 'cycle';
  cardId?: string;
  indices?: ReadonlySet<number>;
}

/** Everything the market sidebar needs to drive the round-start cycle picker —
 * the first player's free "cycle up to 1 card per market" step, either
 * answering the live window ('live') or pre-queueing next round's choice
 * ('queue'). */
interface RoundStartCycleControls {
  mode: 'live' | 'queue';
  /** The single pick per market row (card ids), or null for "keep that row". */
  sel: { regular: string | null; win: string | null };
  onToggle: (kind: FortressKind, cardId: string | null) => void;
  /** Confirm the current picks. */
  onConfirm: () => void;
  /** Explicitly cycle no cards (both rows kept). */
  onSkip: () => void;
  /** Leave the picker without deciding — queue mode only (the live window
   * must be answered). */
  onCancel?: () => void;
}

interface MarketSidebarProps {
  markets: MarketView;
  collapsed: boolean;
  onToggle: () => void;
  onOpenLibrary: () => void;
  handlers: SelectionHandlers;
  cycle: CycleControls;
  /** Round-start cycle picker, or null when off. Takes precedence over the
   * paid Cycle-Market flow (the two are never live together). */
  roundStart: RoundStartCycleControls | null;
  /** Match-pinned fortress catalogue. */
  cardsById: ReadonlyMap<string, FortressCard>;
  /** cardId → outline: 'ready' (pattern on board + Obsidian in hand) or 'poor'
   * (pattern on board, can't afford). Cards absent from the map get no outline. */
  buildable: ReadonlyMap<string, 'ready' | 'poor'>;
  /** Selected queued move's market marker, or null when none is selected. */
  queuedMark: QueuedMarketMark | null;
}

/** Right sidebar: the face-up Regular and Win Fortress market rows. Collapsible
 * to a thin rail via the inline header toggle; the header also opens the
 * full-catalogue modal. */
function MarketSidebar({
  markets,
  collapsed,
  onToggle,
  onOpenLibrary,
  handlers,
  cycle,
  roundStart,
  cardsById,
  buildable,
  queuedMark,
}: MarketSidebarProps) {
  // A row's cards are selectable in cycle mode when no row is fixed yet, or this
  // is the fixed row; the other row is locked out (no mixing). `selected` is the
  // live set for the fixed row, empty otherwise.
  const sectionCycle = (kind: FortressKind) =>
    cycle.active
      ? {
          selectable:
            cycle.selectedKind === null || cycle.selectedKind === kind,
          selected:
            cycle.selectedKind === kind
              ? cycle.selectedIndices
              : EMPTY_SELECTION,
          onToggle: (index: number) => cycle.onToggleCard(kind, index),
        }
      : undefined;

  // Round-start picking: both rows stay selectable, each holding at most ONE
  // pick (by card id — mapped to the row index the card tiles select by).
  const sectionRoundStart = (kind: FortressKind) => {
    if (!roundStart) return undefined;
    const cards = (kind === 'win' ? markets.win : markets.regular).cards;
    const pick = kind === 'win' ? roundStart.sel.win : roundStart.sel.regular;
    const pickIndex = pick ? cards.indexOf(pick) : -1;
    return {
      selectable: true,
      selected:
        pickIndex >= 0 ? new Set([pickIndex]) : (EMPTY_SELECTION as ReadonlySet<number>),
      onToggle: (index: number) =>
        roundStart.onToggle(kind, cards[index] ?? null),
    };
  };
  const sectionMode = (kind: FortressKind) =>
    sectionRoundStart(kind) ?? sectionCycle(kind);

  const selectedCount = cycle.selectedIndices.size;
  const rsPickNames = roundStart
    ? [roundStart.sel.regular, roundStart.sel.win]
        .filter((id): id is string => id !== null)
        .map((id) => cardsById.get(id)?.name ?? id)
    : [];

  return (
    <aside
      className={`sidebar sidebar-right${collapsed ? ' is-collapsed' : ''}`}
    >
      <div className="sidebar-header">
        <button
          type="button"
          className="sidebar-toggle"
          onClick={onToggle}
          title={collapsed ? 'Show Fortress markets' : 'Hide Fortress markets'}
          aria-label={
            collapsed ? 'Expand market panel' : 'Collapse market panel'
          }
        >
          {collapsed ? '‹' : '›'}
        </button>
        <button
          type="button"
          className="sidebar-action"
          onClick={onOpenLibrary}
          title="Browse every Fortress in the game"
        >
          ⛶ All Fortresses
        </button>
      </div>
      {collapsed ? (
        <div className="market-rail">
          {([
            ['Regular Fortresses', markets.regular, REGULAR_ACCENT],
            ['Win Fortresses', markets.win, WIN_ACCENT],
          ] as const).map(([label, pile, accent]) => (
            <div
              key={label}
              className="market-rail-group"
              style={{ borderColor: accent }}
              role="group"
              aria-label={label}
            >
              {pile.cards.map((id, index) => {
                const card = cardsById.get(id);
                const target: SelectionTarget | null = card
                  ? { kind: 'card', card }
                  : null;
                return (
                  <button
                    key={`${id}-${index}`}
                    type="button"
                    className="rail-market-card"
                    data-cursor-target={`card:${id}`}
                    title={card?.name ?? id}
                    aria-label={card?.name ?? id}
                    onMouseEnter={
                      target
                        ? (e) => handlers.onPreview(target, e.currentTarget)
                        : undefined
                    }
                    onMouseLeave={
                      target ? () => handlers.onPreviewEnd(target) : undefined
                    }
                    onClick={
                      target
                        ? (e) => handlers.onPin(target, e.currentTarget)
                        : undefined
                    }
                  >
                    {card ? (
                      <PatternDiagram
                        pattern={card.pattern}
                        maxW={28}
                        maxH={28}
                        pad={2}
                        iconScale={1}
                      />
                    ) : (
                      <span aria-hidden>?</span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      ) : (
        <div className="sidebar-body">
        {roundStart && (
          <div
            className="cycle-banner"
            role="region"
            aria-label="Round-start cycle"
          >
            <div className="cycle-banner-row">
              <span className="cycle-banner-title">
                {roundStart.mode === 'live'
                  ? 'Round start — cycle out up to 1 card per market, free'
                  : 'Queue for round start — cycle out up to 1 card per market, free'}
              </span>
            </div>
            <div className="cycle-banner-actions">
              <button
                type="button"
                className="cycle-confirm"
                disabled={rsPickNames.length === 0}
                onClick={roundStart.onConfirm}
                title={
                  rsPickNames.length > 0
                    ? `Cycle out: ${rsPickNames.join(' + ')}`
                    : 'Pick a card in either market first'
                }
              >
                {roundStart.mode === 'queue' ? 'Queue: cycle' : 'Cycle'}
                {rsPickNames.length > 0
                  ? ` ${rsPickNames.length} card${rsPickNames.length === 1 ? '' : 's'}`
                  : ''}
              </button>
              <button
                type="button"
                className="cycle-cancel"
                onClick={roundStart.onSkip}
                title="Keep both markets as they are"
              >
                {roundStart.mode === 'queue'
                  ? 'Queue: cycle nothing'
                  : 'Cycle nothing'}
              </button>
              {roundStart.onCancel && (
                <button
                  type="button"
                  className="cycle-cancel"
                  onClick={roundStart.onCancel}
                  title="Close without queueing anything"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        )}
        {!roundStart && cycle.active && (
          <div className="cycle-banner" role="region" aria-label="Cycle market">
            <div className="cycle-banner-row">
              <span className="cycle-banner-title">Choose cards to cycle</span>
              <span
                className="cycle-cost"
                title="Food paid to cycle the selected cards"
              >
                <ResourceIcon kind="food" size={36} /> {cycle.foodCost}
              </span>
            </div>
            <div className="cycle-banner-actions">
              <button
                type="button"
                className="cycle-confirm"
                disabled={selectedCount === 0 || !cycle.affordable}
                onClick={cycle.onConfirm}
              >
                {selectedCount > 0
                  ? `Cycle ${selectedCount} card${selectedCount === 1 ? '' : 's'}`
                  : 'Cycle'}
              </button>
              <button
                type="button"
                className="cycle-cancel"
                onClick={cycle.onCancel}
              >
                Cancel
              </button>
            </div>
            {selectedCount > 0 && !cycle.affordable && (
              <span className="cycle-warning">Not enough Food.</span>
            )}
          </div>
        )}
        <MarketSection
          title="Regular Fortresses"
          pile={markets.regular}
          accent={REGULAR_ACCENT}
          handlers={handlers}
          cardsById={cardsById}
          cycle={sectionMode('REGULAR')}
          buildable={buildable}
          queuedMark={queuedMark?.row === 'REGULAR' ? queuedMark : null}
        />
        <MarketSection
          title="Win Fortresses"
          pile={markets.win}
          accent={WIN_ACCENT}
          handlers={handlers}
          cardsById={cardsById}
          cycle={sectionMode('win')}
          buildable={buildable}
          queuedMark={queuedMark?.row === 'win' ? queuedMark : null}
        />
        </div>
      )}
    </aside>
  );
}

/** One face-up market card row in normal (non-cycle) mode. The slot is keyed by
 * position, so when a cycle replaces the card here the SAME instance sees a new
 * `id` and plays a swap: the old card slides out to the left while the new one
 * slides in from the right. Hover/click open the detail popover, exactly as the
 * static row did. Respects `prefers-reduced-motion` (instant swap, no overlay). */
function MarketCard({
  id,
  handlers,
  cycle,
  buildable,
  locked,
  queued,
  cardsById,
}: {
  id: string;
  handlers: SelectionHandlers;
  /** Present in cycle mode. The tile renders identically (so its hover detail
   * popover still works); only the click behaviour changes — a click toggles
   * this card's cycle selection instead of pinning the detail popover. */
  cycle?: {
    selectable: boolean;
    selected: boolean;
    onToggle: () => void;
  };
  /** Outline tier: the player has this card's pattern on the board — 'ready'
   * (can also afford it) or 'poor' (short on Obsidian). */
  buildable?: 'ready' | 'poor';
  /** Locked against cycling (Supreme Bondage); still purchasable. */
  locked?: boolean;
  /** Marked by the SELECTED queued move: this card is queued to be implemented
   * or cycled when the owner's turn comes. */
  queued?: 'implement' | 'cycle';
  cardsById: ReadonlyMap<string, FortressCard>;
}) {
  // `shown` is the card currently in flow; `exiting` is the previous card kept
  // briefly as an overlay so it can animate out. Both null-exit and the entry
  // animation are gated on a real swap, so the first mount doesn't animate.
  const prevId = useRef(id);
  const [shown, setShown] = useState(id);
  const [exiting, setExiting] = useState<string | null>(null);

  useEffect(() => {
    if (prevId.current === id) return;
    const reduce =
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    if (!reduce) setExiting(prevId.current); // overlay the outgoing card
    setShown(id);
    prevId.current = id;
  }, [id]);

  // Drop the outgoing overlay when its slide-out ends; a timeout backstops the
  // case where `animationend` never fires (e.g. reduced motion).
  useEffect(() => {
    if (exiting === null) return;
    const t = window.setTimeout(() => setExiting(null), 950);
    return () => window.clearTimeout(t);
  }, [exiting]);

  const card = cardsById.get(shown);
  const face = (faceId: string) => {
    const fc = cardsById.get(faceId);
    return (
      <>
        {fc && (
          <span className="card-pattern" title="Required build pattern">
            <PatternDiagram pattern={fc.pattern} maxW={52} maxH={44} pad={5} iconScale={1} />
          </span>
        )}
        <span className="card-id">{fc?.name ?? faceId}</span>
        {fc && (
          <span className="card-cost-badge" title="Obsidian cost">
            <ResourceIcon kind="obsidian" size={36} /> {fc.cost[3]}
          </span>
        )}
      </>
    );
  };

  // In cycle mode the click toggles selection (when this row is selectable);
  // otherwise it pins the detail popover. Hover preview stays wired in both
  // modes so players can still inspect a card before choosing.
  const cycleClass = cycle
    ? ` is-cycle${cycle.selectable ? ' is-cyclable' : ' is-cycle-locked'}${
        cycle.selected ? ' is-cycle-selected' : ''
      }`
    : card
      ? ' is-clickable'
      : '';
  const onClick = cycle
    ? cycle.selectable
      ? () => cycle.onToggle()
      : undefined
    : card
      ? (e: ReactMouseEvent<HTMLLIElement>) =>
          handlers.onPin({ kind: 'card', card }, e.currentTarget)
      : undefined;

  const buildableClass =
    buildable === 'ready'
      ? ' is-buildable'
      : buildable === 'poor'
        ? ' is-buildable-poor'
        : '';

  return (
    <li
      className={`card card-anim${cycleClass}${buildableClass}${queued ? ' is-queued' : ''}`}
      title={card ? undefined : shown}
      data-cursor-target={`card:${id}`}
      aria-pressed={cycle ? cycle.selected : undefined}
      onMouseEnter={
        card
          ? (e) => handlers.onPreview({ kind: 'card', card }, e.currentTarget)
          : undefined
      }
      onMouseLeave={
        card ? () => handlers.onPreviewEnd({ kind: 'card', card }) : undefined
      }
      onClick={onClick}
    >
      {queued && (
        <span
          className={`card-queued-badge is-${queued}`}
          title={
            queued === 'implement'
              ? 'Queued: implement this Fortress when your turn starts'
              : 'Queued: cycle this card out when your turn starts'
          }
        >
          ⏳ {queued === 'implement' ? 'Queued' : 'Cycling'}
        </span>
      )}
      {cycle?.selectable && (
        <span className="cycle-check card-cycle-check" aria-hidden>
          {cycle.selected ? '✓' : ''}
        </span>
      )}
      {locked && (
        <span
          className="card-lock-badge"
          title="Locked — can't be cycled, still purchasable"
        >
          🔒
        </span>
      )}
      {exiting !== null && (
        <div
          key={`exit-${exiting}`}
          className="card-face card-exit"
          onAnimationEnd={() => setExiting(null)}
        >
          {face(exiting)}
        </div>
      )}
      <div
        key={`face-${shown}`}
        className={`card-face${exiting !== null ? ' card-enter' : ''}`}
      >
        {face(shown)}
      </div>
    </li>
  );
}

function MarketSection({
  title,
  pile,
  accent,
  handlers,
  cycle,
  buildable,
  cardsById,
  queuedMark,
}: {
  title: string;
  pile: MarketPile;
  accent: string;
  handlers: SelectionHandlers;
  /** Present in cycle mode: per-card selection for this row. `selectable` is
   * false for the locked-out row once the other row holds the selection. */
  cycle?: {
    selectable: boolean;
    selected: ReadonlySet<number>;
    onToggle: (index: number) => void;
  };
  buildable: ReadonlyMap<string, 'ready' | 'poor'>;
  cardsById: ReadonlyMap<string, FortressCard>;
  /** Selected queued move's marker when it targets THIS row, else null. */
  queuedMark: QueuedMarketMark | null;
}) {
  return (
    <section className={`market-section${cycle ? ' is-cycling' : ''}`}>
      <h2 className="market-title" style={{ borderColor: accent }}>
        {title}
      </h2>
      <div className="market-counts">
        <span title="Cards remaining in the draw pile">
          Deck {pile.deckCount}
        </span>
        <span title="Cards in the discard pile">
          Discard {pile.discardCount}
        </span>
      </div>
      <ul className="card-list">
        {pile.cards.map((id, i) => (
          // A self-animating slot, keyed by position so it persists across
          // cycles (and entering/leaving cycle mode) — the tile renders the same
          // in both modes; in cycle mode the click toggles selection instead of
          // pinning, while hover detail stays available.
          <MarketCard
            key={`slot-${i}`}
            id={id}
            handlers={handlers}
            cardsById={cardsById}
            buildable={buildable.get(id)}
            locked={pile.locked.includes(id)}
            queued={
              queuedMark
                ? queuedMark.mode === 'implement'
                  ? queuedMark.cardId === id
                    ? 'implement'
                    : undefined
                  : queuedMark.indices?.has(i)
                    ? 'cycle'
                    : undefined
                : undefined
            }
            cycle={
              cycle
                ? {
                    // A locked card can't be cycled — the server rejects the
                    // batch, so don't let it into the selection either.
                    selectable:
                      cycle.selectable && !pile.locked.includes(id),
                    selected: cycle.selected.has(i),
                    onToggle: () => cycle.onToggle(i),
                  }
                : undefined
            }
          />
        ))}
        {pile.cards.length === 0 && (
          <li className="card empty">Market empty</li>
        )}
      </ul>
    </section>
  );
}

/** A detail popover anchored beside a sidebar tile. Both sidebars hug a screen
 * edge and clip their own overflow, so this is portalled to `<body>` and fixed-
 * positioned beside the tile — to its left, flipping to its right when it won't
 * fit, clamped to the viewport. Shown as a hover preview or pinned open by a
 * click; the wrappers below supply the content.
 *
 * It anchors to the live `anchorEl` (re-measured), not a one-shot rect, so it
 * follows the row as the roster reflows (a sibling grows, a player leaves, the
 * list scrolls). Reposition fires from: every render (catches React-driven
 * reflow), a `ResizeObserver` on both the popover and the anchor (size changes),
 * and page scroll / resize. Dismissal:
 *  - Escape closes either kind.
 *  - A pinned popover also closes on an outside click; the listener is attached
 *    only when pinned, so the click-to-pin mousedown can't close the popover it
 *    just opened (effects run after that click).
 *  - A hover preview is read-only (`pointer-events:none`) and dismissed by its
 *    tile's `mouseleave`; if its tile unmounts, the parent clears the selection. */
function AnchoredPopover({
  anchorEl,
  pinned,
  label,
  onClose,
  children,
  className = 'detail-popover',
  onHoverEnter,
  onHoverLeave,
}: {
  anchorEl: HTMLElement;
  pinned: boolean;
  label: string;
  onClose: () => void;
  children: ReactNode;
  /** Positioned-shell class (must set `position: fixed`). Defaults to the detail
   * popover; the action notice passes its own toast variant. */
  className?: string;
  /** Hover-preview lifecycle hooks. When supplied (and not pinned) the preview
   * becomes interactive so the cursor can move onto it: entering keeps it open,
   * leaving lets the parent close it. Omitted for pinned/notice popovers, which
   * keep their original behaviour. */
  onHoverEnter?: () => void;
  onHoverLeave?: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  const reposition = useCallback(() => {
    const el = ref.current;
    // If the anchor tile has unmounted (card cycled out / player left) its rect
    // collapses to 0,0; hold the last position and let the parent close us.
    if (!el || !anchorEl.isConnected) return;
    const a = anchorEl.getBoundingClientRect();
    const { width, height } = el.getBoundingClientRect();
    const gap = 10;
    const margin = 8;
    let left = a.left - gap - width;
    if (left < margin) {
      left = Math.min(a.right + gap, window.innerWidth - width - margin);
    }
    const top = Math.max(
      margin,
      Math.min(a.top, window.innerHeight - height - margin),
    );
    // Equality-guard so the every-render layout effect can't loop: a new {left,top}
    // object would always be !== the old, re-rendering forever; return the same
    // reference when nothing moved and React bails out.
    setPos((prev) =>
      prev && prev.left === left && prev.top === top ? prev : { left, top },
    );
  }, [anchorEl]);

  // Re-anchor after every render — the cheapest way to follow React-driven
  // reflow of the roster/market (a row grows, the list re-sorts, a peer leaves).
  useLayoutEffect(reposition);

  // Async size changes that don't go through React: the popover's own content
  // (e.g. the pattern SVG / font swap) and the anchor row resizing in place.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(reposition);
    ro.observe(el);
    ro.observe(anchorEl);
    return () => ro.disconnect();
  }, [anchorEl, reposition]);

  // Scroll (the sidebar list, capture-phase) and viewport resize move the anchor
  // without a React render — follow it. Ignore scrolls inside the popover's own
  // scrollable body, which don't move the anchor.
  useEffect(() => {
    const onScrollOrResize = (e: Event) => {
      if (e.type === 'scroll' && ref.current?.contains(e.target as Node))
        return;
      reposition();
    };
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [reposition]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    if (!pinned) {
      return () => document.removeEventListener('keydown', onKey);
    }
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [onClose, pinned]);

  // A hover preview with lifecycle hooks is interactive so the cursor can reach
  // it; without hooks (the action notice) it stays read-only. A pinned popover
  // is always interactive.
  const interactive = pinned || !!onHoverEnter;
  return createPortal(
    <div
      ref={ref}
      className={className}
      role="dialog"
      aria-label={label}
      // Hover hooks apply only to the preview; a pinned popover keeps its
      // original click-driven lifecycle untouched.
      onMouseEnter={!pinned ? onHoverEnter : undefined}
      onMouseLeave={!pinned ? onHoverLeave : undefined}
      style={{
        left: pos?.left ?? 0,
        top: pos?.top ?? 0,
        visibility: pos ? 'visible' : 'hidden',
        pointerEvents: interactive ? 'auto' : 'none',
      }}
    >
      {children}
    </div>,
    document.body,
  );
}

/** A transient, read-only popup describing an action another player just took,
 * anchored beside that player's roster row. Reuses `AnchoredPopover` (so it
 * follows the row as the sidebar reflows and is non-interactive while showing);
 * the parent auto-dismisses it after `NOTICE_TTL`, and Escape closes it early. */
function ActionNoticePopover({
  notice,
  anchorEl,
  onDismiss,
}: {
  notice: ActionNotice;
  anchorEl: HTMLElement;
  onDismiss: () => void;
}) {
  return (
    <AnchoredPopover
      anchorEl={anchorEl}
      pinned={false}
      onClose={onDismiss}
      label={`${notice.title} — action notification`}
      className="action-notice-popover"
    >
      <div className="action-notice">
        <div className="action-notice-title">{notice.title}</div>
        {notice.groups?.map((g, i) => (
          <div className="action-notice-group" key={`${g.label}-${i}`}>
            <div className="action-notice-group-label">{g.label}</div>
            <ul className="action-notice-items">
              {g.items.map((item, j) => (
                <li key={`${item}-${j}`}>{item}</li>
              ))}
              {g.items.length === 0 && (
                <li className="action-notice-empty">—</li>
              )}
            </ul>
          </div>
        ))}
      </div>
    </AnchoredPopover>
  );
}

/** Full detail of a market card, anchored beside its summary tile. */
function CardDetailPopover({
  card,
  anchorEl,
  pinned,
  onClose,
  onHoverEnter,
  onHoverLeave,
}: {
  card: FortressCard;
  anchorEl: HTMLElement;
  pinned: boolean;
  onClose: () => void;
  onHoverEnter?: () => void;
  onHoverLeave?: () => void;
}) {
  return (
    <AnchoredPopover
      anchorEl={anchorEl}
      pinned={pinned}
      onClose={onClose}
      onHoverEnter={onHoverEnter}
      onHoverLeave={onHoverLeave}
      label={`${card.name} card details`}
    >
      <FortressCardFace card={card} style={{ width: 300 }} />
    </AnchoredPopover>
  );
}

/** Detail of one of a player's implemented fortress cards, anchored beside its
 * roster tile: the full card face plus its live token / exhausted state, and a
 * "View on board" action that pans + zooms to where it sits. While this is open,
 * the fortress's nodes are ring-highlighted on the board (driven from App). */
function FortressCardDetailPopover({
  card,
  tokens,
  disabled,
  hasNodes,
  onViewOnBoard,
  anchorEl,
  pinned,
  onClose,
  onHoverEnter,
  onHoverLeave,
}: {
  card: FortressCard;
  tokens: number;
  disabled: boolean;
  hasNodes: boolean;
  /** Pan + zoom the board to this fortress. Omitted when the popover was
   * opened FROM the board (already panned there) — the button is hidden. */
  onViewOnBoard?: () => void;
  anchorEl: HTMLElement;
  pinned: boolean;
  onClose: () => void;
  onHoverEnter?: () => void;
  onHoverLeave?: () => void;
}) {
  return (
    <AnchoredPopover
      anchorEl={anchorEl}
      pinned={pinned}
      onClose={onClose}
      onHoverEnter={onHoverEnter}
      onHoverLeave={onHoverLeave}
      label={`${card.name} fortress details`}
    >
      <div className="fortress-detail">
        <FortressCardFace card={card} style={{ width: 300 }} />
        {(tokens > 0 || disabled) && (
          <div className="fortress-detail-state">
            {tokens > 0 && (
              <span
                className="fortress-detail-tokens"
                title="Tokens on this card"
              >
                ● {tokens} token{tokens === 1 ? '' : 's'}
              </span>
            )}
            {disabled && (
              <span className="fortress-detail-disabled" title="Exhausted">
                ⊘ Exhausted
              </span>
            )}
          </div>
        )}
        {onViewOnBoard && (
          <button
            type="button"
            className="fortress-detail-view"
            onClick={onViewOnBoard}
            disabled={!hasNodes}
            title={
              hasNodes
                ? 'Pan and zoom the board to this fortress'
                : 'This fortress has no recorded board location'
            }
          >
            ⌖ View on board
          </button>
        )}
      </div>
    </AnchoredPopover>
  );
}

/** Full detail of a player, anchored beside their roster row: every implemented
 * Fortress in full (with its token count and exhausted state), plus their
 * face-down hand. The `player` is resolved live in `App`, so this stays current
 * while open. */
function PlayerDetailPopover({
  player,
  cardsById,
  anchorEl,
  pinned,
  onClose,
  onHoverEnter,
  onHoverLeave,
}: {
  player: LobbyPlayer;
  cardsById: ReadonlyMap<string, FortressCard>;
  anchorEl: HTMLElement;
  pinned: boolean;
  onClose: () => void;
  onHoverEnter?: () => void;
  onHoverLeave?: () => void;
}) {
  return (
    <AnchoredPopover
      anchorEl={anchorEl}
      pinned={pinned}
      onClose={onClose}
      onHoverEnter={onHoverEnter}
      onHoverLeave={onHoverLeave}
      label={`${player.name} details`}
    >
      <div className="player-detail">
        <div className="player-detail-head">
          <span
            className="seat-dot"
            style={{ background: seatColor(player.seat) }}
            aria-hidden
          />
          <strong className="player-detail-name">{player.name}</strong>
        </div>

        <div className="player-detail-section">
          <div className="player-detail-title">Resources</div>
          <div className="player-detail-resources">
            <ResourceRow label="Bank" resources={player.resources} oneTime />
            <ResourceRow label="Production" resources={player.production} />
          </div>
        </div>

        <div className="player-detail-section">
          <div className="player-detail-title">
            Fortresses
            <span className="player-detail-count">
              {player.fortressCards.length}
            </span>
          </div>
          {player.fortressCards.length === 0 ? (
            <p className="player-detail-empty">
              No fortresses implemented yet.
            </p>
          ) : (
            <div className="player-detail-cards">
              {player.fortressCards.map((fc, i) => {
                const card = cardsById.get(fc.cardId);
                return (
                  <div className="player-detail-card" key={`${fc.cardId}-${i}`}>
                    {card ? (
                      <FortressCardFace card={card} style={{ width: '100%' }} />
                    ) : (
                      <div className="player-detail-unknown">{fc.cardId}</div>
                    )}
                    {(fc.tokens > 0 || fc.disabled) && (
                      <div className="player-detail-badges">
                        {fc.tokens > 0 && (
                          <span
                            className="fc-badge fc-badge-token"
                            title="Tokens on this fortress"
                          >
                            ● {fc.tokens}
                            {Number.isFinite(tokenTargetOf(fc.cardId))
                              ? `/${tokenTargetOf(fc.cardId)}`
                              : ''}{' '}
                            token{fc.tokens === 1 ? '' : 's'}
                          </span>
                        )}
                        {fc.disabled && (
                          <span
                            className="fc-badge fc-badge-exhausted"
                            title="Effect already used — exhausted this round"
                          >
                            Exhausted
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="player-detail-section">
          <div className="player-detail-title">
            Face-down cards
            <span className="player-detail-count">
              {player.hiddenCards.length}
            </span>
          </div>
          {player.hiddenCards.length === 0 ? (
            <p className="player-detail-empty">None in hand.</p>
          ) : (
            // Hand ids only sync to whoever may see them (the owner while the
            // match is live; everyone once the game ends and hands are
            // revealed) — so any entry here can be shown face-up.
            <div className="player-detail-cards">
              {player.hiddenCards.map((id, i) => {
                const card = cardsById.get(id);
                return (
                  <div className="player-detail-card" key={`${id}-${i}`}>
                    {card ? (
                      <FortressCardFace card={card} style={{ width: '100%' }} />
                    ) : (
                      <div className="player-detail-unknown">{id}</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* A hover preview is read-only and can't be scrolled, so a long roster of
            fortresses is clipped by the popover's max-height. Hint that clicking
            pins it open (and scrollable). */}
        {!pinned && (
          <div className="player-detail-hint">
            Click to keep open &amp; scroll
          </div>
        )}
      </div>
    </AnchoredPopover>
  );
}

interface LobbyProps {
  players: LobbyPlayer[];
  self: LobbyPlayer | undefined;
  lobbyInfo: LobbyInfo;
  /** Everyone watching this room. */
  spectators: SpectatorView[];
  /** This client joined watch-only — no seat, no ready control. */
  isSpectator: boolean;
  onToggleReady: () => void;
  disabled: boolean;
  /** Non-prod only: when true, show the dev Auto-Initialize button. */
  devMode: boolean;
  /** Dev-only: force-start the match, auto-setting up every player. */
  onAutoInit: () => void;
  onLeave: () => void;
}

function Lobby({
  players,
  self,
  lobbyInfo,
  spectators,
  isSpectator,
  onToggleReady,
  disabled,
  devMode,
  onAutoInit,
  onLeave,
}: LobbyProps) {
  const readyCount = players.filter((p) => p.ready).length;
  const [copied, setCopied] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);

  useEffect(() => {
    if (!lobbyInfo.autoStartAtMs) {
      setCountdown(null);
      return;
    }
    const tick = () => {
      const left = Math.max(
        0,
        Math.ceil((lobbyInfo.autoStartAtMs - Date.now()) / 1000),
      );
      setCountdown(left);
    };
    tick();
    const id = window.setInterval(tick, 200);
    return () => window.clearInterval(id);
  }, [lobbyInfo.autoStartAtMs]);

  async function copyCode() {
    const code = lobbyInfo.inviteCode.toUpperCase();
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  }

  return (
    <div className="lobby">
      <div className="lobby-card">
        <h1>Lobby</h1>
        <p className="lobby-sub">
          {readyCount} / {players.length} ready · {players.length}/
          {lobbyInfo.maxPlayers} seats
          {lobbyInfo.visibility === 'private' ? ' · Private' : ' · Public'}
          {spectators.length > 0 &&
            ` · ${spectators.length} watching`}
        </p>

        <div className="lobby-code-row">
          <span className="lobby-code-label">Code</span>
          <code className="lobby-code">{lobbyInfo.inviteCode || '————'}</code>
          <button
            type="button"
            className="lobby-copy-btn"
            onClick={() => void copyCode()}
            disabled={!lobbyInfo.inviteCode}
          >
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>

        {countdown !== null && (
          <p className="lobby-countdown" role="status">
            Starting in {countdown}s…
          </p>
        )}

        <ul className="lobby-roster">
          {players.map((p) => (
            <li
              key={p.id}
              className={p.ready ? 'is-ready' : ''}
              data-cursor-target={`player:${p.id}`}
            >
              <span className="dot" aria-hidden />
              <span className="name">
                {p.name}
                {p.id === self?.id ? ' (you)' : ''}
              </span>
              <span className="state">{p.ready ? 'Ready' : 'Not ready'}</span>
            </li>
          ))}
          {players.length === 0 && (
            <li className="empty">Waiting for players…</li>
          )}
        </ul>
        {isSpectator ? (
          <p className="lobby-spectating" role="status">
            👁 Spectating — you&apos;ll watch the match when it starts.
          </p>
        ) : (
          <button
            className={`ready-btn ${self?.ready ? 'on' : ''}`}
            data-cursor-target="lobby:ready"
            onClick={onToggleReady}
            disabled={disabled}
          >
            {self?.ready ? 'Cancel ready' : 'Ready up'}
          </button>
        )}
        <button type="button" className="auth-link lobby-leave" onClick={onLeave}>
          Leave lobby
        </button>
        {devMode && !isSpectator && (
          <button
            type="button"
            className="auto-init-btn"
            onClick={onAutoInit}
            disabled={disabled}
            title="Dev only: force-start now, auto-assigning every player's home base and production."
          >
            ⚡ Auto Initialize (dev)
          </button>
        )}
      </div>
    </div>
  );
}
